import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Lock, Sparkles, FileText, Users, ChevronRight, Filter, Search,
  Loader2, X, Eye, Check, Plus, Layers, Beaker, BookOpen, Flag,
  Calendar, AlertCircle, Download, FileSpreadsheet, Presentation, Mail,
  PieChart, GitCompare, Bell, Radar, Info, Wrench, ChevronDown, RefreshCw,
  Activity, Circle, GitMerge, ClipboardCheck, ShieldCheck, ShieldAlert,
  Database, Unlock, ArrowRight, MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScreeningPanel } from '@/components/ScreeningPanel';
import { WorkflowBanner } from '@/components/WorkflowBanner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import AdminDeepDiveModal from '../components/AdminDeepDiveModal';
import { ViatrisJourneyPanel } from '../components/ViatrisJourneyPanel';
import WhyRecommendationInfo from '../components/WhyRecommendationInfo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical definitions for every label that appears in the workbench.
// Surfaced via small (i) tooltips so the operator can self-serve the
// meaning of any KPI/stage/tier without leaving the page.
const DEFINITIONS = {
  // Status Pipeline stages (Feb 2026 v2.2 — 5-stage view)
  cohort: 'A curated subset of assets selected for the current evaluation cycle (the pool that funnels through the AI + committee stages).',
  phase_3_plus: 'Assets in the cohort that are Phase 3 / Approved / Marketed / NDA. First narrowing point — off-phase assets are auto-classified Not Suitable without any LLM cost.',
  ta_of_interest: 'Assets that ALSO fall inside Viatris\' priority therapeutic areas: Cardiovascular · Metabolic · CNS/Neuro · Immunology · Dermatology · Pain. Second narrowing point — this is the pool that gets AI-enriched and scored for committee review.',
  committee_review: 'Assets in active-review tiers (Strategic Priority + High Priority + Medium) currently with the committee for decision. Only these tiers count — Low Priority / Not Suitable / Could Not Assess are excluded.',
  finalised: 'Assets shortlisted by Viatris after the first committee review. Click to view them, send availability-check emails to innovators, and track each company\'s China-availability response.',
  finalised_assets: 'Assets shortlisted by Viatris after the first committee review — pending innovator availability confirmation before final client selection.',
  // Recommendation tiers (composite-score driven)
  strategic_priority: 'Top tier — rare must-do partnering target, all dimensions aligned. Pursue immediately.',
  high_priority: 'Strong tier — solid on most dimensions, low risk, fast path to value. Proceed with outreach.',
  medium: 'Mid tier — solid but with 1-2 noteworthy gaps. Proceed with watchlist.',
  monitor: 'Watch tier — interesting but needs more data. Revisit next cycle.',
  low_priority: 'Defer tier — weak across dimensions today. Defer until next milestone or data readout.',
  not_suitable: 'Override — deal-killer risks (IP, safety, crowded, mis-fit). Rejected by AI or operator.',
  could_not_assess: 'AI could not produce a valid composite score for this asset (LLM returned malformed scoring data, asset missing critical fields, etc.). Re-run the assessment or manually mark as Not Suitable to exclude.',
};

// Rich React tooltip content for the COMPOSITE SCORE column. This is the
// canonical definition — anywhere a composite score is rendered, the
// adjacent InfoTip surfaces this so analysts and committee members never
// have to ask "what's the score, exactly?". The formula, range, and 9
// criteria match `_compute_composite()` and `SCORING_CRITERIA` in
// backend/services/viatris_assessment.py.
const COMPOSITE_SCORE_TOOLTIP = (
  <div className="space-y-1.5">
    <div className="font-semibold text-white">Composite Score (out of 45)</div>
    <div className="text-slate-200">
      Sum of <b>9 AI-scored criteria</b>, each rated 1–5 (so range is 9 – 45).
      A parallel <i>Weighted Average</i> (1–5) drives the recommendation tier.
    </div>
    <div className="pt-1">
      <div className="font-medium text-white">The 9 criteria:</div>
      <ol className="list-decimal pl-4 mt-0.5 text-slate-200 space-y-0.5">
        <li>Disease prevalence</li>
        <li>Unmet medical need</li>
        <li>Efficacy</li>
        <li>Safety</li>
        <li>Competitive landscape</li>
        <li>Time to launch</li>
        <li>Strategic alignment (with buyer profile)</li>
        <li>Development cost</li>
        <li>Guideline potential</li>
      </ol>
    </div>
    <div className="pt-1 text-slate-300">
      <b>Recommendation tier</b> derives from the weighted average:
      ≥ 4.5 Strategic Priority · ≥ 3.75 High Priority · ≥ 3.0 Medium ·
      ≥ 2.0 Monitor · &lt; 2.0 Low Priority.
    </div>
  </div>
);

// Innovator availability tracking (Jul 2026 · Client Shortlisted stage).
// Compact chip-styled select rendered on committee-Approved rows so the
// operator can log each innovator's China-availability response inline.
const AVAILABILITY_OPTIONS = ['Not Contacted', 'Email Sent', 'Awaiting Response', 'Available for China', 'Not Available'];
const AVAILABILITY_STYLES = {
  'Not Contacted':       'bg-slate-100 text-slate-600 border-slate-300',
  'Email Sent':          'bg-sky-50 text-sky-700 border-sky-300',
  'Awaiting Response':   'bg-amber-50 text-amber-700 border-amber-300',
  'Available for China': 'bg-emerald-50 text-emerald-700 border-emerald-300',
  'Not Available':       'bg-rose-50 text-rose-700 border-rose-300',
};
function AvailabilityChip({ row, onChange }) {
  const val = AVAILABILITY_OPTIONS.includes(row.availability_status) ? row.availability_status : 'Not Contacted';
  return (
    <select
      value={val}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => onChange(row.id, e.target.value)}
      className={`text-[10px] font-medium rounded-full border px-1.5 py-0.5 cursor-pointer outline-none w-fit max-w-full ${AVAILABILITY_STYLES[val]}`}
      title="Innovator availability status — updates are saved instantly"
      data-testid={`availability-status-${row.id}`}
    >
      {AVAILABILITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

// Aug 2026 · Outreach response tracking — manual Responded Yes/No per asset
// plus a free-text "Rights / Response Note" (2–3 lines, no dropdowns, no AI
// interpretation). Independent of Shortlisted / Team Approved / availability.
function RespondedCell({ row, onToggle, onSaveNote }) {
  const [note, setNote] = useState(row.response_note || '');
  useEffect(() => { setNote(row.response_note || ''); }, [row.response_note]);
  const responded = !!row.responded;
  return (
    <div className="flex flex-col gap-1 items-start w-full">
      <select
        value={responded ? 'Yes' : 'No'}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onToggle(row.id, e.target.value === 'Yes')}
        className={`text-[10px] font-medium rounded-full border px-1.5 py-0.5 cursor-pointer outline-none w-fit ${responded ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-slate-100 text-slate-600 border-slate-300'}`}
        title="Has the asset owner responded to our outreach? Saved instantly."
        data-testid={`responded-select-${row.id}`}
      >
        <option value="No">Responded: No</option>
        <option value="Yes">Responded: Yes</option>
      </select>
      {responded && (
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => { if (note !== (row.response_note || '')) onSaveNote(row.id, note); }}
          onClick={(e) => e.stopPropagation()}
          rows={3}
          maxLength={500}
          placeholder="Rights / Response Note — type exactly what the company communicated"
          className="w-full text-[10px] leading-snug border border-slate-200 rounded p-1 bg-white text-slate-700 resize-none focus:outline-none focus:border-emerald-400"
          title="Rights / Response Note — free text, saved when you click away"
          data-testid={`response-note-${row.id}`}
        />
      )}
    </div>
  );
}

// Small (i) info icon that opens a definition tooltip on hover. Wrap any
// label with this to surface the canonical definition. The `text` prop
// can be a plain string OR a React node (for rich tooltips with tables).
function InfoTip({ text, className = '', wide = false }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        {/* span (not button) — InfoTip renders inside clickable tiles
            that are themselves <button>s; nested buttons are invalid
            HTML and trigger React hydration warnings. */}
        <span
          role="button"
          tabIndex={-1}
          aria-label="Definition"
          className={`inline-flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors cursor-help ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="w-3 h-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className={`${wide ? 'max-w-[480px]' : 'max-w-[280px]'} bg-slate-900 text-white text-[11px] leading-snug px-3 py-2`}
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

// LRI tooltip content removed (Feb 2026) — LRI was demoted from a
// parallel score to background context (see "Deal Context" on each
// asset detail page). The compute_lri service still exists in the
// backend to harvest the underlying enrichment data, but it no longer
// surfaces as a competing score in the workbench.

// Status Pipeline (Feb 2026 v2.2 — client-finalised design):
//
//   Top row · 5-stage pipeline (status-only, no CTAs):
//     Cohort → Phase 3+ → TA of Interest → Committee Review → Finalised Assets
//
//   Bottom row · 3 action buttons (contextual, next-step highlighted):
//     Generate AI Assessment · Create Reports · Start Deep Dive
//
// AI Assessment is an ACTION (not a stage) because it's what moves
// assets from TA of Interest into Committee Review. Reports and Deep
// Dive are downstream actions triggered by the analyst. Deep Dive is
// LOCK-GATED — the cohort must be locked (client has finalised) before
// the button is clickable.
const STATUS_PIPELINE_STAGES = [
  { key: 'cohort',            label: 'Cohort',            icon: Flag,     filterField: '__all__' },
  { key: 'phase_3_plus',      label: 'Phase 3+',          icon: Filter,   filterField: 'phase_3_plus',     filterValue: '1' },
  { key: 'ta_of_interest',    label: 'Therapeutic Area',  icon: Layers,   filterField: 'ta_priority_only', filterValue: '1' },
  { key: 'eligible_assets',   label: 'Eligible Assets',   icon: Check,    filterField: 'eligible_only',    filterValue: '1' },
  { key: 'committee_review',  label: 'Committee Review',  icon: BookOpen, filterField: 'recommendation_active_only', filterValue: '1' },
  { key: 'finalised_assets',  label: 'Finalised Assets',  icon: Check,    filterField: 'committee_status',  filterValue: 'Scored' },
];

// FUNNEL_STAGES removed (Feb 2026 v2) — the 5-stage clickable Status
// Pipeline now tells the full narrowing story on its own.



// Feb 2026 v12 · Small portal helper for the Recommendation
// Distribution tiles. Mounts its children into #viatris-rd-anchor
// (defined inside ExplainableFunnel below the Eligible for AI card).
// Falls back to in-place render on first paint / non-Viatris pages.
function RdPortal({ children }) {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    const el = typeof document !== 'undefined' ? document.getElementById('viatris-rd-anchor') : null;
    setTarget(el);
    // Re-check once shortly after mount in case the anchor hydrates
    // after this component (React commit ordering).
    const t = setTimeout(() => {
      const el2 = document.getElementById('viatris-rd-anchor');
      if (el2 && el2 !== el) setTarget(el2);
    }, 100);
    return () => clearTimeout(t);
  });
  if (!target) return children;
  return createPortal(children, target);
}

// ─── ExplainableFunnel ────────────────────────────────────────────────
//
// Feb 2026 v5 · SOURCE-OF-TRUTH funnel starting from the FULL country
// universe (not the pre-committed engagement cohort). Feeds from
// /api/viatris/universe-pipeline. Every stage clickable → drill-down
// modal listing individual excluded assets.
// Explorer modal that opens when the operator clicks any pipeline stage.
// Server-side paginated (Feb 2026 v6) so the user always sees the true
// total ("1–100 of 1,841") instead of a silently-truncated 500-row list.
function StageDrillModal({ stage, rule, onClose, cohortName: _cohortName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [exporting, setExporting] = useState(false);
  // Feb 2026 v10 · Row-level search + removal-summary aggregation.
  // The search box narrows the *current page* client-side so operators
  // can quickly locate an asset in a long removed-by-TA list without
  // triggering another round-trip.
  const [rowSearch, setRowSearch] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const url = `${API}/viatris/universe-stage-drill?country=China&stage=${encodeURIComponent(stage)}${rule ? `&rule=${encodeURIComponent(rule)}` : ''}&limit=${pageSize}&offset=${offset}`;
    axios.get(url, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ items: [], total: 0, total_returned: 0 }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [stage, rule, offset, pageSize]);

  // Fetch the FULL matching set once (limit=0) to compute the removal
  // summary breakdown regardless of the currently visible page.
  useEffect(() => {
    let alive = true;
    setSummary(null);
    const url = `${API}/viatris/universe-stage-drill?country=China&stage=${encodeURIComponent(stage)}${rule ? `&rule=${encodeURIComponent(rule)}` : ''}&limit=0&offset=0`;
    axios.get(url, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const items = r.data?.items || [];
        // Group by TA when we're on a TA-related stage, otherwise by
        // rule_category. Falls back to `reason` when neither is
        // meaningful (rule drill).
        const groupField =
          stage === 'ta_excluded' || stage === 'phase_excluded' ? 'therapeutic_area'
          : stage === 'modality_excluded' ? 'modality'
          : 'rule_category';
        const buckets = {};
        for (const it of items) {
          const key = (it[groupField] || '<blank>').toString().trim() || '<blank>';
          buckets[key] = (buckets[key] || 0) + 1;
        }
        setSummary({
          total: items.length,
          group_field: groupField,
          rows: Object.entries(buckets).sort((a, b) => b[1] - a[1]).slice(0, 12),
        });
      })
      .catch(() => { if (alive) setSummary({ total: 0, rows: [] }); });
    return () => { alive = false; };
  }, [stage, rule]);

  const total = data?.total || 0;
  const items = data?.items || [];
  const filteredItems = useMemo(() => {
    if (!rowSearch.trim()) return items;
    const q = rowSearch.toLowerCase();
    return items.filter(it =>
      (it.asset_name || '').toLowerCase().includes(q) ||
      (it.company_name || '').toLowerCase().includes(q) ||
      (it.therapeutic_area || '').toLowerCase().includes(q) ||
      (it.modality || '').toLowerCase().includes(q) ||
      (it.reason || '').toLowerCase().includes(q)
    );
  }, [items, rowSearch]);
  const page = Math.floor(offset / pageSize) + 1;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const shown_from = total ? offset + 1 : 0;
  const shown_to = Math.min(offset + items.length, total);

  const heading = stage === 'eligible'
    ? 'Eligible for AI Assessment'
    : `Removed by ${stage.replace('_excluded', '').replace('_', ' ')}`;

  const _exportCsv = async () => {
    setExporting(true);
    try {
      const url = `${API}/viatris/universe-stage-drill?country=China&stage=${encodeURIComponent(stage)}${rule ? `&rule=${encodeURIComponent(rule)}` : ''}&limit=0&offset=0`;
      const r = await axios.get(url, { withCredentials: true });
      const rows = r.data?.items || [];
      const header = ['asset_name', 'company_name', 'phase', 'therapeutic_area', 'modality', 'rule_category', 'reason'];
      const csv = [
        header.join(','),
        ...rows.map(row => header.map(h => `"${(row[h] || '').toString().replace(/"/g, '""')}"`).join(',')),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `screening_${stage}${rule ? '_' + rule.replace(/\s+/g, '_') : ''}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="funnel-drill-modal">
        {/* Section 1 · Header — huge count, plain typography */}
        <DialogHeader>
          <DialogTitle className="flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-black tabular-nums text-slate-900">
              {(summary?.total ?? total).toLocaleString()}
            </span>
            <span className="text-sm font-normal text-slate-600 uppercase tracking-wide">
              {heading}
            </span>
            {rule && (
              <span className="font-mono text-[11px] bg-slate-100 text-slate-700 rounded px-2 py-0.5 border border-slate-200 self-center">
                {rule}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Section 2 · Removal Summary — top offenders grouped by the
            most meaningful field for this stage. */}
        {summary && summary.rows.length > 0 && !rule && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3"
            data-testid="drill-removal-summary">
            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500 mb-2">
              Removal breakdown · by {summary.group_field.replace('_', ' ')}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {summary.rows.map(([label, count]) => (
                <div
                  key={label}
                  className="inline-flex items-center gap-1.5 text-[11px] bg-white border border-slate-200 rounded px-2 py-1"
                  data-testid={`drill-summary-${label.replace(/\s/g, '-').toLowerCase()}`}
                >
                  <span className="text-slate-700 max-w-[220px] truncate" title={label}>{label}</span>
                  <span className="font-bold tabular-nums text-slate-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Section 3 · Toolbar */}
        <div className="flex items-center justify-between gap-2 mt-3 mb-2 text-[11px] text-slate-600 flex-wrap">
          <div data-testid="drill-total">
            {loading ? 'Loading…' : (
              total === 0
                ? 'No assets in this bucket.'
                : <>Showing <strong>{shown_from.toLocaleString()}–{shown_to.toLocaleString()}</strong> of <strong className="text-slate-800">{total.toLocaleString()}</strong> · page {page} of {pages}</>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={rowSearch}
              onChange={(e) => setRowSearch(e.target.value)}
              placeholder="Search asset / company / TA…"
              className="text-[11px] border border-slate-300 rounded px-2 py-1 w-56"
              data-testid="drill-row-search"
            />
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setOffset(0); }}
              className="text-[11px] border border-slate-300 rounded px-1 py-0.5 bg-white"
              data-testid="drill-page-size"
            >
              {[100, 250, 500, 1000].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={_exportCsv}
              disabled={exporting || total === 0}
              className="h-7 text-[11px]"
              data-testid="drill-export-csv"
            >
              {exporting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Exporting…</> : <><Download className="w-3 h-3 mr-1" />Export CSV</>}
            </Button>
          </div>
        </div>

        {/* Section 4 · Asset rows — Feb 2026 v11 · redesigned as
            2-line cards so the "Reason" is prominently visible without
            horizontal scrolling. Metadata (phase / TA / modality)
            renders as inline chips on line 1; the reason takes the
            full width of line 2. */}
        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">
            {rowSearch ? 'No rows match this search.' : 'No assets in this bucket.'}
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[52vh] overflow-y-auto">
            {filteredItems.map((it, i) => (
              <div
                key={it.id || i}
                className="px-3 py-2 hover:bg-slate-50"
                data-testid={`drill-row-${it.id || i}`}
              >
                <div className="flex items-center gap-2 flex-wrap text-[12px]">
                  <span className="font-semibold text-slate-800 truncate max-w-[220px]">{it.asset_name || '—'}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-600 truncate max-w-[220px]">{it.company_name || '—'}</span>
                  {it.phase && (
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 text-[10px] px-1.5 py-0.5 text-slate-700">
                      {it.phase}
                    </span>
                  )}
                  {it.therapeutic_area && (
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 text-[10px] px-1.5 py-0.5 text-slate-700">
                      TA · {it.therapeutic_area}
                    </span>
                  )}
                  {it.modality && (
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 text-[10px] px-1.5 py-0.5 text-slate-700">
                      {it.modality}
                    </span>
                  )}
                </div>
                {it.reason && (
                  <div className="mt-1 flex items-start gap-1.5">
                    <span className="text-[9px] uppercase tracking-wide font-bold text-rose-600 shrink-0 mt-[2px]">Reason</span>
                    <span className="text-[11px] text-slate-700 leading-snug">
                      {it.rule_category && it.rule_category !== 'Other' && it.rule_category !== 'Eligible' && (
                        <span className="text-slate-500 mr-1">({it.rule_category})</span>
                      )}
                      {it.reason}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Section 5 · Pagination */}
        {total > pageSize && (
          <div className="flex items-center justify-between gap-2 mt-3 text-[11px]">
            <div className="text-slate-500">Page <strong>{page.toLocaleString()}</strong> of <strong>{pages.toLocaleString()}</strong></div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={() => setOffset(0)} disabled={offset === 0}
                data-testid="drill-page-first">« First</Button>
              <Button variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={() => setOffset(Math.max(0, offset - pageSize))} disabled={offset === 0}
                data-testid="drill-page-prev">‹ Prev</Button>
              <Button variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={() => setOffset(offset + pageSize)} disabled={offset + pageSize >= total}
                data-testid="drill-page-next">Next ›</Button>
              <Button variant="outline" size="sm" className="h-7 text-[11px]"
                onClick={() => setOffset((pages - 1) * pageSize)} disabled={offset + pageSize >= total}
                data-testid="drill-page-last">Last »</Button>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-3">
          <Button variant="outline" onClick={onClose} data-testid="close-drill">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Human-readable descriptions for each pipeline stage. Rendered as a
// tooltip on hover and as helper text in-card.
const STAGE_DESCRIPTIONS = {
  universe:           'Every asset from a China-based biotech company. This is the starting point of every screening run.',
  phase_filter:       'Removes assets that don\'t meet the Include-phase rule (e.g. Phase 3+).',
  ta_filter:          'Removes assets whose therapeutic area matches the client\'s off-strategy TAs.',
  modality_exclusion: 'Removes assets built on excluded modalities (e.g. Cell therapy, CAR-T).',
  other_exclusion:    'Removes assets excluded by Asset Type, Licensing Status, or Keyword rules.',
  eligible:           'Assets that survived every deterministic rule and are ready for AI assessment.',
  ai_scored:          'Assets that Perplexity + Claude have evaluated and scored.',
  committee_review:   'AI-recommended assets (Strategic + High Priority) — client-visible.',
  finalised:          'Assets the deal committee has marked finalised for outreach.',
};

// Pill button used inside the action bar on top of the Screening Funnel.
function FunnelActionButton({ icon: Icon, label, onClick, disabled, primary, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 rounded-full border text-[12px] font-medium px-3 py-1.5 transition ${
        primary
          ? 'bg-indigo-600 border-indigo-600 text-white hover:bg-indigo-700 disabled:bg-slate-300 disabled:border-slate-300'
          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50'
      } disabled:cursor-not-allowed`}
      data-testid={testId}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

// Rule Audit panel — collapsible section that surfaces the /rule-audit
// diagnostic (Feb 2026 v8 · user demanded rule engine validation).
// Renders in-page so operators don't have to hit a curl endpoint to
// prove the screening pipeline is aligned with the /assets page.
function RuleAuditPanel({ country = 'China' }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assetId, setAssetId] = useState('');
  const [check, setCheck] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/viatris/rule-audit?country=${encodeURIComponent(country)}&stages_csv=Phase+3,Commercial`,
        { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, country, data]);

  const runCheck = async () => {
    if (!assetId.trim()) return;
    setCheckLoading(true);
    setCheck(null);
    try {
      const r = await axios.get(
        `${API}/viatris/rule-check?asset_id=${encodeURIComponent(assetId.trim())}`,
        { withCredentials: true },
      );
      setCheck(r.data);
    } catch (e) {
      setCheck({ error: e?.response?.data?.detail || 'Asset not found' });
    } finally { setCheckLoading(false); }
  };

  const aligned = data && data.divergence_count === 0 && data.assets_page_style_match === data.screening_engine_match;

  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50/60"
      data-testid="rule-audit-panel">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-100/60 rounded-lg"
        data-testid="rule-audit-toggle"
      >
        <div className="flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-indigo-600" />
          <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-700">
            Rule Validation & Debug
          </span>
          {data && (
            <span className={`text-[10px] font-medium rounded-full px-2 py-0.5 border ${
              aligned ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-amber-50 text-amber-800 border-amber-200'
            }`}>
              {aligned ? '✓ /assets & Screening aligned' : `⚠ ${data.divergence_count} divergent`}
            </span>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 border-t border-slate-200 space-y-3">
          {loading && (
            <div className="text-[11px] text-slate-500 flex items-center gap-2 py-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Running audit against DB…
            </div>
          )}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <div className="rounded border border-slate-200 bg-white px-2.5 py-2">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Universe</div>
                  <div className="text-lg font-bold tabular-nums text-slate-800">{data.universe.toLocaleString()}</div>
                </div>
                <div className="rounded border border-slate-200 bg-white px-2.5 py-2">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold" title="Phase 3 + Commercial via /assets page _stage_filter regex">Assets page match</div>
                  <div className="text-lg font-bold tabular-nums text-slate-800">{data.assets_page_style_match.toLocaleString()}</div>
                </div>
                <div className="rounded border border-slate-200 bg-white px-2.5 py-2">
                  <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold" title="Multi-field VIATRIS_PHASE_REGEX match">Screening match</div>
                  <div className="text-lg font-bold tabular-nums text-slate-800">{data.screening_engine_match.toLocaleString()}</div>
                </div>
                <div className={`rounded border px-2.5 py-2 ${aligned ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
                  <div className="text-[9px] uppercase tracking-wide font-semibold text-slate-500">Divergence</div>
                  <div className={`text-lg font-bold tabular-nums ${aligned ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {data.divergence_count}
                  </div>
                </div>
              </div>

              {data.possibly_over_excluded && data.possibly_over_excluded.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50/60 px-2.5 py-2"
                  data-testid="rule-audit-over-excluded">
                  <div className="text-[10px] uppercase tracking-wide font-bold text-amber-900 mb-1">
                    Possibly over-excluded ({data.possibly_over_excluded.length} TA values)
                  </div>
                  <div className="text-[10px] text-amber-800 leading-snug mb-1.5">
                    These TA labels appear in Phase 3+ China assets but don&apos;t match the current TA include regex — arguably legitimate misses (hemophilia · diabetes · etc.).
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {data.possibly_over_excluded.slice(0, 12).map((r) => (
                      <span key={r.ta}
                        className="inline-flex items-center gap-1 text-[10px] rounded-full border border-amber-300 bg-white px-2 py-0.5 text-amber-900">
                        <strong>{r.ta}</strong>
                        <span className="tabular-nums opacity-70">×{r.count}</span>
                        <span className="text-slate-500 italic">{r.hint.split(' (')[0]}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="rounded border border-slate-200 bg-white px-2.5 py-2">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">
                    TA regex hits · Phase 3+ subset (top 8)
                  </div>
                  <div className="space-y-0.5">
                    {(data.phase3_subset_ta_regex_hits || []).slice(0, 8).map((r) => (
                      <div key={r.value} className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-700 truncate mr-2">{r.value}</span>
                        <span className="tabular-nums text-slate-500">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded border border-slate-200 bg-white px-2.5 py-2">
                  <div className="text-[10px] uppercase tracking-wide font-semibold text-rose-700 mb-1">
                    TA regex misses · Phase 3+ subset (top 8)
                  </div>
                  <div className="space-y-0.5">
                    {(data.phase3_subset_ta_regex_misses || []).slice(0, 8).map((r) => (
                      <div key={r.value} className="flex items-center justify-between text-[11px]">
                        <span className="text-slate-700 truncate mr-2">{r.value}</span>
                        <span className="tabular-nums text-slate-500">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <details className="text-[11px] text-slate-600" data-testid="rule-audit-semantics">
                <summary className="cursor-pointer font-semibold text-slate-700">
                  Matching semantics — what fields each rule actually inspects
                </summary>
                <ul className="mt-2 space-y-1.5 pl-2 border-l-2 border-slate-200">
                  {Object.entries(data.matching_semantics || {}).map(([k, v]) => (
                    <li key={k}>
                      <strong className="uppercase text-[10px] text-slate-500 tracking-wide">{k}</strong>
                      <span className="ml-2 text-slate-700">{v}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-2 text-[10px] text-slate-500">
                  Phase regex: <code className="bg-slate-100 rounded px-1">{data.regex_definitions.phase_include_regex}</code>
                  <br />
                  TA regex: <code className="bg-slate-100 rounded px-1">{data.regex_definitions.ta_include_regex}</code>
                </div>
                <div className="mt-2 text-[10px] text-emerald-700 italic">
                  {data.no_ai_confirmation}
                </div>
              </details>
            </>
          )}

          {/* Debug Mode — per-asset inspection */}
          <div className="rounded border border-slate-200 bg-white p-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-slate-700 mb-1.5">
              Debug Mode · inspect one asset
            </div>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={assetId}
                onChange={(e) => setAssetId(e.target.value)}
                placeholder="Paste an asset ID (e.g. from any drill modal row)"
                className="flex-1 text-[11px] border border-slate-300 rounded px-2 py-1 font-mono"
                data-testid="rule-check-input"
              />
              <Button size="sm" onClick={runCheck}
                disabled={checkLoading || !assetId.trim()}
                data-testid="rule-check-btn"
                className="h-7 text-[11px]">
                {checkLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Check'}
              </Button>
            </div>
            {check && !check.error && (
              <div className="mt-2 text-[11px]" data-testid="rule-check-result">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={`font-semibold ${check.final_decision === 'eligible' ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {check.final_decision === 'eligible' ? '✓ Eligible' : '✗ Excluded'}
                  </span>
                  <span className="text-slate-500">·</span>
                  <span className="font-medium text-slate-700">{check.asset_name || '—'}</span>
                  <span className="text-slate-500">·</span>
                  <span className="text-slate-600">{check.company_name || '—'}</span>
                  {check.failed_stage && (
                    <span className="ml-2 text-[10px] bg-rose-50 text-rose-700 rounded px-1.5 py-0.5 border border-rose-200">
                      failed: {check.failed_stage}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
                  {check.checks.map((c) => (
                    <div key={c.stage}
                      className={`rounded border p-1.5 ${
                        c.pass === true ? 'border-emerald-200 bg-emerald-50/50'
                        : c.pass === false ? 'border-rose-200 bg-rose-50/50'
                        : 'border-slate-200 bg-slate-50'
                      }`}>
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
                        {c.pass === true ? '✓' : c.pass === false ? '✗' : '—'} {c.stage.replace('_', ' ')}
                      </div>
                      {c.inspected_value !== undefined && (
                        <div className="text-[10px] text-slate-600 truncate" title={c.inspected_value}>
                          Value: <span className="font-mono">{c.inspected_value || '—'}</span>
                        </div>
                      )}
                      {c.inspected_values && (
                        <div className="text-[10px] text-slate-600 space-y-0.5 mt-0.5">
                          {Object.entries(c.inspected_values).map(([k, v]) => (
                            <div key={k} className="truncate" title={v}>
                              <span className={c.matched_field === k ? 'font-semibold text-emerald-700' : ''}>{k}</span>: <span className="font-mono">{v || '—'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {c.reason && (
                        <div className="text-[10px] text-rose-700 mt-0.5">{c.reason}</div>
                      )}
                      {c.hint && (
                        <div className="text-[10px] text-amber-700 italic mt-0.5">{c.hint}</div>
                      )}
                      {c.note && (
                        <div className="text-[10px] text-slate-500 italic mt-0.5">{c.note}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {check?.error && (
              <div className="mt-2 text-[11px] text-rose-700">{check.error}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Section 4/5/11 of the UX spec — a single left-to-right journey.
// Every stage becomes a card that shows:
//   • Input        (assets entering the stage)
//   • Removed      (labelled, clickable → drill modal)
//   • Remaining    (headline number)
//   • % removed
//
// AI + Committee stages are state-aware ("Not Started" until AI runs).
// Section 2 & 12: "The pipeline itself becomes the audit" — the old
// Database Screening Audit table is gone; every count lives here.
function ExplainableFunnel({
  cohortName,
  country = 'China',  // Feb 2026 · Modulus passes 'Global' to load the entire universe
  refreshKey = 0,
  onRefreshPipeline,
  onRestoreShortlist,
  onRunAssessment,
  onSyncUniverseEligible,
  onSyncFromMaster,
  onFixMisShortlisted,
  onOpenReports,
  pipelineWorkflowRunning = false,
  aiOverrides = null,
  onStageFilter = null,
  activeStageFilter = '',
  onShortlistFilter = null,
  shortlistActive = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);   // {stage, rule?}
  const [rulesOpen, setRulesOpen] = useState({ stage: null });
  // Universe finalize/lock (Jul 2026)
  const [universeLock, setUniverseLock] = useState(null);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [refreshScreeningOpen, setRefreshScreeningOpen] = useState(false);
  const [locking, setLocking] = useState(false);
  // Data-integrity validation report
  const [validation, setValidation] = useState(null);
  const [validationOpen, setValidationOpen] = useState(false);
  const [validating, setValidating] = useState(false);

  const runValidation = async () => {
    setValidating(true);
    setValidationOpen(true);
    setValidation(null);
    try {
      const { data } = await axios.get(`${API}/viatris/validation-report`, { withCredentials: true });
      setValidation(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Validation failed');
      setValidationOpen(false);
    } finally { setValidating(false); }
  };

  const fetchLockStatus = useCallback(() => {
    axios.get(`${API}/viatris/universe/lock-status`, { withCredentials: true })
      .then((r) => setUniverseLock(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { fetchLockStatus(); }, [fetchLockStatus, refreshKey]);

  const onOpenFinalize = () => { setFinalizeConfirm(false); setFinalizeOpen(true); };
  const onOpenUnlock = () => setUnlockOpen(true);

  const handleFinalize = async () => {
    setLocking(true);
    try {
      const { data } = await axios.post(`${API}/viatris/universe/finalize-lock`, {}, { withCredentials: true });
      const s = data.summary || {};
      toast.success(`Universe finalized & locked — ${s.eligible} eligible · ${s.committee_review} committee. Nothing will change until you unlock.`, { duration: 10000 });
      setFinalizeOpen(false);
      fetchLockStatus();
      window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally { setLocking(false); }
  };

  const handleUnlock = async () => {
    setLocking(true);
    try {
      await axios.delete(`${API}/viatris/universe/lock`, { withCredentials: true });
      toast.success('Universe unlocked — numbers are live again. Re-lock when ready.');
      setUnlockOpen(false);
      fetchLockStatus();
      window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlock failed');
    } finally { setLocking(false); }
  };

  // Jul 2026 v4 · Refresh Screening — re-baseline the frozen universe in
  // one click so newly added assets enter ONLY when the operator says so.
  const handleRefreshScreening = async () => {
    setLocking(true);
    try {
      const { data } = await axios.post(`${API}/viatris/universe/refresh-screening`, {}, { withCredentials: true });
      const diff = data.added || 0;
      toast.success(
        `Screening refreshed — eligible universe ${data.old_eligible} → ${data.new_eligible}` +
        (diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff} asset${Math.abs(diff) === 1 ? '' : 's'})` : ' (no change)'),
        { duration: 10000 },
      );
      setRefreshScreeningOpen(false);
      fetchLockStatus();
      window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh screening failed');
    } finally { setLocking(false); }
  };

  // Jul 2026 · Maintenance Mode — repeatable post-conference data-quality
  // cycle. Re-opens Refresh/Sync/Run-AI while KEEPING the lock + client
  // shortlist snapshot; Complete re-baselines the frozen funnel.
  const handleStartMaintenance = async () => {
    setLocking(true);
    try {
      await axios.post(`${API}/viatris/universe/maintenance/start`, {}, { withCredentials: true });
      toast.success('Maintenance Mode ON — screening re-opened. Add/remove assets, then Refresh Screening → Sync → Run AI. Committee decisions & client shortlist stay intact.', { duration: 12000 });
      fetchLockStatus();
      window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start maintenance');
    } finally { setLocking(false); }
  };

  const handleCompleteMaintenance = async () => {
    setLocking(true);
    try {
      const { data } = await axios.post(`${API}/viatris/universe/maintenance/complete`, {}, { withCredentials: true });
      const diff = data.added || 0;
      const bits = [`eligible universe ${data.old_eligible} → ${data.new_eligible}` +
        (diff !== 0 ? ` (${diff > 0 ? '+' : ''}${diff})` : ' (no change)')];
      if (data.decisions_restored > 0) bits.push(`${data.decisions_restored} lost committee decision(s) restored`);
      toast.success(`Maintenance complete — universe re-frozen. ${bits.join(' · ')}`, { duration: 12000 });
      fetchLockStatus();
      window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not complete maintenance');
    } finally { setLocking(false); }
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/viatris/universe-pipeline?country=${country}`, { withCredentials: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [refreshKey]);

  const openDrill = (stage, rule = '') => setDrill({ stage, rule });

  // Jul 2026 · apply single-source overrides — the parent derives the
  // AI-section numbers from the SAME inventory rows that power the
  // Recommendation Distribution tiles and the table, so every card on
  // this page reconciles arithmetically. Server numbers remain as the
  // fallback until the inventory has loaded.
  const stages = (data?.stages || []).map((s) => {
    if (!aiOverrides) return s;
    if (s.key === 'eligible' && aiOverrides.eligible != null) {
      return { ...s, count: aiOverrides.eligible };
    }
    if (s.key === 'ai_scored' && aiOverrides.ai_scored != null) {
      return { ...s, count: aiOverrides.ai_scored };
    }
    if (s.key === 'committee_review' && aiOverrides.committee_review != null) {
      return {
        ...s,
        count: aiOverrides.committee_review,
        breakdown: aiOverrides.committee_breakdown || s.breakdown,
        committee_status_breakdown: aiOverrides.committee_status_breakdown || s.committee_status_breakdown,
      };
    }
    if (s.key === 'finalised' && aiOverrides.finalised != null) {
      return { ...s, count: aiOverrides.finalised,
               availability_breakdown: aiOverrides.availability_breakdown };
    }
    return s;
  });
  const buckets = data?.ai_score_buckets || {};
  const aiTotal = Object.values(buckets).reduce((s, v) => s + (v || 0), 0);
  const aiRun = aiTotal > 0;

  // Split stages into two visual bands — Database Screening vs AI
  // Assessment — with a separator between them (spec §7).
  const dbStages = stages.filter(s => s.section === 'database');
  const boundary = stages.find(s => s.section === 'boundary');
  const aiStages = stages.filter(s => s.section === 'ai');

  const renderFilterCard = (s, keyIdx) => {
    const stageForDrill =
      s.key === 'phase_filter' ? 'phase_excluded'
      : s.key === 'ta_filter' ? 'ta_excluded'
      : s.key === 'modality_exclusion' ? 'modality_excluded'
      : s.key === 'other_exclusion' ? 'other_excluded'
      : null;
    const isFilter = 'input' in s;
    const isTerminal = 'count' in s;
    const canExpand = (s.rule_effects || []).length > 0;
    const isOpen = rulesOpen.stage === s.key;

    // Feb 2026 v13 · Minimal card. Every non-essential detail moves
    // out — Removed % + count collapse to one clickable subtitle, and
    // "N rules applied" now opens in a shared full-width panel below
    // the row (rendered by the parent) instead of expanding inside
    // this narrow column.
    const cardCls =
      s.key === 'universe'   ? 'border-slate-300 bg-slate-50'
      : s.key === 'eligible' ? 'border-emerald-300 bg-emerald-50'
      : isOpen               ? 'border-slate-400 bg-white ring-1 ring-slate-300'
      : 'border-slate-200 bg-white';

    return (
      <div
        key={s.key}
        className={`flex-1 min-w-[160px] rounded-lg border p-3 flex flex-col gap-1 ${cardCls}`}
        data-testid={`funnel-stage-${s.key}`}
        title={STAGE_DESCRIPTIONS[s.key] || ''}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-wide font-semibold text-slate-400 leading-tight">
              Stage {keyIdx + 1}
            </div>
            <div className="text-[12px] font-bold text-slate-800 leading-tight truncate mt-0.5">
              {s.label}
            </div>
          </div>
          <InfoTip text={STAGE_DESCRIPTIONS[s.key] || s.label} />
        </div>

        {isFilter && (
          <>
            <div className="text-[36px] font-black text-slate-900 tabular-nums leading-none mt-1"
              data-testid={`funnel-remaining-${s.key}`}>
              {(s.output || 0).toLocaleString()}
            </div>
            <div className="text-[10px] text-slate-400 leading-tight">
              of {(s.input || 0).toLocaleString()} · after {s.label.toLowerCase()}
            </div>

            <div className="mt-1 flex items-center justify-between gap-1">
              {s.excluded > 0 ? (
                <button
                  type="button"
                  onClick={() => openDrill(stageForDrill)}
                  className="text-rose-700 hover:text-rose-900 tabular-nums text-left"
                  data-testid={`funnel-removed-${s.key}`}
                  title="Explore removed assets"
                >
                  <span className="block text-[9px] uppercase tracking-wide text-rose-500 font-semibold leading-none">Removed</span>
                  <span className="block text-[12px] font-semibold leading-tight mt-0.5">
                    {s.excluded.toLocaleString()} assets <span className="text-slate-400 font-normal">({s.pct_removed}%)</span>
                  </span>
                </button>
              ) : (
                <span className="text-[10px] text-slate-400">No removals</span>
              )}
              {canExpand && (
                <button
                  type="button"
                  onClick={() => setRulesOpen(v => ({ stage: v.stage === s.key ? null : s.key }))}
                  className="text-[10px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-0.5"
                  data-testid={`funnel-expand-${s.key}`}
                >
                  {s.rule_effects.length} rules {isOpen ? '▴' : '▾'}
                </button>
              )}
            </div>
          </>
        )}

        {isTerminal && (
          <button
            type="button"
            onClick={() => s.key === 'eligible' ? openDrill('eligible') : null}
            disabled={s.key !== 'eligible'}
            className={`text-[36px] font-black tabular-nums leading-none mt-1 text-left ${
              s.key === 'eligible' ? 'text-emerald-700 hover:underline cursor-pointer' : 'text-slate-900'
            }`}
            data-testid={`funnel-count-${s.key}`}
          >
            {(s.count || 0).toLocaleString()}
          </button>
        )}
        {isTerminal && s.hint && (
          <div className="text-[10px] text-slate-500 italic">{s.hint}</div>
        )}
      </div>
    );
  };

  const renderAiCard = (s, keyIdx) => {
    const enabled = aiRun && (s.count || 0) > 0;
    const displayVal = enabled ? (s.count || 0).toLocaleString() : (aiRun ? '0' : '—');
    // Stage 2 (Committee Review) is clickable — filters the table below to
    // the active-review tiers (Strategic + High + Medium). Stage 3 (Client
    // Shortlisted / Availability Check) is clickable — filters the table to
    // committee-Approved assets. Both toggle off on a second click.
    const isCommitteeClickable = s.key === 'committee_review' && enabled && typeof onStageFilter === 'function';
    const isShortlistCard = s.key === 'finalised';
    const isShortlistClickable = isShortlistCard && aiRun && typeof onShortlistFilter === 'function';
    const isClickable = isCommitteeClickable || isShortlistClickable;
    const isActiveFilter = (s.key === 'committee_review' && activeStageFilter === '__committee__')
      || (isShortlistCard && shortlistActive);
    const handleCardClick = isCommitteeClickable
      ? () => onStageFilter(isActiveFilter ? '' : '__committee__')
      : isShortlistClickable ? () => onShortlistFilter(!shortlistActive) : undefined;
    // Feb 2026 v10 · Muted AI card palette. Accent only when the stage
    // holds material state: committee_review = indigo (client-visible),
    // finalised = emerald (success). Interior AI stages stay slate.
    const cardCls = isShortlistCard && aiRun
      ? (shortlistActive ? 'border-emerald-500 bg-emerald-100 ring-2 ring-emerald-400' : 'border-emerald-300 bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100')
      : !enabled
      ? 'border-dashed border-slate-300 bg-slate-50'
      : s.key === 'committee_review' ? (isActiveFilter ? 'border-indigo-500 bg-indigo-100 ring-2 ring-indigo-400' : 'border-indigo-300 bg-indigo-50 hover:border-indigo-400 hover:bg-indigo-100')
      : s.key === 'finalised'        ? 'border-emerald-300 bg-emerald-50'
      : 'border-slate-200 bg-white';

    return (
      <div
        key={s.key}
        onClick={handleCardClick}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } } : undefined}
        className={`flex-1 min-w-[180px] rounded-lg border p-3 flex flex-col gap-1.5 transition-colors ${cardCls} ${isClickable ? 'cursor-pointer' : ''}`}
        data-testid={`funnel-stage-${s.key}`}
        title={isClickable
          ? (isActiveFilter ? 'Click to clear this filter'
            : isShortlistCard ? 'Click to filter the table to client-shortlisted assets (committee Approved)'
            : 'Click to filter the table to Committee Review assets (Strategic + High + Medium)')
          : (STAGE_DESCRIPTIONS[s.key] || '')}
      >
        <div className="flex items-start justify-between gap-1">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 leading-tight">
              Stage {keyIdx + 1}
            </div>
            <div className="text-[13px] font-bold text-slate-800 leading-tight truncate">
              {s.label}
            </div>
          </div>
          <InfoTip text={STAGE_DESCRIPTIONS[s.key] || s.label} />
        </div>
        <div
          className={`text-3xl font-black tabular-nums leading-none mt-1 ${
            enabled ? 'text-slate-900' : 'text-slate-400'
          }`}
          data-testid={`funnel-ai-${s.key}`}
        >
          {displayVal}
        </div>
        {enabled && (
          <div className="text-[10px] font-medium text-slate-400 -mt-0.5">
            {s.key === 'committee_review' ? 'Pending review'
              : s.key === 'finalised' ? 'Client shortlisted'
              : 'Assets'}
          </div>
        )}
        {/* Jul 2026 · Stage 3 availability tally — live innovator response
            breakdown so the operator sees check progress at a glance.
            Note: the "outside universe" chip was intentionally removed
            (Feb 2026) — the count is still tracked server-side, but
            surfacing it here confused the client-facing Stage 3 view. */}
        {isShortlistCard && (s.count || 0) > 0 && s.availability_breakdown && (
          <div className="flex flex-wrap gap-1 mt-0.5" data-testid="availability-tally">
            <span className="text-[9px] font-semibold rounded-full border px-1.5 py-px bg-emerald-50 text-emerald-700 border-emerald-300">
              {s.availability_breakdown.available} available
            </span>
            <span className="text-[9px] font-semibold rounded-full border px-1.5 py-px bg-rose-50 text-rose-700 border-rose-300">
              {s.availability_breakdown.not_available} not available
            </span>
            <span className="text-[9px] font-semibold rounded-full border px-1.5 py-px bg-amber-50 text-amber-700 border-amber-300">
              {s.availability_breakdown.awaiting} awaiting
            </span>
          </div>
        )}
        <div className="text-[10px] text-slate-500 italic">
          {!aiRun ? 'Not started · run AI Assessment'
            : isActiveFilter ? 'Filtering table · click to clear'
            : isShortlistClickable ? 'Click to view shortlisted assets'
            : isClickable ? 'Click to review these assets'
            : s.hint || ''}
        </div>
        {/* Feb 2026 v24 · Removed the tier chip strip (Strategic / High
            / Medium) and the committee status sub-strip (Pending /
            Under Review / Completed) from the AI Stage cards. User
            asked for a clean image-feel — the number + subtitle are
            enough on the funnel card. The full breakdown still lives
            on the Recommendation Distribution row above (for tier)
            and inside the Committee Progress modal (for status). */}
      </div>
    );
  };

  if (!data && !loading) return null;

  return (
    <div className="mt-4 border border-slate-200 rounded-lg bg-white p-4" data-testid="explainable-funnel">
      {/* Header · title + top-line action bar */}
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[13px] uppercase tracking-wide font-bold text-slate-800">
            Screening Funnel
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 max-w-2xl">
            One journey: {stages[0]?.count?.toLocaleString?.() || '—'} assets in the universe are progressively narrowed down to the final shortlist eligible for AI evaluation. Every count is clickable.
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Jul 2026 · Buttons follow the workflow order: Maintenance →
              Reports → Run AI Assessment → Finalize & Lock. Run AI stays
              the visually dominant (primary) button since it's the main
              action, but sits in workflow position. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition"
                data-testid="funnel-action-maintenance-menu"
              >
                <Wrench className="w-3.5 h-3.5" /> Maintenance
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Pipeline maintenance</DropdownMenuLabel>
              <DropdownMenuItem
                onClick={onRefreshPipeline}
                disabled={pipelineWorkflowRunning}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-refresh"
              >
                <span className="font-medium inline-flex items-center gap-1">
                  <RefreshCw className={`w-3.5 h-3.5 ${pipelineWorkflowRunning ? 'animate-spin' : ''}`} />
                  {pipelineWorkflowRunning ? 'Refreshing…' : 'Refresh Screening'}
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Pull newly added assets into the pipeline & re-classify — auto-manages the maintenance lock. Existing scores, committee decisions & shortlist preserved.</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onSyncUniverseEligible}
                disabled={pipelineWorkflowRunning || !cohortName}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-sync-universe"
              >
                <span className="font-medium inline-flex items-center gap-1">
                  <GitMerge className="w-3.5 h-3.5" /> Sync Eligible → Cohort
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Apply all screening rules (Stage → TA → Modality → Asset Type → Licensing → Keywords) & populate the Eligible for AI list</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={runValidation}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-validate"
              >
                <span className="font-medium inline-flex items-center gap-1 text-indigo-700">
                  <ShieldCheck className="w-3.5 h-3.5" /> Data Integrity Check
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Final QC before AI — duplicates, missing fields, orphans & funnel count reconciliation</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onSyncFromMaster}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-sync-from-master"
              >
                <span className="font-medium inline-flex items-center gap-1 text-emerald-700">
                  <Database className="w-3.5 h-3.5" /> Sync Names from Master
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Refresh Asset Name, Company, Phase, TA, Modality across Committee Review / Reviewer / Client Portal from the master record — fixes stale snapshots (e.g. Ginkgolide Products → Jingelai)</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onFixMisShortlisted}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-fix-mis-shortlisted"
              >
                <span className="font-medium inline-flex items-center gap-1 text-rose-700">
                  <ShieldAlert className="w-3.5 h-3.5" /> Fix Mis-shortlisted Assets
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Find & un-shortlist any Monitor / Low / Not-Suitable / CNA assets marked Approved — restores Stage 3 to Strategic + High + Medium only</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition"
                data-testid="funnel-action-reports-menu"
              >
                <FileText className="w-3.5 h-3.5" /> Reports & Exports
                <ChevronDown className="w-3 h-3 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Universe &amp; Screening Exports</DropdownMenuLabel>
              <div className="px-2 pb-1 text-[10px] text-slate-400 leading-tight">
                Master screening universe only — cohort exports live on the Cohort 1 &amp; Cohort 2 cards at the top of the page.
              </div>
              <DropdownMenuItem
                onClick={() => {
                  window.open(`${API}/viatris/export-partneringone?scope=master&country=China`, '_blank');
                  toast.success('Downloading China Universe · Stage 1 (all assets, pre-screening)');
                }}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-export-master"
              >
                <span className="font-medium inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Export China Universe
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Stage 1 — every China asset before any screening rules</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  window.open(`${API}/viatris/export-partneringone?scope=filtered&country=China`, '_blank');
                  toast.success('Downloading Eligible for AI · deterministic screening output');
                }}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-export-filtered"
              >
                <span className="font-medium inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Export Eligible for AI
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Final deterministic output after all inclusion/exclusion rules</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  window.open(`${API}/viatris/export-partneringone?scope=recommended&country=China`, '_blank');
                  toast.success('Downloading Client Recommendations · Strategic + High + Medium');
                }}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-export-recommended"
              >
                <span className="font-medium inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Export Client Recommendations
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Post-AI list — Strategic / High / Medium with score, tier & committee status</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={onOpenReports}
                disabled={!cohortName}
                className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                data-testid="funnel-action-reports"
              >
                <span className="font-medium inline-flex items-center gap-1 text-indigo-700">
                  <FileText className="w-3.5 h-3.5" /> Generate Client Report
                </span>
                <span className="text-[10px] text-slate-500 leading-tight">Presentation-ready summary of the full pipeline for client sharing</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Aug 2026 v3 · funnel-level "Run AI Assessment" button REMOVED —
              the Phase 2 card's "Run AI on N Assets" is the operational CTA
              (phase-scoped). Row-level Run AI buttons remain in the table. */}

          {universeLock?.locked ? (
            universeLock?.maintenance ? (
              <button
                type="button"
                onClick={handleCompleteMaintenance}
                disabled={locking || pipelineWorkflowRunning}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 transition disabled:opacity-40"
                data-testid="funnel-maintenance-active-badge"
                title={`Maintenance started ${(universeLock.maintenance_started_at || '').slice(0,10)}${universeLock.maintenance_started_by ? ' by ' + universeLock.maintenance_started_by : ''} — screening temporarily re-opened. Click to complete maintenance & re-freeze the universe.`}
              >
                <Wrench className="w-3.5 h-3.5" />
                Maintenance Mode
                <span className="ml-0.5 text-[10px] font-normal opacity-70">click to complete</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onOpenUnlock}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition"
                data-testid="funnel-universe-locked-badge"
                title={`Screening frozen ${(universeLock.locked_at || '').slice(0,10)}${universeLock.locked_by ? ' by ' + universeLock.locked_by : ''} — committee & shortlist numbers stay live. Click to unlock.`}
              >
                <Lock className="w-3.5 h-3.5" />
                Universe Locked
                {universeLock.summary && (
                  <span className="ml-0.5 text-[10px] font-normal text-emerald-600">
                    {universeLock.summary.eligible} elig
                  </span>
                )}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={onOpenFinalize}
              disabled={pipelineWorkflowRunning || !cohortName}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-md border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="funnel-action-finalize-lock"
            >
              <Lock className="w-3.5 h-3.5" /> Finalize &amp; Lock
            </button>
          )}

          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400 ml-1" />}
        </div>
      </div>

      {/* Deterministic screening band */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-200">
          Deterministic Screening
        </span>
        <span className="text-[10px] text-slate-400">deterministic · no AI credits used</span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>
      <div className="flex items-stretch gap-1.5 overflow-x-auto pb-1">
        {dbStages.map((s, i) => (
          <React.Fragment key={s.key}>
            {renderFilterCard(s, i)}
            {i < dbStages.length - 1 && (
              <div className="flex items-center text-slate-300 shrink-0">
                <ChevronRight className="w-5 h-5" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Feb 2026 v13 · Shared full-width rule breakdown panel — opens
          below the row of cards instead of expanding inside the narrow
          column. Only one panel visible at a time; chips flow freely. */}
      {rulesOpen.stage && (() => {
        const st = dbStages.find(x => x.key === rulesOpen.stage);
        if (!st || !(st.rule_effects || []).length) return null;
        const stageForDrill =
          st.key === 'phase_filter' ? 'phase_excluded'
          : st.key === 'ta_filter' ? 'ta_excluded'
          : st.key === 'modality_exclusion' ? 'modality_excluded'
          : st.key === 'other_exclusion' ? 'other_excluded'
          : null;
        return (
          <div
            className="mt-2 rounded-lg border border-slate-200 bg-slate-50/70 p-3"
            data-testid={`funnel-rules-panel-${st.key}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                {st.label} · {st.rule_effects.length} rules applied
              </div>
              <button
                type="button"
                onClick={() => setRulesOpen({ stage: null })}
                className="text-[10px] text-slate-500 hover:text-slate-800"
                data-testid="funnel-rules-panel-close"
              >
                Close ✕
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {st.rule_effects.map((re) => (
                <button
                  key={re.rule}
                  type="button"
                  onClick={() => openDrill(stageForDrill, re.rule)}
                  className="inline-flex items-center gap-1.5 text-[11px] rounded-full border border-slate-200 bg-white hover:bg-slate-100 hover:border-slate-300 text-slate-700 px-2.5 py-1"
                  data-testid={`funnel-rule-${st.key}-${re.rule.replace(/\s/g, '-').toLowerCase()}`}
                  title={`${re.count} asset${re.count === 1 ? '' : 's'} removed by "${re.rule}" · click to inspect`}
                >
                  <span>{re.rule}</span>
                  <span className="text-slate-400 tabular-nums font-medium">{re.count}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Aug 2026 UX simplification: the large green "Current Eligible
          Universe" banner was REMOVED — the top Live China Universe summary
          already states the currently-eligible count. */}

      {/* Aug 2026 v3 · the funnel now STOPS at the deterministic output.
          The former AI band (Recommendation Distribution anchor, AI
          Assessed / Committee Review / Client Shortlisted cards) duplicated
          the Phase 1 / Phase 2 workflow cards and was removed per user
          directive — "the expandable section should contain ONLY the
          deterministic screening history". */}

      {drill && (
        <StageDrillModal
          stage={drill.stage}
          rule={drill.rule}
          onClose={() => setDrill(null)}
          cohortName={cohortName}
        />
      )}

      {/* Finalize & Lock Universe confirmation */}
      <Dialog open={finalizeOpen} onOpenChange={(o) => { if (!o) setFinalizeOpen(false); }}>
        <DialogContent className="max-w-lg" data-testid="finalize-lock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Lock className="w-4 h-4 text-indigo-600" /> Finalize &amp; Lock Universe
            </DialogTitle>
            <DialogDescription>
              This freezes the <span className="font-medium text-slate-700">entire cross-section</span> at its current state — the screening funnel, the recommendation distribution, committee review, and the client export. Once locked, <span className="font-medium text-slate-700">nothing in this universe can change</span> (screening re-runs and AI re-runs are blocked) until an admin unlocks it.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-2 gap-2 text-[13px]">
            <div className="flex items-center justify-between"><span className="text-slate-500">China Universe</span><span className="font-semibold">{data?.universe ?? '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Eligible for AI</span><span className="font-semibold">{aiOverrides?.eligible ?? (stages.find(s=>s.key==='eligible')?.count) ?? '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">Committee Review</span><span className="font-semibold">{aiOverrides?.committee_review ?? (stages.find(s=>s.key==='committee_review')?.count) ?? '—'}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500">AI Assessed</span><span className="font-semibold">{aiOverrides?.ai_scored ?? (stages.find(s=>s.key==='ai_scored')?.count) ?? '—'}</span></div>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">The client-committed 80-asset shortlist is restored to its original tiers before locking, so the frozen cross-section matches exactly what the client received.</p>
          <label className="flex items-start gap-2 mt-1 cursor-pointer">
            <Checkbox checked={finalizeConfirm} onCheckedChange={setFinalizeConfirm} data-testid="finalize-confirm-checkbox" className="mt-0.5" />
            <span className="text-[13px] text-slate-700">I confirm this is the client-committed snapshot and want to lock it.</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinalizeOpen(false)} data-testid="finalize-cancel">Cancel</Button>
            <Button onClick={handleFinalize} disabled={!finalizeConfirm || locking} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="finalize-confirm-btn">
              {locking ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Lock className="w-3.5 h-3.5 mr-1" />}
              Finalize &amp; Lock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unlock confirmation */}
      <Dialog open={refreshScreeningOpen} onOpenChange={(o) => { if (!o) setRefreshScreeningOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="refresh-screening-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <RefreshCw className="w-4 h-4 text-sky-600" /> Refresh Screening?
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              This re-runs the deterministic screening and <span className="font-medium text-slate-700">re-baselines the frozen universe</span> —
              newly added or edited assets will enter (or leave) the eligible set now, and the new cross-section becomes the locked baseline.
              Committee decisions, shortlist marks and availability statuses are untouched.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRefreshScreeningOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-sky-600 hover:bg-sky-700 text-white"
              onClick={handleRefreshScreening}
              disabled={locking}
              data-testid="refresh-screening-confirm-btn"
            >
              {locking ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Refreshing…</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh baseline</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unlockOpen} onOpenChange={(o) => { if (!o) setUnlockOpen(false); }}>
        <DialogContent className="max-w-md" data-testid="unlock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Lock className="w-4 h-4 text-amber-600" /> Unlock Universe?
            </DialogTitle>
            <DialogDescription>
              Unlocking makes all numbers <span className="font-medium text-slate-700">live again</span>, and screening / AI re-runs become possible — which means the counts can shift. Only unlock if you intend to re-baseline this engagement.
              {universeLock?.locked_at && (
                <span className="block mt-2 text-[11px] text-slate-400">Locked {String(universeLock.locked_at).slice(0,10)}{universeLock.locked_by ? ` by ${universeLock.locked_by}` : ''}.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockOpen(false)} data-testid="unlock-cancel">Keep Locked</Button>
            <Button onClick={handleUnlock} disabled={locking} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="unlock-confirm-btn">
              {locking ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Integrity Validation report */}
      <Dialog open={validationOpen} onOpenChange={(o) => { if (!o) setValidationOpen(false); }}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="validation-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <ShieldCheck className="w-4 h-4 text-indigo-600" /> Data Integrity Check
            </DialogTitle>
            <DialogDescription>Validates the Recommended set is unique per molecule and reconciles across every module before you publish.</DialogDescription>
          </DialogHeader>
          {validating || !validation ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-3">
              <div className={`rounded-lg px-3 py-2 text-[13px] font-medium ${validation.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="validation-verdict">
                {validation.ok
                  ? '✓ Ready to publish — every asset appears exactly once and all counts reconcile.'
                  : `⚠ ${validation.issues.length} issue(s) must be resolved before publishing:`}
                {!validation.ok && (
                  <ul className="mt-1 list-disc pl-5 font-normal space-y-0.5">
                    {validation.issues.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                )}
              </div>
              {validation.advisories?.length > 0 && (
                <div className="rounded-lg px-3 py-2 text-[12px] bg-slate-50 text-slate-600 border border-slate-200" data-testid="validation-advisories">
                  <span className="font-medium text-slate-700">Advisory notes (no action required):</span>
                  <ul className="mt-0.5 list-disc pl-5 space-y-0.5">
                    {validation.advisories.map((it, i) => <li key={i}>{it}</li>)}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 text-[13px]">
                <div className="flex justify-between rounded border border-slate-200 px-2 py-1.5"><span className="text-slate-500">Unique recommended</span><span className="font-semibold">{validation.unique_recommended_assets}</span></div>
                <div className="flex justify-between rounded border border-slate-200 px-2 py-1.5"><span className="text-slate-500">Duplicates removed</span><span className="font-semibold">{validation.duplicates_removed}</span></div>
                <div className="flex justify-between rounded border border-slate-200 px-2 py-1.5"><span className="text-slate-500">Committee tile</span><span className="font-semibold">{validation.committee_review_tile}</span></div>
                <div className="flex justify-between rounded border border-slate-200 px-2 py-1.5"><span className="text-slate-500">Counts reconcile</span><span className={`font-semibold ${validation.counts_reconcile ? 'text-emerald-600' : 'text-red-600'}`}>{validation.counts_reconcile ? 'Yes' : 'No'}</span></div>
              </div>
              {validation.shortlist_lock && (
                <div
                  className={`rounded-lg border px-3 py-2 ${validation.shortlist_lock.locked ? (validation.shortlist_lock.all_tiered ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200') : 'bg-slate-50 border-slate-200'}`}
                  data-testid="validation-shortlist-lock"
                >
                  {validation.shortlist_lock.locked ? (
                    <>
                      <div className="flex items-center justify-between text-[13px]">
                        <span className="font-medium inline-flex items-center gap-1 text-slate-700">
                          <Lock className="w-3.5 h-3.5" /> Client shortlist LOCKED
                        </span>
                        <span className="text-[11px] text-slate-500">{validation.shortlist_lock.locked_at}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[12px]">
                        <span>Locked: <b>{validation.shortlist_lock.total}</b></span>
                        <span>Tiered: <b className="text-emerald-700">{validation.shortlist_lock.tiered}</b></span>
                        <span className={validation.shortlist_lock.blank ? 'text-red-600' : ''}>Blank: <b>{validation.shortlist_lock.blank}</b></span>
                        {Object.entries(validation.shortlist_lock.tier_breakdown || {}).map(([t, n]) => (
                          <span key={t} className="text-slate-500">{t}: <b>{n}</b></span>
                        ))}
                      </div>
                      {validation.shortlist_lock.blank > 0 && (
                        <div className="mt-1 text-[11px] text-red-600 truncate">
                          Needs classification: {(validation.shortlist_lock.blank_assets || []).join(', ')}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[12px] text-slate-500 inline-flex items-center gap-1">
                      <Unlock className="w-3.5 h-3.5" /> Shortlist not locked — counts compute live and can shift on re-runs. Use "Finalize &amp; Lock" to freeze the deliverable.
                    </div>
                  )}
                </div>
              )}
              {validation.companies_missing_country?.with_assets > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="validation-missing-country">
                  <p className="text-[12px] font-medium text-amber-800 inline-flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {validation.companies_missing_country.with_assets} company(ies) with assets have NO country set
                  </p>
                  <p className="text-[11px] text-amber-700 mt-0.5 leading-snug">
                    Their assets are invisible to the China universe until you set a country. Open each company profile → set Country. ({validation.companies_missing_country.count} companies total have no country.)
                  </p>
                  {validation.companies_missing_country.samples?.length > 0 && (
                    <div className="text-[11px] text-amber-800 mt-1 max-h-24 overflow-y-auto">
                      {validation.companies_missing_country.samples.join(' · ')}
                    </div>
                  )}
                </div>
              )}
              {validation.duplicate_groups?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Duplicate molecule groups ({validation.duplicate_groups.length}) · auto-merged in exports — informational</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {validation.duplicate_groups.map((g, i) => (
                      <div key={i} className="text-[12px] rounded bg-slate-50 border border-slate-100 px-2 py-1">
                        <span className="font-medium break-words">{g.molecule}</span> <span className="text-slate-400">· {g.company}</span> <span className="text-amber-600">×{g.count}</span>
                        <div className="text-[10px] text-slate-500 break-words">{g.names.join(' · ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {validation.cross_company_collisions?.length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Same molecule, multiple companies — review ({validation.cross_company_collisions.length})</p>
                  <div className="max-h-28 overflow-y-auto text-[12px] space-y-0.5">
                    {validation.cross_company_collisions.map((c, i) => (
                      <div key={i} className="text-slate-600">{c.companies.join(' ↔ ')}</div>
                    ))}
                  </div>
                </div>
              )}
              {validation.orphaned_assets?.length > 0 && (
                <div className="text-[12px] text-red-600">Orphaned (no company): {validation.orphaned_assets.join(', ')}</div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidationOpen(false)} data-testid="validation-close">Close</Button>
            <Button variant="outline" onClick={runValidation} disabled={validating} data-testid="validation-rerun">
              {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Re-run Check
            </Button>
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/viatris/export-partneringone?scope=recommended&country=China`, { withCredentials: true, responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url; a.download = `ForesightRx_Viatris_China_RecommendedAssets_${new Date().toISOString().slice(0,10)}.xlsx`;
                  document.body.appendChild(a); a.click(); a.remove();
                  toast.success('Validated Recommended export downloaded');
                } catch (e) { toast.error('Export failed'); }
              }}
              disabled={!validation || validating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="validation-export-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" /> Export Client Recommendations
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 v11 · Rule Validation Debug panel removed from the
          workflow view per Amit's feedback ("we don't need this anymore
          because I'm using Perplexity to enrich all TAs"). The endpoints
          `/api/viatris/rule-audit` and `/rule-check` remain live for
          curl/console access when needed. */}
    </div>
  );
}


// ─── PipelineActivityLog ───────────────────────────────────────────────
//
// Renders the most recent screening/pipeline audit events under the
// Status Pipeline. Feeds from GET /api/viatris/activity-log which
// returns rows written by _run_reapply_bg and sync_china_universe.
//
// Renders as a compact strip — nothing to click, just a "yes it
// happened, here's the receipt" panel. This alone resolves ~half the
// user's Feb 2026 v3 complaint that "Refresh Universe appears to run
// but there is no visible indication of what it actually did".
function PipelineActivityLog({ items = [], loading = false, onReload }) {
  // Feb 2026 v7 · Collapsed by default (spec §11). Renders as a compact
  // strip with a "Show activity" toggle — keeps the interface clean for
  // end-users while preserving developer traceability on demand.
  const [expanded, setExpanded] = useState(false);
  if (!items.length && !loading) return null;
  const visible = expanded ? items.slice(0, 15) : [];

  const _describe = (row) => {
    const st = row.stats || {};
    if (row.action === 'sync_universe') {
      return {
        title: 'Universe refreshed',
        detail: (st.added || 0) > 0
          ? `${st.added} added · ${st.already_in_scope || 0} already in scope`
          : `${st.already_in_scope || st.candidates || 0} already in scope`,
        badge: 'DB',
      };
    }
    if (row.action === 'reapply') {
      const parts = [];
      if (st.scanned) parts.push(`${st.scanned} evaluated`);
      if (st.excluded_new) parts.push(`${st.excluded_new} newly excluded`);
      if (st.restored) parts.push(`${st.restored} restored`);
      if (st.phase_excluded !== undefined || st.ta_excluded !== undefined || st.rule_excluded !== undefined) {
        parts.push(`(${st.phase_excluded || 0} phase · ${st.ta_excluded || 0} TA · ${st.rule_excluded || 0} rule)`);
      }
      return {
        title: 'Screening rules applied',
        detail: parts.join(' · ') || 'Completed',
        badge: 'DB',
      };
    }
    if (row.action === 'save_definition') {
      return { title: 'Screening definition saved', detail: '', badge: 'DB' };
    }
    return { title: row.action || 'Activity', detail: JSON.stringify(st), badge: 'DB' };
  };

  const _fmtTime = (ts) => {
    if (!ts) return '';
    try {
      const t = new Date(ts).getTime();
      const diff = Date.now() - t;
      const mins = Math.floor(diff / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      return new Date(ts).toLocaleDateString();
    } catch { return ts; }
  };

  return (
    <div
      className="mt-3 border border-slate-200 rounded-lg bg-slate-50/60 p-3"
      data-testid="pipeline-activity-log"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 inline-flex items-center gap-1.5">
          <Activity className="w-3 h-3" /> Pipeline Activity
        </div>
        <div className="flex items-center gap-2">
          {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
          {onReload && !loading && (
            <button
              type="button"
              className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
              onClick={onReload}
              data-testid="pipeline-activity-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          )}
          {items.length > 0 && (
            <button
              type="button"
              className="text-[10px] text-slate-500 hover:text-slate-700"
              onClick={() => setExpanded(v => !v)}
              data-testid="pipeline-activity-toggle"
            >
              {expanded ? 'Hide' : `Show activity (${items.length})`}
            </button>
          )}
        </div>
      </div>
      <div className="space-y-1.5" data-testid="pipeline-activity-items">
        {visible.map((row, i) => {
          const d = _describe(row);
          return (
            <div
              key={i}
              className="flex items-start gap-2 text-[11px]"
              data-testid={`pipeline-activity-item-${row.action}-${i}`}
            >
              <span className="text-slate-400 tabular-nums whitespace-nowrap w-16 pt-0.5">
                {_fmtTime(row.at)}
              </span>
              <span
                className={`shrink-0 text-[9px] font-bold rounded px-1 py-0 border ${
                  d.badge === 'AI'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}
              >
                {d.badge}
              </span>
              <span className="text-slate-700 font-medium">{d.title}</span>
              <span className="text-slate-500 truncate">{d.detail}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── PipelineProgressModal ─────────────────────────────────────────────
//
// Live checklist that renders while runRefreshScreeningPipeline() ticks
// through its four stages. Never toast-only — the user asked
// specifically for a persistent, non-disappearing progress surface.
function PipelineStageIcon({ status }) {
  if (status === 'done')    return <Check className="w-4 h-4 text-emerald-600" />;
  if (status === 'running') return <Loader2 className="w-4 h-4 animate-spin text-sky-600" />;
  if (status === 'failed')  return <AlertCircle className="w-4 h-4 text-rose-600" />;
  return <Circle className="w-4 h-4 text-slate-300" />;
}

function PipelineProgressModal({ state, onClose }) {
  if (!state?.open) return null;
  const { stages = [], running, error, diff } = state;
  const done = stages.length > 0 && stages.every(s => s.status === 'done');
  const failed = stages.some(s => s.status === 'failed');

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !running) onClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="pipeline-progress-modal"
        onInteractOutside={(e) => { if (running) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (running) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {running
              ? <><Loader2 className="w-4 h-4 animate-spin text-sky-600" /> Refreshing Screening Pipeline</>
              : done
                ? <><Check className="w-4 h-4 text-emerald-600" /> Pipeline refreshed successfully</>
                : failed
                  ? <><AlertCircle className="w-4 h-4 text-rose-600" /> Refresh interrupted</>
                  : <>Pipeline refresh</>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          {stages.map((s) => (
            <div
              key={s.key}
              className="flex items-start gap-3"
              data-testid={`pipeline-stage-${s.key}-${s.status}`}
            >
              <div className="pt-0.5"><PipelineStageIcon status={s.status} /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`text-sm font-medium ${
                    s.status === 'done' ? 'text-slate-800'
                    : s.status === 'running' ? 'text-sky-800'
                    : s.status === 'failed' ? 'text-rose-800'
                    : 'text-slate-400'
                  }`}>{s.label}</div>
                  {s.badge && (
                    <span className={`text-[9px] font-bold rounded px-1 py-0 border ${
                      s.badge === 'AI'
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>{s.badge}</span>
                  )}
                </div>
                {s.detail && (
                  <div className="text-[11px] text-slate-500 mt-0.5">{s.detail}</div>
                )}
              </div>
            </div>
          ))}
          {diff && diff.added > 0 && (
            <div className="mt-3 p-2 rounded bg-sky-50 border border-sky-200 text-[11px] text-sky-800">
              <strong>{diff.added}</strong> new asset{diff.added === 1 ? '' : 's'} pulled into the universe.
            </div>
          )}
          {error && (
            <div className="mt-3 p-2 rounded bg-rose-50 border border-rose-200 text-[11px] text-rose-800">
              {error}
            </div>
          )}
        </div>
        <div className="flex justify-end mt-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={running}
            data-testid="pipeline-progress-close"
          >
            {running ? 'Running…' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// COUNTER_TO_STATUS retained for legacy compatibility with handleCounterClick.
const COUNTER_TO_STATUS = {
  inventory: 'Inventory',
  ai_enriched: 'AI Enrichment',
  scored: 'Scoring',
  analyst_reviewed: 'Analyst Review',
  committee_ready: 'Committee Ready',
  go_candidate: 'Go Candidate',
  deep_dive: 'Deep Dive',
  recommended: 'Recommended',
};

// Tailwind-safe color classes — declared statically so JIT picks them up.
const COLOR_CLASSES = {
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-200',   text: 'text-slate-700',   active: 'ring-2 ring-slate-400 bg-slate-100' },
  sky:     { bg: 'bg-sky-50',     border: 'border-sky-200',     text: 'text-sky-700',     active: 'ring-2 ring-sky-400 bg-sky-100' },
  violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  text: 'text-violet-700',  active: 'ring-2 ring-violet-400 bg-violet-100' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  active: 'ring-2 ring-indigo-400 bg-indigo-100' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700',    active: 'ring-2 ring-blue-400 bg-blue-100' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', active: 'ring-2 ring-emerald-400 bg-emerald-100' },
  green:   { bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700',   active: 'ring-2 ring-green-400 bg-green-100' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   active: 'ring-2 ring-amber-400 bg-amber-100' },
  rose:    { bg: 'bg-rose-50',    border: 'border-rose-200',    text: 'text-rose-700',    active: 'ring-2 ring-rose-400 bg-rose-100' },
};

// Unified label vocabulary — LRI bands === Recommendation values === KPI tiles.
// Strategic Priority MERGED into High Priority (Feb 2026 — one tier removed
// for operator clarity). Final ladder: High Priority (75+) -> Medium (60-74)
// -> Monitor (40-59) -> Low Priority (<40). "Not Suitable" is a kill-switch
// override on Recommendation only.
const RECOMMENDATION_COLORS = {
  'High Priority':      'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Medium':             'bg-sky-100 text-sky-800 border-sky-200',
  'Monitor':            'bg-amber-100 text-amber-800 border-amber-300',
  'Low Priority':       'bg-orange-100 text-orange-800 border-orange-300',
  'Not Suitable':       'bg-rose-100 text-rose-900 border-rose-400',
  // Legacy values still rendered for historical assessments (will be
  // recomputed under the new schema on next assessment).
  'Strategic Priority': 'bg-emerald-200 text-emerald-900 border-emerald-300',
  'Medium Priority':    'bg-sky-100 text-sky-800 border-sky-200',
  'Deep Dive':          'bg-amber-100 text-amber-800 border-amber-200',
};

const STAGE_COLORS = {
  'Inventory':            'bg-slate-100 text-slate-600',
  'Selected for Cohort':  'bg-sky-100 text-sky-700',
  'AI Enrichment':        'bg-violet-100 text-violet-700',
  'Medical Assessment':   'bg-indigo-100 text-indigo-700',
  'Scoring':              'bg-blue-100 text-blue-700',
  'Analyst Review':       'bg-cyan-100 text-cyan-800',
  'Committee Ready':      'bg-emerald-100 text-emerald-800',
  'Go Candidate':         'bg-green-100 text-green-800',
  'Deep Dive':            'bg-violet-100 text-violet-800',
  'Recommended':          'bg-rose-100 text-rose-800',
  'Archived':             'bg-slate-200 text-slate-500',
};

export default function ViatrisWorkbenchPage({ engagementId = 'eng-viatris-china', clientLabel = 'Viatris' } = {}) {
  // Feb 2026 · engagement_id is now a prop so this same page can render
  // for multiple clients (Viatris, Modulus, …). Legacy hardcoded refs
  // to 'eng-viatris-china' below use the ENG constant so both routes
  // (/clients/viatris + /clients/modulus) share one implementation.
  const ENG = engagementId;
  const CLIENT_LABEL = clientLabel;
  // Modulus × Animal Health scans the ENTIRE global asset universe (no
  // country filter). Viatris is China-scoped. Any future single-country
  // engagement adds a case here.
  const COUNTRY = clientLabel === 'Modulus' ? 'Global' : 'China';
  const UNIVERSE_LABEL = clientLabel === 'Modulus' ? 'Global Universe' : 'China Universe';
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'inventory'; // inventory | cohort | reports
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [shortlistAudit, setShortlistAudit] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    assessment_status: searchParams.get('status') || '',
    modality: '',
    ai_assessment_status: '',
    committee_status: '',
    recommendation: searchParams.get('rec') || '',
    strategic_alignment: '',
    rare_disease: '',  // '', 'true', 'false'
    licensing_status: '',
    cohort: searchParams.get('cohort') || '',
    reporting_cycle: '',
    stage: '',
    therapeutic_area: '',
    last_updated_days: 0,
    scored_only: '',  // '1' to filter to assets with a composite_score
    // Feb 2026 — Viatris kickoff scope (CV/Metab/CNS/Immuno/Derm/Pain × Phase 3+).
    // Hardcoded ON. The Sep 2026 UX swap replaced the toggle pair with a
    // static "Viatris scope" badge in the filter bar; the badge implies
    // the scope is always applied, so the state must default to '1' or
    // every counter (Conversion Funnel, Committee Status, Recommendation
    // Distribution) silently reverts to the engagement-wide universe.
    ta_priority_only: '1',  // '1' = restrict to CV/Metab/CNS/Immuno/Derm/Pain
    phase_3_plus: '1',      // '1' = Phase 3 / 3+ / Approved / Marketed
  });
  const [revealCompanies] = useState(true); // Operator workbench always shows company names; Viatris portal stays anonymised.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);
  // Post-approval innovator availability-inquiry dialog (Jul 2026)
  const [availDialog, setAvailDialog] = useState({ open: false, loading: false, sending: false, items: [], results: null });
  const [showCohortDialog, setShowCohortDialog] = useState(false);
  const [cohortForm, setCohortForm] = useState({ name: '', reporting_cycle: '', notes: '' });
  const [creatingCohort, setCreatingCohort] = useState(false);
  const [cohortList, setCohortList] = useState([]);
  const [showSuggestionsDialog, setShowSuggestionsDialog] = useState(false);
  const [showCommitteeProgressModal, setShowCommitteeProgressModal] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // ─── Unified "Refresh Screening Pipeline" orchestrator (Feb 2026 v3) ─
  // Owned by ViatrisWorkbenchPage because the state has to be visible
  // to both the top-level StatusPipeline (which disables buttons while
  // running) AND the PipelineProgressModal (which shows the checklist).
  const [pipelineWorkflow, setPipelineWorkflow] = useState({
    open: false,
    running: false,
    // stages[i] = { key, label, status: 'pending'|'running'|'done'|'failed', detail, badge }
    stages: [],
    error: '',
    diff: null,
  });
  const [pipelineActivity, setPipelineActivity] = useState([]);
  const [pipelineActivityLoading, setPipelineActivityLoading] = useState(false);

  // Aug 2026 v3 · Journey filter — clicking any Phase 1 / Phase 2 count in
  // the ViatrisJourneyPanel filters the asset table to the EXACT underlying
  // asset list (explicit ids, never an inferred filter).
  const [journeyFilter, setJourneyFilter] = useState(null);

  // Aug 2026 · Responded filter — segregates companies that replied to
  // outreach. Independent of tier/committee filters; persisted to ?responded=1.
  const [respondedOnly, setRespondedOnly] = useState(searchParams.get('responded') === '1');

  // Aug 2026 · duplicate-row health check (QP-6211 incident).
  const [dupGroups, setDupGroups] = useState(0);
  useEffect(() => {
    axios.get(`${API}/viatris/duplicates?count_only=true`, { withCredentials: true })
      .then(r => setDupGroups(r.data.groups || 0)).catch(() => {});
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      // Pass the same scope filters the inventory uses so the
      // Conversion Funnel + Committee Status chips + Recommendation
      // Distribution tiles all react to the filter row at the bottom.
      const params = {};
      if (filters.cohort) params.cohort = filters.cohort;
      if (filters.ta_priority_only) params.ta_priority_only = filters.ta_priority_only;
      if (filters.phase_3_plus) params.phase_3_plus = filters.phase_3_plus;
      const r = await axios.get(`${API}/viatris/summary`, {
        params, withCredentials: true,
      });
      setSummary(r.data);
      setCohortList(r.data.cohorts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Viatris summary');
    }
  }, [filters.cohort, filters.ta_priority_only, filters.phase_3_plus]);

  // Jul 2026 · China-Universe view. The inventory now loads the FULL
  // deduped universe-eligible asset list ONCE (universe_eligible_only=1),
  // and recommendation / committee-status filtering happens CLIENT-SIDE
  // over that array — instant, no refetch, and tile counts can never
  // disagree with the table (both derive from the same rows).
  const CLIENT_SIDE_FILTER_KEYS = ['recommendation', 'committee_status', 'cohort', 'ta_priority_only', 'phase_3_plus'];
  const serverFilterKey = JSON.stringify(
    Object.fromEntries(Object.entries(filters).filter(([k]) => !CLIENT_SIDE_FILTER_KEYS.includes(k)))
  );
  const loadInventory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 500, offset: 0, reveal_company: revealCompanies,
        universe_eligible_only: '1',
      };
      Object.entries(JSON.parse(serverFilterKey)).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined && v !== 0) {
          params[k] = v;
        }
      });
      const r = await axios.get(`${API}/viatris/inventory`, {
        params, withCredentials: true,
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
      setShortlistAudit(r.data.shortlist_audit || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  }, [serverFilterKey, revealCompanies]);

  // Single source of truth: tiles + table + row counter all derive from
  // the same `rows` array, so every number reconciles by construction.
  const COMMITTEE_TIER_SET = ['Strategic Priority', 'High Priority', 'Medium', 'Medium Priority'];
  const displayRows = useMemo(() => {
    let out = rows;
    if (journeyFilter) {
      // Explicit id membership — the badge count always equals the rows shown.
      const idSet = new Set(journeyFilter.ids || []);
      const aidSet = new Set(journeyFilter.assetIds || []);
      out = rows.filter((r) => idSet.has(r.id) || (r.asset_id && aidSet.has(r.asset_id)));
      return respondedOnly ? out.filter((r) => !!r.responded) : out;
    }
    const rec = filters.recommendation;
    if (rec) {
      if (rec === '__committee__') {
        // Stage 2 (Committee Review) filter — the FULL set selected for
        // committee review (Strategic + High + Medium), including already-
        // decided assets, matching the client-shared spec (67 = 15 HP + 52 MP).
        out = out.filter((r) => COMMITTEE_TIER_SET.includes(r.recommendation));
      } else if (rec === 'Medium' || rec === 'Medium Priority') {
        out = out.filter((r) => r.recommendation === 'Medium' || r.recommendation === 'Medium Priority');
      } else if (rec === '__awaiting_ai__') {
        out = out.filter((r) => !r.recommendation);
      } else {
        out = out.filter((r) => r.recommendation === rec);
      }
    }
    const cs = filters.committee_status;
    if (cs) {
      const inTier = (r) => COMMITTEE_TIER_SET.includes(r.recommendation);
      // Normalize stored status (trim) so the table filter uses the SAME
      // predicate as the tile counters in aiOverrides — a stray space in
      // committee_status must never make the table drop a counted row.
      const rowCs = (r) => (r.committee_status || '').trim();
      if (cs === 'Pending') {
        out = out.filter((r) => inTier(r) && (!rowCs(r) || rowCs(r) === 'Pending'));
      } else if (cs === 'Under Review') {
        out = out.filter((r) => inTier(r) && rowCs(r) === 'Under Review');
      } else if (cs === 'Scored') {
        out = out.filter((r) => inTier(r) && (rowCs(r) === 'Approved' || rowCs(r) === 'Rejected'));
      } else {
        out = out.filter((r) => rowCs(r) === cs);
      }
    }
    if (respondedOnly) out = out.filter((r) => !!r.responded);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filters.recommendation, filters.committee_status, journeyFilter, respondedOnly]);

  // A recommendation / committee-status filter click supersedes any active
  // journey filter (the two are exclusive so counts always reconcile).
  useEffect(() => {
    if (filters.recommendation || filters.committee_status) setJourneyFilter(null);
  }, [filters.recommendation, filters.committee_status]);

  const rdCounts = useMemo(() => {
    const c = {
      all: rows.length, strategic_priority: 0, high_priority: 0, medium: 0,
      monitor: 0, low_priority: 0, not_suitable: 0, could_not_assess: 0,
      awaiting_ai: 0, responded: 0,
    };
    rows.forEach((r) => {
      if (r.responded) c.responded += 1;
      const rec = r.recommendation || '';
      if (rec === 'Strategic Priority') c.strategic_priority += 1;
      else if (rec === 'High Priority') c.high_priority += 1;
      else if (rec === 'Medium' || rec === 'Medium Priority') c.medium += 1;
      else if (rec === 'Monitor') c.monitor += 1;
      else if (rec === 'Low Priority') c.low_priority += 1;
      else if (rec === 'Not Suitable') c.not_suitable += 1;
      else if (rec === 'Could Not Assess') c.could_not_assess += 1;
      else c.awaiting_ai += 1;
    });
    return c;
  }, [rows]);

  // Jul 2026 · ONE SOURCE OF TRUTH for the funnel's AI-section cards.
  // AI Assessed / Committee Review / Finalised and the committee-status
  // strip are all derived from the SAME rows as the table + tiles, so:
  //   Eligible = AI Assessed + Awaiting AI
  //   Committee Review = Strategic + High + Medium
  //   Pending + Under Review + Scored = Committee Review
  // …always hold by construction. Null until rows load (funnel then
  // falls back to server numbers).
  const aiOverrides = useMemo(() => {
    if (!rows.length) return null;
    const tiers = ['Strategic Priority', 'High Priority', 'Medium', 'Medium Priority'];
    let pending = 0, underReview = 0, scored = 0, shortlisted = 0;
    let availAvailable = 0, availNotAvailable = 0, availAwaiting = 0;
    rows.forEach((r) => {
      const cs = (r.committee_status || '').trim();
      // Client Shortlisted = every Approved row, regardless of tier — the
      // tile count MUST equal the filtered table row count exactly.
      if (cs === 'Approved') {
        shortlisted += 1;
        const av = r.availability_status || 'Not Contacted';
        if (av === 'Available for China') availAvailable += 1;
        else if (av === 'Not Available') availNotAvailable += 1;
        else if (av === 'Email Sent' || av === 'Awaiting Response') availAwaiting += 1;
      }
      if (!tiers.includes(r.recommendation)) return;
      if (cs === 'Under Review') underReview += 1;
      else if (cs === 'Approved' || cs === 'Rejected') scored += 1;
      else pending += 1;
    });
    return {
      eligible: rows.length,
      ai_scored: rows.length - rdCounts.awaiting_ai,
      could_not_assess: rdCounts.could_not_assess,
      committee_review: rdCounts.strategic_priority + rdCounts.high_priority + rdCounts.medium,
      finalised: shortlisted,
      availability_breakdown: {
        available: availAvailable,
        not_available: availNotAvailable,
        awaiting: availAwaiting,
        outside_universe: shortlistAudit?.outside_universe || 0,
        total_marked: shortlistAudit?.total_marked || 0,
      },
      committee_breakdown: [
        { tier: 'Strategic Priority', count: rdCounts.strategic_priority },
        { tier: 'High Priority', count: rdCounts.high_priority },
        { tier: 'Medium Priority', count: rdCounts.medium },
      ],
      committee_status_breakdown: [
        { status: 'Pending', count: pending },
        { status: 'Under Review', count: underReview },
        { status: 'Completed', count: scored },
      ],
    };
  }, [rows, rdCounts, shortlistAudit]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadInventory(); }, [loadInventory]);

  // ─── Pipeline activity log + unified refresh orchestrator ────────────
  // Placed here (AFTER `loadSummary` / `loadInventory` are declared)
  // so the orchestrator can reference them without a temporal dead zone
  // error at render time.
  const loadPipelineActivity = useCallback(async () => {
    setPipelineActivityLoading(true);
    try {
      const r = await axios.get(`${API}/viatris/activity-log?limit=15`, { withCredentials: true });
      setPipelineActivity(r.data?.items || []);
    } catch { /* non-blocking */ }
    finally { setPipelineActivityLoading(false); }
  }, []);

  useEffect(() => { loadPipelineActivity(); }, [loadPipelineActivity]);

  // Feb 2026 · Master-asset sync — refreshes engagement_asset mirror
  // fields (asset name, company, phase, TA, modality, indication) from
  // the current `assets` + `biotech_companies` master records. Fixes
  // stale snapshots across Committee Review / Reviewer / Client Portal
  // without touching any scoring or committee decisions.
  const runSyncFromMaster = useCallback(async () => {
    try {
      toast.info('Syncing asset names from master…', { duration: 4000 });
      const r = await axios.post(
        `${API}/viatris/sync-asset-names-from-master?names_only=true`,
        {}, { withCredentials: true, timeout: 120_000 },
      );
      const d = r.data || {};
      const renamed = d.updated_from_asset || 0;
      const unchanged = d.unchanged || 0;
      const scanned = d.scanned || 0;
      const missing = d.skipped_missing_asset || 0;
      const samples = d.sample_changes || [];

      if (renamed === 0) {
        toast.success(
          `Scanned ${scanned} rows · every name already matches the master. No changes needed.`,
          { duration: 8000 },
        );
      } else {
        // Preserve up to 25 samples for the diff modal so the user
        // sees WHICH names changed instead of a black-box counter.
        setSyncResultModal({
          renamed, unchanged, scanned, missing,
          samples,
        });
        toast.success(
          `Renamed ${renamed} asset(s) · ${unchanged} unchanged · ${missing} skipped (master missing). See the modal for details.`,
          { duration: 10000 },
        );
      }
      await Promise.all([loadSummary(), loadInventory()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Sync from master failed');
    }
  }, []);

  // Feb 2026 · Fix mis-shortlisted — finds every engagement_asset row
  // where committee_status='Approved' but recommendation is NOT in
  // {Strategic, High, Medium} (i.e. Monitor / Low / Not Suitable /
  // Could Not Assess) and un-shortlists it. Restores Stage 3 to the
  // stated business rule: only actively-tiered assets can be Client
  // Shortlisted.
  const runFixMisShortlisted = useCallback(async () => {
    try {
      // 1. Preview which rows would be un-shortlisted.
      const inv = await axios.get(
        `${API}/viatris/inventory?engagement_id=${ENG}&committee_status=Approved&limit=1000`,
        { withCredentials: true },
      );
      const rows = inv.data?.rows || inv.data?.items || [];
      const _ACTIVE = new Set(['Strategic Priority', 'High Priority', 'Medium', 'Medium Priority']);
      const bad = rows.filter(r => !_ACTIVE.has(r.recommendation || ''));
      if (bad.length === 0) {
        toast.success('No mis-shortlisted assets found — every Approved row is Strategic / High / Medium.');
        return;
      }
      const proceed = window.confirm(
        `Found ${bad.length} Approved asset(s) whose recommendation is NOT Strategic / High / Medium:\n\n` +
        bad.slice(0, 10).map(r => `• ${r.asset_name} — ${r.recommendation || 'Not scored'}`).join('\n') +
        (bad.length > 10 ? `\n… and ${bad.length - 10} more` : '') +
        `\n\nUn-shortlist all of them (revert committee_status to null)? Their scores & everything else stay intact.`,
      );
      if (!proceed) return;
      // 2. Bulk-clear committee_status on the bad rows.
      const eaIds = bad.map(r => r.id);
      const res = await axios.post(
        `${API}/viatris/assets/bulk-committee-decision`,
        { ea_ids: eaIds, decision: 'Pending', comment: 'Auto-unshortlisted: Monitor/Low/Not-Suitable/CNA cannot be Stage 3.' },
        { withCredentials: true },
      );
      toast.success(`Un-shortlisted ${res.data?.updated || 0} asset(s). Refresh the page to see the updated Stage 3 count.`);
      await Promise.all([loadSummary(), loadInventory()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Fix mis-shortlisted failed');
    }
  }, []);


  const runRefreshScreeningPipeline = useCallback(async () => {
    if (pipelineWorkflow.running) return;
    const cohort = filters.cohort || '';
    const seedStages = [
      { key: 'universe',   label: 'Rebuild China Universe (latest data)', status: 'pending', detail: '', badge: 'DB' },
      { key: 'screening',  label: 'Apply Screening Rules',    status: 'pending', detail: '', badge: 'DB' },
      { key: 'eligible',   label: 'Sync Eligible Cohort → Awaiting AI', status: 'pending', detail: '', badge: 'DB' },
      { key: 'committee',  label: 'Refresh Counters',         status: 'pending', detail: '', badge: 'DB' },
    ];
    setPipelineWorkflow({ open: true, running: true, stages: seedStages, error: '', diff: null });
    const patchStage = (key, patch) => setPipelineWorkflow(prev => ({
      ...prev,
      stages: prev.stages.map(s => s.key === key ? { ...s, ...patch } : s),
    }));

    // Step 0 (silent) — auto-manage maintenance lock. If the universe is
    // locked and NOT already in maintenance, silently start maintenance
    // so the pipeline can re-classify rows + insert new eligible ones.
    // Maintenance stays ON after the pipeline finishes — Run AI
    // Assessment depends on it. User completes it later via the
    // "Maintenance Mode" badge in the toolbar.
    try {
      const lockRes = await axios.get(`${API}/viatris/universe/lock-status`, { withCredentials: true });
      if (lockRes.data?.locked && !lockRes.data?.maintenance) {
        await axios.post(`${API}/viatris/universe/maintenance/start`, {}, { withCredentials: true });
        toast.info('Maintenance Mode auto-enabled — Refresh Screening can now pull new assets.', { duration: 6000 });
      }
    } catch {
      /* Non-blocking. If lock status unavailable, fall through and let
         the individual steps 423 naturally with a clear error. */
    }

    // Step 1 — Refresh Universe
    patchStage('universe', { status: 'running' });
    let universeDiff = null;
    try {
      const r = await axios.post(`${API}/viatris/sync-china-universe`, {}, { withCredentials: true, timeout: 180_000 });
      universeDiff = r.data;
      const bits = [];
      if (r.data?.added > 0) bits.push(`${r.data.added} new asset${r.data.added === 1 ? '' : 's'} added`);
      bits.push(`${r.data?.already_in_scope || r.data?.candidates || 0} already in scope`);
      if (r.data?.fields_resynced > 0) bits.push(`${r.data.fields_resynced} rows re-synced with latest data`);
      if (r.data?.out_of_universe > 0) bits.push(`${r.data.out_of_universe} no longer qualify`);
      patchStage('universe', { status: 'done', detail: bits.join(' · ') });
    } catch (e) {
      patchStage('universe', { status: 'failed', detail: e?.response?.data?.detail || e?.message || 'Universe sync failed' });
      setPipelineWorkflow(prev => ({ ...prev, running: false, error: 'Universe refresh failed' }));
      toast.error('Refresh Universe failed'); return;
    }
    setPipelineWorkflow(prev => ({ ...prev, diff: universeDiff }));

    // Step 2 — Apply Screening Rules (poll to done)
    patchStage('screening', { status: 'running' });
    let reapplyResult = null;
    try {
      const start = await axios.post(
        `${API}/viatris/screening-definition/reapply?cohort=${encodeURIComponent(cohort)}`,
        {}, { withCredentials: true },
      );
      const taskId = start.data?.task_id;
      if (!taskId) throw new Error('No task_id returned');
      const startedMs = Date.now();
      for (;;) {
        await new Promise(r => setTimeout(r, 1000));
        const s = await axios.get(`${API}/viatris/screening-refresh/${taskId}`, { withCredentials: true });
        if (s.data?.status === 'done') { reapplyResult = s.data; break; }
        if (s.data?.status === 'error') {
          // Feb 2026 v15b — bubble up the exact failing stage + backend
          // error message. Prior to this, the UI showed a generic
          // "Screening reapply failed" with no clue what actually broke.
          const stage = s.data?.error_stage || s.data?.stage || 'unknown_stage';
          const msg = s.data?.error || 'Screening refresh failed';
          throw new Error(`[${stage}] ${msg}`);
        }
        if (typeof s.data?.scanned === 'number' && typeof s.data?.total === 'number' && s.data.total > 0) {
          const stageLabel = s.data?.stage && s.data.stage !== 'scan_engagement_assets' ? ` (${s.data.stage})` : '';
          patchStage('screening', { status: 'running', detail: `Scanning ${s.data.scanned} / ${s.data.total}${stageLabel} …` });
        }
        // Feb 2026 v15b — bumped from 2m → 10m. Large cohorts (4400+
        // assets) can legitimately need several minutes; the previous
        // limit killed valid refreshes before they finished.
        if (Date.now() - startedMs > 600_000) throw new Error('Screening refresh timed out after 10 minutes');
      }
      patchStage('screening', {
        status: 'done',
        detail: `${reapplyResult?.scanned || 0} evaluated · ${reapplyResult?.excluded_new || 0} newly excluded · ${reapplyResult?.restored || 0} restored`,
      });
    } catch (e) {
      patchStage('screening', { status: 'failed', detail: e?.response?.data?.detail || e?.message || 'Screening reapply failed' });
      setPipelineWorkflow(prev => ({ ...prev, running: false, error: 'Screening reapply failed' }));
      toast.error('Apply Screening Rules failed'); return;
    }

    // Step 3 — Sync Eligible Cohort (Feb 2026 · Maintenance workflow)
    // Moves newly-eligible, never-assessed universe assets INTO the
    // active cohort as `Awaiting AI`. Fixes the "manually added
    // assets don't show up after Refresh Screening" bug. Never
    // touches existing rows; never re-queues previously-assessed ones.
    patchStage('eligible', { status: 'running' });
    let syncSummary = null;
    try {
      if (cohort) {
        const r = await axios.post(
          `${API}/viatris/sync-eligible-cohort?cohort=${encodeURIComponent(cohort)}`,
          {}, { withCredentials: true, timeout: 120_000 },
        );
        syncSummary = r.data;
        const bits = [];
        bits.push(`${r.data?.added_to_cohort || 0} new asset${(r.data?.added_to_cohort || 0) === 1 ? '' : 's'} → Awaiting AI`);
        if (r.data?.previously_assessed_skipped > 0) bits.push(`${r.data.previously_assessed_skipped} previously-assessed skipped`);
        bits.push(`${r.data?.already_in_cohort || 0} already in cohort`);
        patchStage('eligible', { status: 'done', detail: bits.join(' · ') });
      } else {
        // No cohort selected — fall back to read-only stats.
        const s = await axios.get(`${API}/viatris/pipeline-stats`, { withCredentials: true });
        patchStage('eligible', {
          status: 'done',
          detail: `${s.data?.eligible || 0} eligible · ${s.data?.excluded || 0} excluded (no cohort selected — nothing moved)`,
        });
      }
    } catch (e) {
      patchStage('eligible', {
        status: 'failed',
        detail: e?.response?.data?.detail || e?.message || 'Sync eligible cohort failed',
      });
    }

    // Step 4 — Reload summary counters
    patchStage('committee', { status: 'running' });
    try {
      await loadSummary();
      patchStage('committee', { status: 'done', detail: 'Committee queue rebuilt · counters refreshed' });
    } catch { patchStage('committee', { status: 'failed', detail: 'Counter reload failed' }); }

    loadPipelineActivity();
    setPipelineWorkflow(prev => ({ ...prev, running: false }));
    toast.success('Screening pipeline refreshed');
  }, [pipelineWorkflow.running, filters.cohort, loadSummary, loadPipelineActivity]);

  useEffect(() => {
    const handler = (ev) => {
      if (ev?.detail?.action === 'refresh_pipeline') runRefreshScreeningPipeline();
    };
    window.addEventListener('viatris:pipeline-action', handler);
    return () => window.removeEventListener('viatris:pipeline-action', handler);
  }, [runRefreshScreeningPipeline]);

  // Feb 2026 · user request — "default status pipeline should show data
  // for cohort 1 only". When the workbench first mounts, if the URL
  // hasn't pre-selected a cohort, auto-scope to Cohort 1 so the pipeline
  // counts + inventory table + Recommendation Distribution all reflect
  // a single cohort (avoids the confusing "sum across all cohorts"
  // display). Match by name suffix `Cohort 1` first (case-insensitive),
  // fall back to the first cohort in the list if none matches.
  const cohortDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (cohortDefaultAppliedRef.current) return;
    if (!cohortList.length) return;
    if (filters.cohort) {
      cohortDefaultAppliedRef.current = true;  // URL already scoped
      return;
    }
    const preferred =
      cohortList.find((c) => /cohort\s*1\b/i.test(c.name || ''))
      || cohortList[0];
    if (!preferred?.name) return;
    cohortDefaultAppliedRef.current = true;
    setFilters((f) => ({ ...f, cohort: preferred.name }));
    const sp = new URLSearchParams(searchParams);
    sp.set('cohort', preferred.name);
    setSearchParams(sp, { replace: true });
  }, [cohortList]);

  const handleCounterClick = (key) => {
    const newStatus = COUNTER_TO_STATUS[key] || '';
    if (key === 'current_cohort') {
      // Active cohort = the most recent cohort in the list
      const latestCohort = (cohortList[0]?.name) || '';
      setFilters((f) => ({ ...f, assessment_status: '', cohort: latestCohort }));
      const sp = new URLSearchParams(searchParams);
      sp.set('cohort', latestCohort); sp.delete('status');
      setSearchParams(sp, { replace: true });
      return;
    }
    setFilters((f) => ({ ...f, assessment_status: newStatus, cohort: '' }));
    const sp = new URLSearchParams(searchParams);
    if (newStatus) sp.set('status', newStatus); else sp.delete('status');
    sp.delete('cohort');
    setSearchParams(sp, { replace: true });
  };

  const clearFilters = () => {
    setFilters({
      assessment_status: '', modality: '', ai_assessment_status: '',
      committee_status: '', recommendation: '', strategic_alignment: '',
      rare_disease: '', licensing_status: '', cohort: '', reporting_cycle: '',
      stage: '', therapeutic_area: '', last_updated_days: 0,
      // Viatris scope is locked ON — never cleared by "Clear filters".
      ta_priority_only: '1', phase_3_plus: '1',
    });
    setSearchParams({}, { replace: true });
  };

  const activeFilterCount = useMemo(
    // Viatris scope (ta_priority_only + phase_3_plus) is engagement-locked,
    // not a user-toggled filter, so it's excluded from the "Active filters"
    // count — otherwise the badge always reads >=2 even on a fresh load.
    () => Object.entries(filters).filter(
      ([k, v]) => v !== '' && v !== 0 && k !== 'ta_priority_only' && k !== 'phase_3_plus',
    ).length,
    [filters],
  );

  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedIds(new Set(displayRows.map((r) => r.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  // Aug 2026 · Team Approved bulk actions — shadcn Dialog confirm ('approve' | 'revoke' | null)
  const [teamApproveDialog, setTeamApproveDialog] = useState(null);
  const [teamApproveBusy, setTeamApproveBusy] = useState(false);
  const runTeamApprove = async (approve) => {
    setTeamApproveBusy(true);
    try {
      const r = await axios.post(`${API}/viatris/journey/team-approve`,
        { ids: Array.from(selectedIds), approve }, { withCredentials: true });
      const skipped = r.data.skipped_not_shortlisted || [];
      const auto = r.data.auto_shortlisted || [];
      if (approve && r.data.updated === 0 && skipped.length) {
        toast.error(`Nothing marked — ${skipped.join(', ')} can't be Team Approved: only Strategic / High / Medium priority assets can enter the funnel.`, { duration: 10000 });
      } else {
        toast.success(approve
          ? `${r.data.updated} asset(s) marked Team Approved${auto.length ? ` · ${auto.length} auto-shortlisted (Committee Approved)` : ''}${skipped.length ? ` · ${skipped.length} skipped (not an eligible tier)` : ''}`
          : `Team approval revoked on ${r.data.updated} asset(s)`);
      }
      setTeamApproveDialog(null);
      clearSelection(); loadInventory(); loadSummary();
      window.dispatchEvent(new Event('viatris:journey-reload'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Team approval update failed'); }
    finally { setTeamApproveBusy(false); }
  };

  // ── Bulk committee approval + innovator availability inquiry (Jul 2026) ──
  const bulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkApproving(true);
    try {
      const r = await axios.post(`${API}/viatris/assets/bulk-committee-decision`,
        { ea_ids: ids, decision: 'Approved' }, { withCredentials: true });
      toast.success(`${r.data.updated} asset(s) marked as Approved`);
      setRows(prev => prev.map(row => selectedIds.has(row.id) ? { ...row, committee_status: 'Approved' } : row));
      clearSelection();
      openAvailabilityDialog(ids);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk approval failed');
    } finally {
      setBulkApproving(false);
    }
  };

  // Per-row innovator availability status update (Jul 2026).
  const setAvailabilityStatus = async (eaId, status) => {
    try {
      await axios.post(`${API}/viatris/assets/${eaId}/availability-status`,
        { status }, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === eaId ? { ...r, availability_status: status } : r));
      toast.success(`Availability status → ${status}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update availability status');
    }
  };

  // Aug 2026 · Per-row Responded Yes/No + Rights / Response Note (manual).
  const setResponded = async (eaId, responded) => {
    try {
      await axios.post(`${API}/viatris/assets/${eaId}/responded`,
        { responded }, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === eaId ? { ...r, responded } : r));
      toast.success(`Responded → ${responded ? 'Yes' : 'No'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update responded status');
    }
  };
  const saveResponseNote = async (eaId, note) => {
    try {
      await axios.post(`${API}/viatris/assets/${eaId}/responded`,
        { responded: true, response_note: note }, { withCredentials: true });
      setRows(prev => prev.map(r => r.id === eaId ? { ...r, responded: true, response_note: note } : r));
      toast.success('Response note saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save response note');
    }
  };

  // Aug 2026 · Bulk "Mark as Responded" from the selection bar. Notes are
  // entered per-asset afterwards via the inline Rights / Response Note box.
  const [bulkResponding, setBulkResponding] = useState(false);
  const bulkMarkResponded = async () => {
    const ids = Array.from(selectedIds).filter(id => {
      const r = rows.find(rr => rr.id === id);
      return r && !r.responded;
    });
    if (!ids.length) return;
    setBulkResponding(true);
    try {
      const r = await axios.post(`${API}/viatris/assets/bulk-responded`,
        { ea_ids: ids, responded: true }, { withCredentials: true });
      setRows(prev => prev.map(row => ids.includes(row.id) ? { ...row, responded: true } : row));
      toast.success(`${r.data.updated} asset(s) → Responded: Yes — add each Rights / Response Note in the Responded column`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark as Responded');
    } finally {
      setBulkResponding(false);
    }
  };

  // Final Client Selection endpoint retained server-side; UI removed
  // Jul 2026 v3 (shortlisted assets now auto-enter the Reviewer queue).

  const openAvailabilityDialog = async (ids) => {
    setAvailDialog({ open: true, loading: true, sending: false, items: [], results: null });
    try {
      const r = await axios.get(`${API}/viatris/availability-inquiry/preview?ea_ids=${ids.join(',')}`, { withCredentials: true });
      setAvailDialog(prev => ({ ...prev, loading: false, items: r.data.items || [] }));
    } catch {
      setAvailDialog(prev => ({ ...prev, loading: false }));
      toast.error('Could not load innovator contacts');
    }
  };

  const sendAvailabilityInquiries = async () => {
    const sendable = availDialog.items.filter(i => i.contact_email);
    if (sendable.length === 0) { toast.error('No innovator contacts with an email on file'); return; }
    setAvailDialog(prev => ({ ...prev, sending: true }));
    try {
      const r = await axios.post(`${API}/viatris/availability-inquiry/send`, { items: sendable },
        { withCredentials: true, timeout: 600000 });
      setAvailDialog(prev => ({ ...prev, sending: false, results: r.data }));
      // Reflect the new "Email Sent" tracking status on the sent rows.
      const failedCompanies = new Set((r.data.failed || []).map(f => f.company_name));
      const sentIds = new Set(sendable.filter(i => !failedCompanies.has(i.company_name)).map(i => i.ea_id));
      if (sentIds.size) {
        setRows(prev => prev.map(row => sentIds.has(row.id) ? { ...row, availability_status: 'Email Sent' } : row));
      }
      if (r.data.rate_limited) {
        toast.warning(`Sent ${r.data.sent} before hitting Gmail's rate limit — wait ~15 min and resend for the rest.`, { duration: 10000 });
      } else {
        toast.success(`${r.data.sent} availability inquiry email(s) sent to innovators`);
      }
    } catch (e) {
      setAvailDialog(prev => ({ ...prev, sending: false }));
      toast.error(e?.response?.data?.detail || 'Sending failed');
    }
  };

  // Feb 2026 · user-selectable "next cohort size". Old flow hard-coded
  // 200. Now the picker (preset chips + custom input) lets the analyst
  // decide the top-N slice up front. Refetches on change.
  const [suggestionSize, setSuggestionSize] = useState(250);
  const suggestionSizeDebounce = useRef(null);
  const openCohortSuggestions = async (sizeOverride) => {
    setShowSuggestionsDialog(true);
    setLoadingSuggestions(true);
    const size = Number.isFinite(sizeOverride) ? sizeOverride : suggestionSize;
    try {
      const r = await axios.get(`${API}/viatris/cohort-suggestions`, {
        params: { size }, withCredentials: true,
      });
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const createCohort = async () => {
    if (!cohortForm.name.trim()) {
      toast.error('Cohort name is required');
      return;
    }
    setCreatingCohort(true);
    try {
      await axios.post(`${API}/viatris/cohorts`, {
        ...cohortForm,
        asset_ids: Array.from(selectedIds),
      }, { withCredentials: true });
      toast.success(`Cohort "${cohortForm.name}" created with ${selectedIds.size} assets`);
      setShowCohortDialog(false);
      setCohortForm({ name: '', reporting_cycle: '', notes: '' });
      setSelectedIds(new Set());
      await Promise.all([loadSummary(), loadInventory()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create cohort');
    } finally {
      setCreatingCohort(false);
    }
  };

  const updateAssetStatus = async (ea_id, newStatus) => {
    try {
      await axios.patch(`${API}/viatris/assets/${ea_id}`, {
        assessment_status: newStatus,
      }, { withCredentials: true });
      toast.success(`Moved to ${newStatus}`);
      await Promise.all([loadSummary(), loadInventory()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  // ----- AI assessment kickoff + polling -----
  const [assessmentJobs, setAssessmentJobs] = useState({}); // ea_id -> {status, step, step_name, progress_pct}
  const [assessmentDialog, setAssessmentDialog] = useState(null); // {ea_id, assessment} — legacy vertical modal, kept for regen flow
  const [syncResultModal, setSyncResultModal] = useState(null); // {renamed, unchanged, scanned, missing, samples: [{before, after, biotech_company_name, ...}]}
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  // Feb 2026 · user request "clicking (i) should show the same output as
  // the client Deep Dive". This state drives the horizontal-tabbed
  // AdminDeepDiveModal that byte-mirrors the client `RefDeepDiveModal`.
  const [deepDiveAsset, setDeepDiveAsset] = useState(null); // {ea_id, asset_name, indication, phase, modality, company_name}

  const pollJobStatus = useCallback(async (ea_id) => {
    try {
      const r = await axios.get(`${API}/viatris/assets/${ea_id}/assessment-status`,
        { withCredentials: true });
      setAssessmentJobs((prev) => ({ ...prev, [ea_id]: r.data }));
      return r.data;
    } catch {
      return null;
    }
  }, []);

  const startAssessment = async (ea_id, asset_name) => {
    try {
      const r = await axios.post(`${API}/viatris/assets/${ea_id}/generate-assessment`,
        {}, { withCredentials: true });
      // Jul 2026 · a "virtual-" row (universe-eligible asset with no
      // engagement_asset yet, e.g. JYB1904 after a company move) is
      // materialized server-side into a real engagement_asset; the
      // response carries the real ea_id. Poll + track job state under
      // that real id, not the synthetic one.
      const effectiveId = r.data?.ea_id || ea_id;
      // Backend returns `already_running` when a LIVE (< 10 min old)
      // job is still running for this asset. In that case just poll —
      // no need to spam the operator with another toast. If the row
      // was stuck > 10 min, the backend auto-reclaimed the orphan and
      // this response is a genuine `queued` start.
      if (r.data?.status === 'already_running') {
        toast.info(
          `${asset_name}: AI is already running (step ${r.data.step || '?'} · ${r.data.progress_pct || 0}%). Polling…`,
        );
      } else {
        toast.success(`Started AI assessment for ${asset_name}`);
      }
      setAssessmentJobs((prev) => ({ ...prev, [effectiveId]: r.data }));
      // Virtual row just became a real engagement_asset — refresh the
      // inventory so the row re-keys to its real id and shows the
      // in-progress state instead of a stale "Run AI" button.
      if (effectiveId !== ea_id) {
        loadInventory();
      }
      // Poll every 4s until done/error.
      const interval = setInterval(async () => {
        const status = await pollJobStatus(effectiveId);
        if (!status || status.status === 'done' || status.status === 'error') {
          clearInterval(interval);
          if (status?.status === 'done') {
            toast.success(`Assessment complete · v${status.version}`);
            const tc = status.tier_change;
            if (tc && tc.from_tier && tc.to_tier && tc.from_tier !== tc.to_tier) {
              toast.warning(
                `${asset_name}: tier changed ${tc.from_tier} → ${tc.to_tier}` +
                (tc.from_score != null && tc.to_score != null
                  ? ` (${Number(tc.from_score).toFixed(2)} → ${Number(tc.to_score).toFixed(2)} /5)` : '') +
                ` — hover the "was…" chip on the row to see what moved.`,
                { duration: 30000, closeButton: true },
              );
            }
            await Promise.all([loadSummary(), loadInventory()]);
          } else if (status?.status === 'error') {
            toast.error(`Assessment failed: ${status.error || 'unknown'}`);
          }
        }
      }, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to start assessment');
    }
  };

  const openAssessment = async (ea_id) => {
    setLoadingAssessment(true);
    setAssessmentDialog({ ea_id, assessment: null });
    try {
      const r = await axios.get(`${API}/viatris/assets/${ea_id}/assessment`,
        { withCredentials: true });
      if (r.data?.status === 'not_found') {
        toast.error('No assessment yet — click Generate AI Assessment first');
        setAssessmentDialog(null);
      } else {
        setAssessmentDialog({ ea_id, assessment: r.data });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load assessment');
      setAssessmentDialog(null);
    } finally {
      setLoadingAssessment(false);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6 px-6 py-5 max-w-[1500px] mx-auto" data-testid="viatris-workbench-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
            <Link to="/clients" className="hover:text-slate-700">Clients</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-700 font-medium">{CLIENT_LABEL}</span>
            <ChevronRight className="w-3 h-3" />
            <span className="inline-flex items-center gap-1.5 text-slate-700 font-medium">
              {CLIENT_LABEL === 'Modulus' ? 'Animal Health Universe' : 'China Universe'}
              {summary?.counters?.cohort_eligible != null && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-mono"
                  title="Screening-eligible assets ready for AI evaluation."
                >
                  {(summary.counters.cohort_eligible ?? 0).toLocaleString()} eligible
                </span>
              )}
            </span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">
            {CLIENT_LABEL === 'Modulus' ? 'Modulus × Animal Health' : 'Viatris × Mainland China'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Intelligence Workspace — Search &amp; Evaluation · bi-monthly review cohorts
          </p>
          <p className="text-[10px] text-indigo-700 mt-0.5 uppercase tracking-widest">
            ForesightRx Intelligence Platform (FIP) · AI-Powered Search, Evaluation &amp; Licensing Intelligence
          </p>
          {dupGroups > 0 && (
            <Link
              to="/data-ops/viatris-duplicates"
              className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-[11px] text-amber-800 hover:bg-amber-100"
              title="Some assets have more than one workbench row — counts can flicker and bulk actions may land on a hidden twin. Click to review & merge."
              data-testid="dup-rows-banner"
            >
              ⚠ {dupGroups} asset{dupGroups === 1 ? '' : 's'} with duplicate rows — Review &amp; Merge
            </Link>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const url = `${window.location.origin}/review`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success(`Reviewer workspace URL copied — ${url}`);
              } catch (e) {
                toast.error('Could not copy reviewer link');
              }
              window.open(url, '_blank');
            }}
            className="border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100"
            data-testid="reviewer-link-btn"
          >
            <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Reviewer link
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                // Fetch the live engagement record so the URL works in
                // every deployment environment (preview/production may
                // have different DBs and tokens).
                const r = await axios.get(`${API}/engagements/${ENG}`, { withCredentials: true });
                const token = r.data?.public_share_token;
                if (!token) {
                  toast.error('No share token configured for Viatris engagement');
                  return;
                }
                const url = `${window.location.origin}/client/viatris/${token}`;
                try { await navigator.clipboard.writeText(url); } catch (_) { /* clipboard optional */ }
                toast.success(`Viatris portal opened — URL also copied`);
                window.open(url, '_blank', 'noopener');
              } catch (e) {
                toast.error('Could not retrieve share token from server');
              }
            }}
            className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
            data-testid="share-portal-btn"
          >
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Client link
          </Button>
          <Button
            size="sm"
            onClick={() => setShowCommitteeProgressModal(true)}
            variant="outline"
            className="border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50"
            data-testid="committee-progress-btn"
            disabled={!filters.cohort}
            title={filters.cohort ? '' : 'Select a cohort first'}
          >
            <Users className="w-3.5 h-3.5 mr-1.5" /> Committee Progress
          </Button>
        </div>
      </div>

      {/* Feb 2026 v22 · Committee Progress modal — opened via the
          "Committee Progress" button above. Keeps the master widget
          out of the main flow (user asked to hide it) while still
          allowing on-demand access. Only rendered when the modal is
          open so we don't fetch data unnecessarily. */}
      {showCommitteeProgressModal && filters.cohort && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowCommitteeProgressModal(false)}
          data-testid="committee-progress-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-[90vw] max-h-[92vh] overflow-hidden flex flex-col w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-700" />
                <span className="font-semibold text-slate-900">Committee Progress · {filters.cohort}</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowCommitteeProgressModal(false)}
                data-testid="committee-progress-close"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <CommitteeProgressMaster cohort={filters.cohort} />
            </div>
          </div>
        </div>
      )}

      {/* Client Screening Definition — inclusion / exclusion rules
          + funnel counts. Session 1 of the generic Screening Engine.
          Feb 2026 · exclusion-based workflow for Viatris China Cohort
          2 ("exclude Oncology, siRNA, Cell therapy, Vaccine, API,
          In-licensing; include Phase 3+"). Shown BEFORE the universe
          strip so operators know which rules produced the counts. */}
      <ScreeningPanel engagementId={ENG} cohort={filters.cohort || ''} />

      {/* Feb 2026 v10 · WorkflowBanner removed — its next-action CTA
          is duplicated by the funnel action bar (Refresh · Run AI ·
          Generate Reports) rendered inside <ExplainableFunnel>. Only
          one toolbar per admin workspace, per Amit's UX refinement. */}

      {/* Feb 2026 v7 · UX refinement — UniverseStrip + StatusPipeline
          removed. The single Screening Funnel below is now the source
          of truth for every count and every user action. */}

      {/* Feb 2026 v4 · Fully-explainable funnel — now the PRIMARY
          navigation element of the Viatris workbench (spec §1, §4).
          Owns the 3 action buttons (Refresh Screening, Run AI, Reports)
          previously scattered across StatusPipeline + toolbar. Every
          count is clickable → drill modal with pagination + CSV export. */}
      {/* Aug 2026 · Persistent 5-stage cohort journey — the PRIMARY
          operational view (Eligible → AI → Committee → Shortlist →
          Availability) with cohort/cycle selection, Send-to-Committee and
          Complete-Cohort actions. The Screening Funnel below collapses
          into a secondary explainer. */}
      {CLIENT_LABEL !== 'Modulus' && (
        <ViatrisJourneyPanel
          mode="admin"
          journeyFilterKey={journeyFilter?.key || null}
          onJourneyFilter={(f) => {
            setJourneyFilter(f);
            if (f) {
              // Journey filters are exclusive — clear legacy filters + ?rec=.
              setFilters((prev) => ({ ...prev, recommendation: '', committee_status: '' }));
              const sp = new URLSearchParams(searchParams);
              sp.delete('rec');
              setSearchParams(sp, { replace: true });
            }
          }}
          onDataChanged={() => { loadSummary(); loadInventory(); }}
        />
      )}

      <details id="screening-details-block" className={CLIENT_LABEL === 'Modulus' ? '' : 'mt-2'} open={CLIENT_LABEL === 'Modulus'} data-testid="screening-funnel-collapsible">
        {CLIENT_LABEL !== 'Modulus' && (
          /* Aug 2026 UX simplification: summary hidden — the block is opened
             via "View Screening Details" in the Live China Universe strip. */
          <summary className="hidden">Screening details</summary>
        )}
        <ExplainableFunnel
        cohortName={filters.cohort}
        country={COUNTRY}
        refreshKey={pipelineActivity.length}
        aiOverrides={aiOverrides}
        activeStageFilter={filters.recommendation}
        onStageFilter={(val) => {
          // Stage 2 (Committee Review) card click — set/clear the
          // committee filter, keeping it in sync with ?rec= like the tiles.
          // Clears the Stage-3 shortlist filter: the funnel cards act as
          // exclusive tabs, so the table always matches the clicked card's
          // own count (never a compound of two filters).
          setFilters({ ...filters, recommendation: val, committee_status: '' });
          const sp = new URLSearchParams(searchParams);
          if (val) sp.set('rec', val); else sp.delete('rec');
          setSearchParams(sp, { replace: true });
        }}
        shortlistActive={filters.committee_status === 'Approved'}
        onShortlistFilter={(on) => {
          // Stage 3 (Client Shortlisted) card click — filter the table to
          // committee-Approved assets only. The tile counts EVERY Approved
          // row regardless of tier, so any active recommendation filter
          // must be cleared or the table would render fewer rows than the
          // tile promises (user report: tab said 29, table showed 28).
          setFilters({ ...filters, committee_status: on ? 'Approved' : '', recommendation: '' });
          const sp = new URLSearchParams(searchParams);
          sp.delete('rec');
          setSearchParams(sp, { replace: true });
        }}
        onRefreshPipeline={runRefreshScreeningPipeline}
        onRestoreShortlist={async () => {
          try {
            const { data } = await axios.post(
              `${API}/viatris/shortlist/restore-client-snapshot`,
              {}, { withCredentials: true }
            );
            const bits = [`${data.with_tier} of ${data.locked_count} locked with tiers`];
            if (data.no_tier?.length) bits.push(`${data.no_tier.length} need Run AI: ${data.no_tier.join('; ')}`);
            if (data.unmatched?.length) bits.push(`${data.unmatched.length} unmatched: ${data.unmatched.join('; ')}`);
            toast.success(`Client shortlist restored & locked — ${bits.join(' · ')}`, { duration: 12000 });
            window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Restore failed');
          }
        }}
        onRunAssessment={() => {
          if (!filters.cohort) {
            toast.info('Select a cohort first to run AI Assessment.');
            return;
          }
          window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'run_ai_assessment' } }));
        }}
        onSyncUniverseEligible={() => {
          if (!filters.cohort) {
            toast.info('Select a cohort first to sync universe-eligible assets.');
            return;
          }
          window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'sync_universe_eligible' } }));
        }}
        onSyncFromMaster={runSyncFromMaster}
        onFixMisShortlisted={runFixMisShortlisted}
        onOpenReports={() => {
          if (!filters.cohort) {
            toast.info('Select a cohort first to generate reports.');
            return;
          }
          window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'open_reports' } }));
        }}
        pipelineWorkflowRunning={pipelineWorkflow.running}
      />
      </details>

      {/* Feb 2026 v11 · Pipeline Activity Log removed per user request.
          The event stream is still recorded server-side via
          `screening_activity_log` — accessible via API for debugging
          if ever needed. */}

      {/* Progress modal — takes over the screen while
          runRefreshScreeningPipeline() ticks through its stages. Cannot
          be dismissed while running (guards against accidental duplicate
          jobs) — the four action buttons above are also disabled via
          the `pipelineWorkflowRunning` prop. */}
      <PipelineProgressModal
        state={pipelineWorkflow}
        onClose={() => setPipelineWorkflow(prev => ({ ...prev, open: false }))}
      />

      {/* Aug 2026 v3 · floating Committee Status chips REMOVED — the
          Pending / Under Review / Scored split now lives INSIDE its phase
          context: "View Phase 1 Details" for the historical Phase-1
          committee workflow, and under Stage 3 of the active Phase 2 card
          once assets have actually been sent to committee. */}

      {/* Committee Progress master table removed (Feb 2026 v20) — user
          confirmed it duplicated the Committee column already present in
          the main asset table (top). The master widget rendered every
          cohort row (including Not Suitable / Did-not-qualify) with
          empty 0/6 scoring slots, which was misleading. The legit
          committee state now lives on each row of the asset inventory:
          "Pending · N/6" (with per-scorer dots) or "Did not qualify".
          Bulk actions moved to the row-level Committee dropdown. */}

      {/* Conversion Funnel removed (Feb 2026 v2) — the 5-stage Status
          Pipeline above now tells the full narrowing story
          (Cohort → Phase 3+ → TA of Interest → Committee Review →
          Recommended), so a separate funnel block is redundant. */}

      {/* Selection bar (when assets selected) — Aug 2026: contextual.
          Actions already true for EVERY selected row are hidden (no
          redundant "Mark as Team Approved" on already-approved rows). */}
      {selectedIds.size > 0 && (() => {
        const selRows = rows.filter(r => selectedIds.has(r.id));
        const allApproved = selRows.length > 0 && selRows.every(r => (r.committee_status || '').trim() === 'Approved');
        const allTeamApproved = selRows.length > 0 && selRows.every(r => r.team_approved);
        const someTeamApproved = selRows.some(r => r.team_approved);
        const notRespondedCount = selRows.filter(r => !r.responded).length;
        return (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-indigo-50 border border-indigo-200 rounded-lg" data-testid="selection-bar">
          <div className="flex items-center gap-3 text-sm text-indigo-900">
            <Check className="w-4 h-4" />
            <span><strong>{selectedIds.size}</strong> asset(s) selected</span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={clearSelection}>
              Clear
            </Button>
            {!allApproved && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={bulkApprove}
              disabled={bulkApproving}
              data-testid="bulk-approve-btn"
            >
              {bulkApproving
                ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Approving…</>
                : <><Check className="w-3.5 h-3.5 mr-1.5" /> Mark as Approved ({selectedIds.size})</>}
            </Button>
            )}
            {!allTeamApproved && (
            <Button
              size="sm"
              className="bg-violet-700 hover:bg-violet-800 text-white"
              data-testid="bulk-team-approve-btn"
              onClick={() => setTeamApproveDialog('approve')}
            >
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Mark as Team Approved ({selectedIds.size})
            </Button>
            )}
            {someTeamApproved && (
              <Button
                size="sm"
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                data-testid="bulk-revoke-team-approve-btn"
                onClick={() => setTeamApproveDialog('revoke')}
              >
                <X className="w-3.5 h-3.5 mr-1.5" /> Revoke Team Approval
              </Button>
            )}
            {/* Aug 2026 · Mark as Responded — outreach-response tracking,
                fully independent of Approved / Team Approved / Shortlisted.
                Hidden when every selected asset is already Responded: Yes. */}
            {notRespondedCount > 0 && (
              <Button
                size="sm"
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={bulkMarkResponded}
                disabled={bulkResponding}
                data-testid="bulk-responded-btn"
              >
                {bulkResponding
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Marking…</>
                  : <><MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Mark as Responded ({notRespondedCount})</>}
              </Button>
            )}
            <Button
              size="sm"
              className="bg-sky-600 hover:bg-sky-700 text-white"
              onClick={() => openAvailabilityDialog(Array.from(selectedIds))}
              data-testid="bulk-availability-email-btn"
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Send Availability Email ({selectedIds.size})
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => setShowCohortDialog(true)}
              data-testid="create-cohort-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New cohort from selection
            </Button>
          </div>
        </div>
        );
      })()}

      {/* Team Approved confirm dialog (approve / revoke) */}
      <Dialog open={!!teamApproveDialog} onOpenChange={(o) => { if (!o && !teamApproveBusy) setTeamApproveDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="team-approve-dialog">
          <DialogHeader>
            <DialogTitle>{teamApproveDialog === 'revoke' ? 'Revoke Team Approval?' : 'Mark as Team Approved?'}</DialogTitle>
            <DialogDescription>
              {teamApproveDialog === 'revoke'
                ? `Team Approval will be removed from ${selectedIds.size} selected asset(s). Client Shortlisted status and availability tracking are unaffected.`
                : `${selectedIds.size} asset(s) will be marked TEAM APPROVED — the client's formal decision to pursue. Assets still at Committee "Pending" are auto-shortlisted (Committee Approved) in the same step; only Strategic / High / Medium priority assets qualify. Availability outreach is a separate, later step and is never overwritten by this milestone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={teamApproveBusy} onClick={() => setTeamApproveDialog(null)} data-testid="team-approve-cancel-btn">Cancel</Button>
            <Button
              className={teamApproveDialog === 'revoke' ? 'bg-slate-700 hover:bg-slate-800 text-white' : 'bg-violet-700 hover:bg-violet-800 text-white'}
              disabled={teamApproveBusy}
              onClick={() => runTeamApprove(teamApproveDialog !== 'revoke')}
              data-testid="team-approve-confirm-btn"
            >
              {teamApproveBusy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              {teamApproveDialog === 'revoke' ? 'Revoke Approval' : 'Confirm Team Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Aug 2026 v3 · empty-state banner REMOVED — it instructed the
          operator to pick a cohort from a selector that was retired
          (Jul 2026 "cohort concept removed"). The phase journey cards
          above now own the operational workflow. */}

      {/* Cohort Reports Panel — surfaces only when a cohort is filtered.
          We pass the TA + Phase scope so the Recommendation Distribution
          tiles inside the panel react together with the rest of the
          Workbench. */}
      {filters.cohort && (
        <CohortReportsPanel
          cohortName={filters.cohort}
          taPriorityOnly={filters.ta_priority_only}
          phase3Plus={filters.phase_3_plus}
          onChange={() => { loadSummary(); loadInventory(); }}
          rdCounts={rdCounts}
          activeRecommendation={filters.recommendation}
          onTierClick={(tier) => {
            // tier === null is the explicit "show All" click — always
            // clears the filter. Otherwise toggle (click same tier twice
            // to clear). Persisted to ?rec= so returning from a Deep
            // Dive or a reload keeps the active tier.
            const next = tier === null ? '' : (filters.recommendation === tier ? '' : tier);
            setFilters({ ...filters, recommendation: next });
            const sp = new URLSearchParams(searchParams);
            if (next) sp.set('rec', next); else sp.delete('rec');
            setSearchParams(sp, { replace: true });
          }}
          respondedCount={rdCounts.responded}
          respondedActive={respondedOnly}
          onRespondedClick={() => {
            const next = !respondedOnly;
            setRespondedOnly(next);
            const sp = new URLSearchParams(searchParams);
            if (next) sp.set('responded', '1'); else sp.delete('responded');
            setSearchParams(sp, { replace: true });
          }}
        />
      )}

      {/* Filter bar — Jul 2026: Cohort selector removed (cohort concept
          retired; everything runs on the China universe). filters.cohort
          is still auto-set internally so cohort-keyed endpoints work. */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="filter-bar">
        <span className="text-xs text-slate-500 inline-flex items-center gap-1">
          <Filter className="w-3 h-3" /> Filters:
        </span>
        {journeyFilter && (
          <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-violet-300 bg-violet-50 text-violet-800 text-[11px] font-medium" data-testid="journey-filter-badge">
            {journeyFilter.label}
            <button onClick={() => setJourneyFilter(null)} data-testid="journey-filter-clear" className="font-bold hover:text-violet-950">✕</button>
          </span>
        )}
        {journeyFilter && journeyFilter.metric && (
          <button
            data-testid="journey-filter-export-btn"
            onClick={() => {
              window.open(`${API}/viatris/journey/export?cohort=${journeyFilter.phase}&metric=${encodeURIComponent(journeyFilter.metric)}`, '_blank');
              toast.success(`Downloading ${journeyFilter.label} (Excel)`);
            }}
            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px] font-medium hover:bg-emerald-100 transition"
          >
            <Download className="w-3 h-3" /> Export {journeyFilter.count} — Cohort {journeyFilter.phase}
          </button>
        )}
        {/* Viatris scope badge — committee default is ON (CV / Metab /
            CNS / Immuno / Derm / Pain × Phase 3+). Feb 2026 made the badge
            click-to-toggle so analysts can flip into an admin-wide
            view of the full asset universe (e.g. to triage out-of-scope
            rows or reclassify them into a cohort). When OFF the badge
            switches to an amber "Scope OFF · admin view" treatment so
            no one accidentally screenshots out-of-scope counts for the
            client. State stored on the same ta_priority_only +
            phase_3_plus filter pair the backend already understands. */}
        {/* Feb 2026 v11 · Viatris scope badge hidden per user feedback
            ("filters of Viatris scope · CV / Metab / CNS · that goes
            off"). Underlying filter state stays intact — only the
            visible chip is suppressed. */}
        {false && (() => {
          const scopeOn = filters.ta_priority_only === '1' && filters.phase_3_plus === '1';
          return (
            <button
              type="button"
              onClick={() => setFilters((f) => ({
                ...f,
                ta_priority_only: scopeOn ? '' : '1',
                phase_3_plus:     scopeOn ? '' : '1',
              }))}
              className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-[11px] transition-colors cursor-pointer ${
                scopeOn
                  ? 'border-indigo-200 bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
                  : 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100'
              }`}
              data-testid="viatris-scope-badge"
              title={scopeOn
                ? 'Engagement scope locked to Viatris-declared criteria. Click to widen to all assets (admin view).'
                : 'Admin view — showing ALL assets across the engagement. Click to re-enable Viatris scope.'}
            >
              <Sparkles className="w-3 h-3" />
              {scopeOn
                ? 'Viatris scope · CV / Metab / CNS / Immuno / Derm / Pain · Phase 3+'
                : 'Scope OFF · admin view (all assets)'}
            </button>
          );
        })()}
        {/* Jul 2026 · "+ New Cohort" button removed — cohort concept
            retired; the workbench operates on the China universe. */}
        {/* Lock toggle removed from filter row (Sep 2026) — duplicated
            the "3. Lock cohort" action button in the toolbar above.
            Consolidated to a single workflow surface for clarity. */}
        <span className="text-xs text-slate-500 ml-auto" data-testid="total-rows">
          {loading ? 'Loading…'
            : journeyFilter ? (
              <span className="font-semibold text-violet-700">{journeyFilter.label.replace('—', '·')}</span>
            ) : `${displayRows.length.toLocaleString()} assets`}
        </span>
      </div>

      {/* Asset table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading && rows.length === 0 ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : displayRows.length === 0 ? (
            <div className="text-center py-16 text-sm text-slate-500" data-testid="empty-state">
              <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              No assets match the selected filters.
            </div>
          ) : (
            <table className="w-full table-fixed text-xs" data-testid="inventory-table">
              <colgroup>
                <col style={{ width: '36px' }} />{/* # num */}
                <col style={{ width: '36px' }} />{/* checkbox */}
                <col style={{ width: '220px' }} />{/* Asset (name + company) */}
                <col />{/* Indication / TA (flex fill) */}
                <col style={{ width: '110px' }} />{/* Stage */}
                <col style={{ width: '88px' }} />{/* Weighted /5 */}
                <col style={{ width: '190px' }} />{/* Recommendation + rationale chip */}
                <col style={{ width: '230px' }} />{/* Committee — "Pending · 0/6" + reviewer dots OR "Did not qualify" */}
                <col style={{ width: '160px' }} />{/* Responded — manual Yes/No + Rights / Response Note (Aug 2026) */}
                <col style={{ width: '90px' }} />{/* Run AI button — single-asset hardened pipeline trigger (Jun 2026) */}
                <col style={{ width: '110px' }} />{/* Deep Dive button — client-parity horizontal-tabbed modal */}
              </colgroup>
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 border-b">
                <tr>
                  <th className="px-2 py-2 text-right text-slate-400">#</th>
                  <th className="px-2 py-2 text-left">
                    <Checkbox
                      checked={selectedIds.size > 0 && selectedIds.size === rows.length}
                      onCheckedChange={(v) => v ? selectAllVisible() : clearSelection()}
                    />
                  </th>
                  <th className="px-2 py-2 text-left">Asset</th>
                  <th className="px-2 py-2 text-left">Indication / TA</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Stage</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">
                      Weighted /5
                      <InfoTip wide text={COMPOSITE_SCORE_TOOLTIP} />
                    </span>
                  </th>
                  <th className="pl-6 pr-2 py-2 text-left whitespace-nowrap">Recommendation</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Committee</th>
                  <th className="px-2 py-2 text-left whitespace-nowrap">Responded</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Run AI</th>
                  <th className="px-2 py-2 text-center whitespace-nowrap">Deep Dive</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((r, idx) => (
                  <tr key={r.id} className="border-b hover:bg-slate-50 transition-colors" data-testid={`row-${r.id}`}>
                    <td className="px-2 py-2 text-right font-mono text-[10px] text-slate-400" data-testid={`row-num-${idx + 1}`}>{idx + 1}</td>
                    <td className="px-2 py-2">
                      <Checkbox
                        checked={selectedIds.has(r.id)}
                        onCheckedChange={() => toggleSelect(r.id)}
                        data-testid={`row-select-${r.id}`}
                      />
                    </td>
                    <td className="px-2 py-2 max-w-0">
                      {(() => {
                        const hasAssessment = r.last_assessed_at || r.overall_score != null;
                        // Feb 2026 · Two click targets on the asset cell so
                        // analysts can (a) view the AI assessment on the
                        // primary label, and (b) jump to the master
                        // biotech page for edits via the ↗ icon —
                        // asset_name / phase / TA / modality edits made
                        // there auto-cascade to Committee Review /
                        // Reviewer / Client Portal.
                        const biotechId = r.biotech_company_id || r.company_id;
                        return (
                          <div className="flex items-center gap-1 min-w-0">
                            <button
                              type="button"
                              onClick={() => hasAssessment
                                ? setDeepDiveAsset({
                                    ea_id: r.id,
                                    asset_name: r.asset_name,
                                    indication: r.indication,
                                    phase: r.phase,
                                    modality: r.modality,
                                    company_name: r.company_name,
                                  })
                                : startAssessment(r.id, r.asset_name)}
                              className={`text-left font-semibold truncate hover:underline flex-1 min-w-0 ${hasAssessment ? 'text-violet-700' : 'text-slate-800'} transition-colors`}
                              title={hasAssessment ? 'Click to view AI assessment' : 'Click to generate AI assessment for this asset'}
                              data-testid={`asset-cell-${r.id}`}
                            >
                              {r.asset_name}
                              {r.ai_assessment_status === 'Enriching' && (
                                <Loader2 className="inline w-3 h-3 ml-1 animate-spin text-violet-500" />
                              )}
                              {r.ai_assessment_status === 'Failed' && (
                                <AlertCircle className="inline w-3 h-3 ml-1 text-rose-500" />
                              )}
                            </button>
                            {biotechId && (
                              <Link
                                to={`/biotech/${biotechId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 text-slate-400 hover:text-violet-600 transition-colors"
                                title="Open master biotech page to edit — changes auto-propagate to Committee Review, Reviewer, Client Portal"
                                data-testid={`asset-edit-link-${r.id}`}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ArrowRight className="w-3 h-3 rotate-[-45deg]" />
                              </Link>
                            )}
                          </div>
                        );
                      })()}
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                        {(r.biotech_company_id || r.company_id) ? (
                          <Link
                            to={`/biotech/${r.biotech_company_id || r.company_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-slate-600 hover:text-violet-600 hover:underline truncate block w-full transition-colors"
                            title={`Open ${r.company_name || 'biotech'} master page to edit`}
                            data-testid={`company-link-${r.id}`}
                          >
                            {r.company_name || '—'}
                          </Link>
                        ) : (
                          <span className="text-slate-600 truncate block w-full" title={r.company_name}>
                            {r.company_name || '—'}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 max-w-0">
                      <div className="text-slate-700 truncate" title={r.indication}>{r.indication || '—'}</div>
                      <div className="text-[10px] text-slate-400 truncate">{r.therapeutic_area || ''}</div>
                    </td>
                    <td className="px-2 py-2 text-slate-600 truncate" title={r.phase}>{r.phase || '—'}</td>
                    {/* Lifecycle column removed (Feb 2026 · user request) —
                        the downstream stages (Analyst Review, Committee
                        Ready, Go Candidate, Deep Dive, Recommended) didn't
                        make sense for Low Priority / Not Suitable tiers.
                        AI status is now communicated by:
                          (a) spinner on the asset-name cell when Enriching
                          (b) Weighted /5 column (number if scored, — if not)
                          (c) Recommendation column ("Run AI" button when tier is empty)
                        This keeps the table narrower and honest about what
                        actually matters per asset. */}
                    <td className="px-2 py-2 whitespace-nowrap">
                      {(() => {
                        const w = r.weighted_avg_score ?? (r.composite_score != null ? r.composite_score / 9 : null);
                        return w != null ? (
                          <span className="font-mono text-slate-800">{w.toFixed(2)}<span className="text-slate-400 text-[10px] ml-0.5">/5</span></span>
                        ) : <span className="text-slate-300">—</span>;
                      })()}
                    </td>
                    <td className="pl-6 pr-2 py-2 overflow-hidden align-top">
                      {r.recommendation ? (
                        <div className="flex flex-col items-start gap-1 min-w-0 max-w-full">
                          <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                            <Badge
                              className={`${RECOMMENDATION_COLORS[r.recommendation] || ''} text-[10px] py-0 shrink-0`}
                            >
                              {r.recommendation}
                            </Badge>
                            {/* Feb 2026 · the (i) icon shows the rationale popover. */}
                            <WhyRecommendationInfo
                              asset={{ ...r, ea_id: r.id }}
                              testIdPrefix={`rec-rationale`}
                            />
                          </span>
                          {/* Jul 2026 · CNA failure reason on its OWN LINE
                              below the badge — the old inline layout in a
                              190px table-fixed cell overflowed and visually
                              collided with the Committee column. Stacked +
                              truncated + cell overflow-hidden ends that for
                              good. Full reason stays in the tooltip. */}
                          {r.recommendation === 'Could Not Assess' && (r.cna_category || r.cna_reason || r.ai_concern_text) && (
                            <span
                              className="block max-w-full truncate px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-medium cursor-help"
                              title={r.cna_reason || r.ai_concern_text || r.cna_category}
                              data-testid="cna-category-chip"
                            >
                              {(r.cna_category || 'See reason').replace('No public information found', 'No public info')}
                            </span>
                          )}
                          {/* Jun 2026 · before→after chip from the latest AI
                              re-run so tier/score movements are never silent.
                              Hover shows per-dimension deltas + rationale. */}
                          {(() => {
                            const tc = r.last_tier_change;
                            if (!tc || !tc.from_tier || !tc.to_tier) return null;
                            const tierChanged = tc.from_tier !== tc.to_tier;
                            const scoreChanged = tc.from_score != null && tc.to_score != null && Math.abs(tc.from_score - tc.to_score) >= 0.01;
                            if (!tierChanged && !scoreChanged) return null;
                            const fs = tc.from_score != null ? Number(tc.from_score).toFixed(2) : '—';
                            const ts = tc.to_score != null ? Number(tc.to_score).toFixed(2) : '—';
                            const deltaLines = (tc.dimension_deltas || [])
                              .map((d) => `• ${d.dimension}: ${d.from} → ${d.to}${d.rationale ? ` — ${d.rationale}` : ''}`)
                              .join('\n');
                            const title =
                              `AI re-run v${tc.from_version} → v${tc.to_version}\n` +
                              `Tier: ${tc.from_tier} → ${tc.to_tier}\n` +
                              `Score: ${fs} → ${ts} /5` +
                              (deltaLines ? `\n\nWhat moved:\n${deltaLines}` : '');
                            return (
                              <span
                                className={`block max-w-full truncate px-1.5 py-0.5 rounded text-[9px] font-medium cursor-help border ${tierChanged ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
                                title={title}
                                data-testid={`tier-change-chip-${r.id}`}
                              >
                                {tierChanged ? `was ${tc.from_tier} · ${fs} → ${ts}` : `score ${fs} → ${ts}`}
                              </span>
                            );
                          })()}
                        </div>
                      ) : (
                        // Blank when no recommendation — the bulk
                        // "Run AI Assessment" button in the toolbar
                        // (generateBatchAssessment → /generate-batch)
                        // picks up every row with a missing tier and
                        // runs AI concurrently. No per-row action here
                        // per user request (Feb 2026).
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-slate-600 whitespace-nowrap text-[11px] overflow-hidden align-top">
                      {(() => {
                        // Jul 2026 · Client Shortlisted stage — an Approved
                        // row shows the Shortlisted badge + the inline
                        // innovator availability tracker (any tier).
                        if (r.committee_status === 'Approved') {
                          return (
                            <div className="flex flex-col gap-1 items-start">
                              <Badge
                                className="text-[10px] py-0 border bg-emerald-50 text-emerald-700 border-emerald-300"
                                title={`Shortlisted by Viatris after the first committee review${r.committee_decided_at ? ` — ${new Date(r.committee_decided_at).toLocaleDateString()}` : ''}`}
                                data-testid={`committee-shortlisted-${r.id}`}
                              >
                                <Check className="w-2.5 h-2.5 mr-0.5" /> Shortlisted
                              </Badge>
                              {r.team_approved && (
                                <Badge
                                  className="text-[10px] py-0 border bg-violet-50 text-violet-700 border-violet-300"
                                  title={`Team Approved — the client's formal decision to pursue${r.team_approved_at ? ` (${new Date(r.team_approved_at).toLocaleDateString()})` : ''}. Availability outcomes never overwrite this milestone.`}
                                  data-testid={`team-approved-badge-${r.id}`}
                                >
                                  <ShieldCheck className="w-2.5 h-2.5 mr-0.5" /> Team Approved
                                </Badge>
                              )}
                              <AvailabilityChip row={r} onChange={setAvailabilityStatus} />
                              <span
                                className="inline-flex items-center gap-0.5 text-[10px] font-medium rounded-full border px-1.5 py-0.5 bg-violet-50 text-violet-700 border-violet-300"
                                title="Shortlisted assets automatically enter the Reviewer Deep Dive queue"
                                data-testid={`deep-dive-badge-${r.id}`}
                              >
                                <ArrowRight className="w-2.5 h-2.5" /> In Deep Dive
                              </span>
                            </div>
                          );
                        }
                        // Feb 2026 · user request — Committee column is
                        // strictly three states, no dots, no fractions:
                        //   • "Did not qualify"  — tier below Medium
                        //   • "<score>/5"        — all 6 reviewers submitted
                        //   • "Pending"          — anything else
                        const ACTIVE = new Set([
                          'Strategic Priority', 'High Priority', 'Medium',
                        ]);
                        const rec = r.recommendation || '';
                        if (!rec) {
                          return <span className="text-slate-300">—</span>;
                        }
                        if (!ACTIVE.has(rec)) {
                          return (
                            <Badge
                              className="text-[10px] py-0 border bg-slate-50 text-slate-500 border-slate-200"
                              title="Not routed to committee — tier below Medium threshold"
                              data-testid={`committee-did-not-qualify-${r.id}`}
                            >
                              Did not qualify
                            </Badge>
                          );
                        }
                        // Active-review tier — show final avg if complete,
                        // else a simple Pending badge.
                        if (r.committee_state === 'Complete' && r.committee_avg != null) {
                          return (
                            <span
                              className="font-mono text-[11px] text-emerald-800 font-bold"
                              data-testid={`committee-avg-${r.id}`}
                            >
                              {r.committee_avg.toFixed(2)}
                              <span className="text-slate-400 text-[10px] ml-0.5">/5</span>
                            </span>
                          );
                        }
                        return (
                          <Badge
                            className="text-[10px] py-0 border bg-slate-100 text-slate-600 border-slate-300"
                            title="Awaiting client committee scoring — appears once all 6 reviewers submit"
                            data-testid={`committee-pending-${r.id}`}
                          >
                            Pending
                          </Badge>
                        );
                      })()}
                    </td>
                    {/* Aug 2026 · Responded — manual Yes/No + free-text
                        Rights / Response Note. Independent of statuses. */}
                    <td className="px-2 py-2 align-top">
                      <RespondedCell row={r} onToggle={setResponded} onSaveNote={saveResponseNote} />
                    </td>
                    {/* Run AI button (Jun 2026 · user request) — manually
                        trigger the hardened grounded-generation pipeline
                        for a SINGLE asset to pilot output quality without
                        batch-running all assets. */}
                    <td className="px-2 py-2 text-center whitespace-nowrap">
                      {(() => {
                        const job = assessmentJobs[r.id];
                        const running = r.ai_assessment_status === 'Enriching' ||
                          (job && job.status && job.status !== 'done' && job.status !== 'error');
                        return (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={running}
                            className="h-6 px-2 text-[10px] font-medium border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-400 disabled:opacity-60"
                            onClick={(e) => {
                              e.stopPropagation();
                              startAssessment(r.id, r.asset_name);
                            }}
                            title={running ? 'AI assessment in progress…' : 'Run the hardened AI generation pipeline for this asset'}
                            data-testid={`run-ai-btn-${r.id}`}
                          >
                            {running ? (
                              <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Running</>
                            ) : (
                              <><Sparkles className="w-3 h-3 mr-1" />Run AI</>
                            )}
                          </Button>
                        );
                      })()}
                    </td>
                    {/* Deep Dive button (Feb 2026 · user request "on right
                        side of committee, there should be a button on Deep
                        Dive"). Opens the client-parity horizontal-tabbed
                        AdminDeepDiveModal — same content the client sees. */}
                    <td className="px-2 py-2 text-center whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[10px] font-medium border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeepDiveAsset({
                            ea_id: r.id,
                            asset_name: r.asset_name,
                            indication: r.indication,
                            phase: r.phase,
                            modality: r.modality,
                            company_name: r.company_name,
                          });
                        }}
                        data-testid={`deep-dive-btn-${r.id}`}
                      >
                        Deep Dive
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Admin Deep Dive Modal — client-parity horizontal tabs (Feb 2026 · user
          request "clicking (i) should show the same output as the client Deep Dive"). */}
      {deepDiveAsset && (
        <AdminDeepDiveModal
          ea_id={deepDiveAsset.ea_id}
          asset={deepDiveAsset}
          onClose={() => setDeepDiveAsset(null)}
        />
      )}

      {/* AI Assessment Results Dialog (legacy vertical layout · kept for regen flow) */}
      <Dialog open={!!assessmentDialog} onOpenChange={(o) => !o && setAssessmentDialog(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="assessment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
              AI Assessment
              {assessmentDialog?.assessment?.version && (
                <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]">
                  v{assessmentDialog.assessment.version}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {assessmentDialog?.assessment?.asset_name || 'Loading...'}
              {assessmentDialog?.assessment?.biotech_name && ` · ${assessmentDialog.assessment.biotech_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-4 pr-2">
            {loadingAssessment ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : assessmentDialog?.assessment ? (
              <AssessmentView assessment={assessmentDialog.assessment} eaId={assessmentDialog.assessment.ea_id} />
            ) : null}
          </div>
          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setAssessmentDialog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Cohort Dialog */}
      <Dialog open={showCohortDialog} onOpenChange={setShowCohortDialog}>
        <DialogContent data-testid="new-cohort-dialog">
          <DialogHeader>
            <DialogTitle>Create new cohort</DialogTitle>
            <DialogDescription>
              {selectedIds.size} asset(s) will be assigned to this cohort. The lifecycle
              stage will move to &quot;Selected for Cohort&quot; for assets still in Inventory.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Cohort name</label>
              <Input
                placeholder="e.g. China Cohort 3"
                value={cohortForm.name}
                onChange={(e) => setCohortForm({ ...cohortForm, name: e.target.value })}
                data-testid="cohort-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Reporting cycle (optional)</label>
              <Input
                placeholder="e.g. 2026-Apr"
                value={cohortForm.reporting_cycle}
                onChange={(e) => setCohortForm({ ...cohortForm, reporting_cycle: e.target.value })}
                data-testid="cohort-cycle-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Notes (optional)</label>
              <Input
                placeholder="Anything to remember about this cohort..."
                value={cohortForm.notes}
                onChange={(e) => setCohortForm({ ...cohortForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCohortDialog(false)}>Cancel</Button>
            <Button
              onClick={createCohort}
              disabled={creatingCohort || !cohortForm.name.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-create-cohort"
            >
              {creatingCohort ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Create cohort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-approval innovator availability inquiry (Jul 2026) */}
      <Dialog open={availDialog.open} onOpenChange={(o) => !o && setAvailDialog(prev => ({ ...prev, open: false }))}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="availability-inquiry-dialog">
          <DialogHeader>
            <DialogTitle>Confirm availability with innovators</DialogTitle>
            <DialogDescription>
              Sends each innovator a short email asking: (1) is the asset still available for partnering,
              (2) are China rights available, (3) are they interested in pursuing the opportunity.
              Replies help refine the final shortlist before presentation prep.
            </DialogDescription>
          </DialogHeader>
          {availDialog.loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Resolving innovator contacts…
            </div>
          ) : (
            <>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[240px] overflow-y-auto">
                {availDialog.items.map((it) => (
                  <div
                    key={it.ea_id}
                    onClick={() => setAvailDialog(prev => ({ ...prev, previewEa: it.ea_id }))}
                    className={`flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer transition-colors ${
                      (availDialog.previewEa || availDialog.items[0]?.ea_id) === it.ea_id ? 'bg-sky-50' : 'hover:bg-slate-50'
                    }`}
                    title="Click to preview this asset's email below"
                    data-testid={`avail-row-${it.ea_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800 truncate">{it.asset_name}</p>
                      <p className="text-xs text-slate-500 truncate">{it.company_name}</p>
                    </div>
                    {it.contact_email ? (
                      <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 shrink-0">
                        {it.contact_name ? `${it.contact_name} · ` : ''}{it.contact_email}
                      </span>
                    ) : (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 shrink-0">No contact on file — will be skipped</span>
                    )}
                  </div>
                ))}
                {availDialog.items.length === 0 && (
                  <p className="text-sm text-slate-500 px-3 py-4">No assets found.</p>
                )}
              </div>
              {availDialog.items.length > 0 && (() => {
                const p = availDialog.items.find(i => i.ea_id === availDialog.previewEa) || availDialog.items[0];
                return (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600" data-testid="avail-email-preview">
                    <p className="font-semibold text-slate-700">Email preview — {p.asset_name}</p>
                    <p className="text-slate-500 mt-0.5 mb-2">Subject: <span className="font-medium text-slate-700">{p.subject}</span></p>
                    <div
                      className="bg-white border border-slate-200 rounded-md p-3 text-[12px] text-slate-700 leading-relaxed max-h-[220px] overflow-y-auto [&_p]:mb-2 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: p.body_html }}
                    />
                    <p className="mt-2 text-slate-500">Sent from your connected Gmail · logged in the outreach audit trail · click another asset above to preview its email.</p>
                  </div>
                );
              })()}
              {availDialog.results && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="avail-results">
                  Sent {availDialog.results.sent} email(s){availDialog.results.failed?.length ? ` · ${availDialog.results.failed.length} failed` : ''}{availDialog.results.rate_limited ? ' · stopped at Gmail rate limit — resend later for the rest' : ''}.
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAvailDialog(prev => ({ ...prev, open: false }))} data-testid="avail-close-btn">
              {availDialog.results ? 'Done' : 'Skip for now'}
            </Button>
            {!availDialog.results && (
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={sendAvailabilityInquiries}
                disabled={availDialog.sending || availDialog.loading || availDialog.items.filter(i => i.contact_email).length === 0}
                data-testid="avail-send-btn"
              >
                {availDialog.sending
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Sending…</>
                  : <>Send to {availDialog.items.filter(i => i.contact_email).length} innovator(s)</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cohort Suggestions Dialog */}
      <Dialog open={showSuggestionsDialog} onOpenChange={setShowSuggestionsDialog}>
        <DialogContent className="max-w-4xl" data-testid="suggestions-dialog">
          <DialogHeader>
            <DialogTitle>Suggested assets for next cohort</DialogTitle>
            <DialogDescription>
              Top un-assigned assets by score. Asset-first view — companies hidden by default.
              When an asset&apos;s parent company has assets in past cohorts, you&apos;ll see the cohort
              names listed so you can avoid duplicating coverage.
            </DialogDescription>
          </DialogHeader>
          {/* Feb 2026 v2 · size picker — user requested flexible range on
              "next cohort" click. Preset chips + custom input. Preset
              click OR custom-input change (debounced 400ms) refetches
              automatically — no manual Load click required. Backend
              allows 10-2000. */}
          <div className="flex items-center gap-2 flex-wrap px-1 pb-2 border-b border-slate-100" data-testid="cohort-suggestion-size-picker">
            <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Cohort size</span>
            {[100, 250, 500, 1000].map(preset => (
              <button
                key={preset}
                type="button"
                onClick={() => { setSuggestionSize(preset); openCohortSuggestions(preset); }}
                className={
                  'text-[11px] px-2.5 py-1 rounded border transition-colors ' +
                  (suggestionSize === preset
                    ? 'bg-indigo-600 text-white border-indigo-700 font-semibold'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400')
                }
                data-testid={`cohort-size-preset-${preset}`}
              >
                {preset}
              </button>
            ))}
            <Input
              type="number"
              min={10}
              max={2000}
              step={10}
              value={suggestionSize}
              onChange={(e) => {
                const next = Math.max(10, Math.min(2000, parseInt(e.target.value, 10) || 10));
                setSuggestionSize(next);
                // Debounce so typing "1234" doesn't fire 4 requests.
                if (suggestionSizeDebounce.current) clearTimeout(suggestionSizeDebounce.current);
                suggestionSizeDebounce.current = setTimeout(() => {
                  openCohortSuggestions(next);
                }, 400);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (suggestionSizeDebounce.current) clearTimeout(suggestionSizeDebounce.current);
                  openCohortSuggestions();
                }
              }}
              className="h-7 w-20 text-[11px]"
              data-testid="cohort-size-custom-input"
              title="Any value between 10 and 2000 — auto-refetches ~400ms after you stop typing"
            />
            {loadingSuggestions && (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
            )}
            <span className="text-[10px] text-slate-500 ml-auto">
              Showing {suggestions.length} of {suggestionSize} requested
            </span>
          </div>
          {loadingSuggestions ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 border-b sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left w-8">
                      {/* Feb 2026 · master select-all. Ticks every
                          currently-loaded suggestion (respects the
                          current cohort-size N). Untick to clear. */}
                      <Checkbox
                        checked={suggestions.length > 0 && suggestions.every(s => selectedIds.has(s.id))}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            const next = new Set(selectedIds);
                            suggestions.forEach(s => next.add(s.id));
                            setSelectedIds(next);
                          } else {
                            const suggestedIds = new Set(suggestions.map(s => s.id));
                            const next = new Set();
                            selectedIds.forEach(id => { if (!suggestedIds.has(id)) next.add(id); });
                            setSelectedIds(next);
                          }
                        }}
                        data-testid="cohort-suggestions-select-all"
                        aria-label="Select all visible suggestions"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Asset</th>
                    <th className="px-3 py-2 text-left">Indication</th>
                    <th className="px-3 py-2 text-left">Stage</th>
                    <th className="px-3 py-2 text-left">
                      <span className="inline-flex items-center gap-1">
                        Score
                        <InfoTip wide text={COMPOSITE_SCORE_TOOLTIP} />
                      </span>
                    </th>
                    <th className="px-3 py-2 text-left">Co. in prior cohorts</th>
                  </tr>
                </thead>
                <tbody>
                  {suggestions.map((s) => (
                    <tr key={s.id} className="border-b hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <Checkbox
                          checked={selectedIds.has(s.id)}
                          onCheckedChange={() => toggleSelect(s.id)}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800">{s.asset_name}</td>
                      <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate" title={s.indication}>{s.indication || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{s.phase || '—'}</td>
                      <td className="px-3 py-2 font-mono text-slate-700">
                        {s.overall_score?.toFixed(1) ?? s.pre_score?.toFixed(1) ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-[10px]">
                        {s.company_in_prior_cohorts?.length > 0 ? (
                          <span className="text-amber-700">{s.company_in_prior_cohorts.join(', ')}</span>
                        ) : (
                          <span className="text-emerald-600">New</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <div className="text-xs text-slate-500 mr-auto">
              {selectedIds.size} selected
            </div>
            <Button variant="outline" onClick={() => setShowSuggestionsDialog(false)}>Close</Button>
            <Button
              onClick={() => { setShowSuggestionsDialog(false); setShowCohortDialog(true); }}
              disabled={selectedIds.size === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Create cohort ({selectedIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sync-from-Master results modal — shows the actual before →
          after asset name changes so the reviewer can see WHAT
          happened, not just an abstract counter. */}
      {syncResultModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
             onClick={() => setSyncResultModal(null)}
             data-testid="sync-result-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full p-6 space-y-4 max-h-[80vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-lg font-bold text-slate-900">Sync from Master · Results</div>
                <div className="text-xs text-slate-500 mt-1">
                  Scanned <strong>{syncResultModal.scanned}</strong> row(s) · Renamed <strong className="text-emerald-700">{syncResultModal.renamed}</strong>
                  {' · '}Unchanged <strong>{syncResultModal.unchanged}</strong>
                  {syncResultModal.missing > 0 && <> · Skipped <strong>{syncResultModal.missing}</strong> (master record missing)</>}
                </div>
                <div className="text-[10px] text-emerald-700 mt-0.5">Only <code className="bg-emerald-50 px-1 rounded">asset_name</code> was modified — every other field left untouched.</div>
              </div>
              <button onClick={() => setSyncResultModal(null)} className="text-slate-400 hover:text-slate-700" data-testid="sync-result-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {syncResultModal.samples && syncResultModal.samples.length > 0 ? (
              <div className="overflow-auto flex-1 border border-slate-200 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="text-left text-[10px] uppercase text-slate-500 tracking-wide">
                      <th className="p-2">Biotech</th>
                      <th className="p-2">Before</th>
                      <th className="p-2">→</th>
                      <th className="p-2">After (from master)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {syncResultModal.samples.map((s, i) => (
                      <tr key={i}>
                        <td className="p-2 text-slate-500 max-w-[10rem] truncate" title={s.biotech_company_name}>{s.biotech_company_name || '—'}</td>
                        <td className="p-2 text-rose-700 line-through max-w-md truncate" title={s.asset_name?.before}>{s.asset_name?.before || <em className="text-slate-300">(blank)</em>}</td>
                        <td className="p-2 text-slate-300">→</td>
                        <td className="p-2 text-emerald-700 font-medium max-w-md truncate" title={s.asset_name?.after}>{s.asset_name?.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {syncResultModal.renamed > (syncResultModal.samples?.length || 0) && (
                  <div className="p-2 text-[10px] text-slate-400 bg-slate-50 border-t border-slate-200">
                    Showing first {syncResultModal.samples.length} of {syncResultModal.renamed} changes. All {syncResultModal.renamed} have been applied.
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-500 py-6 text-center">No name changes detected.</div>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={() => setSyncResultModal(null)} variant="outline" data-testid="sync-result-done">Done</Button>
            </div>
          </div>
        </div>
      )}

    </div>
    </TooltipProvider>
  );
}

// ─── Quick-create cohort + Lock/Unlock toggle (Sep 2026) ───────────
// Drops a fresh named cohort. Monthly cadence per Viatris cycle:
// every new feed (~250 assets) starts with a name typed here, then
// assets get assigned via the inventory bulk-select flow.
function NewCohortButton({ existingCohorts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [cycle, setCycle] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) return;
    if ((existingCohorts || []).some((c) => (c.name || '').toLowerCase() === clean.toLowerCase())) {
      toast.error(`Cohort "${clean}" already exists.`);
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/viatris/cohorts`, {
        name: clean,
        reporting_cycle: cycle.trim() || null,
        asset_ids: [],
      }, { withCredentials: true });
      toast.success(`Cohort "${clean}" created. Assign assets next.`);
      setOpen(false); setName(''); setCycle('');
      if (onCreated) onCreated(clean);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create cohort');
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
        className="h-8 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
        data-testid="new-cohort-btn"
      >
        + New Cohort
      </Button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="new-cohort-modal">
          <div className="bg-white rounded-lg max-w-md w-full p-5 shadow-2xl">
            <h3 className="text-base font-bold text-slate-900 mb-2">Create new cohort</h3>
            <p className="text-xs text-slate-600 mb-4">
              Name your cohort manually so monthly / bi-monthly cadence stays flexible. Assets get assigned afterwards from the inventory below.
            </p>
            <label className="text-xs font-medium text-slate-700">Cohort name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. China Cohort · Sep 2026"
              className="mb-3 text-sm"
              autoFocus
              data-testid="new-cohort-name"
            />
            <label className="text-xs font-medium text-slate-700">Reporting cycle (optional)</label>
            <Input
              value={cycle}
              onChange={(e) => setCycle(e.target.value)}
              placeholder="e.g. 2026-09 or Q3 2026"
              className="mb-4 text-sm"
              data-testid="new-cohort-cycle"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="text-xs h-8" data-testid="new-cohort-cancel">
                Cancel
              </Button>
              <Button size="sm" onClick={submit} disabled={busy || !name.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8" data-testid="new-cohort-submit">
                {busy ? 'Creating…' : 'Create cohort'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Cohort Lock / Unlock toggle. Locking freezes the client portal
// scoring (writes return 423); unlocking re-opens for late edits.
function CohortLockToggle({ cohortName, locked, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    const next = !locked;
    const ok = window.confirm(
      next
        ? `Lock cohort "${cohortName}"? Client portal scoring will become read-only until you unlock.`
        : `Unlock cohort "${cohortName}"? Client portal scoring will be writable again.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      const path = next ? 'lock' : 'unlock';
      await axios.post(`${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/${path}`, {}, { withCredentials: true });
      toast.success(next ? 'Cohort locked · client view read-only' : 'Cohort unlocked · writes re-enabled');
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${next ? 'lock' : 'unlock'} cohort`);
    } finally { setBusy(false); }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={toggle}
      disabled={busy}
      className={`h-8 text-[11px] ${locked ? 'border-rose-300 text-rose-700 hover:bg-rose-50' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
      data-testid="cohort-lock-toggle"
    >
      {busy ? '…' : locked ? '🔒 Unlock cohort' : '🔓 Lock for board'}
    </Button>
  );
}


function FilterSelect({ value, onChange, label, options, testid, noAll = false }) {
  // Accept either string[] or {v, l}[] for label-different options.
  const normalized = options.map((o) => (typeof o === 'string' ? { v: o, l: o } : o));
  // `noAll` (Feb 2026 · user request for Cohort filter) — hides the
  // "— All" pseudo-option so users can't accidentally view an
  // aggregate across multiple cohorts. Recommended when the filter
  // only makes sense scoped to a specific value (e.g. cohort counts
  // are not additive because the same asset can span cohorts).
  return (
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger className={`h-8 text-[11px] w-auto min-w-[110px] ${value ? 'bg-blue-50 border-blue-300 text-blue-800' : ''}`} data-testid={testid}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        {!noAll && (
          <SelectItem value="__all__" className="text-xs">{label} — All</SelectItem>
        )}
        {normalized.map((o) => (
          <SelectItem key={o.v} value={o.v} className="text-xs">{o.l}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}


// Assessment results view — renders 8 sections (Company / Asset / Scientific /
// Competitive / Scoring / Executive Summary / Change Log / References).
function AssessmentView({ assessment, eaId }) {
  const sections = [
    { key: 'executive_summary', title: 'Executive Summary', icon: BookOpen },
    { key: 'scoring', title: 'Viatris Scoring', icon: Beaker },
    { key: 'company_intelligence', title: 'Company Intelligence', icon: Layers },
    { key: 'asset_intelligence', title: 'Asset Intelligence', icon: Sparkles },
    { key: 'scientific_assessment', title: 'Scientific Assessment', icon: BookOpen },
    { key: 'competitive_landscape', title: 'Competitive Landscape', icon: Users },
  ];
  const empty = sections.every((s) => !assessment[s.key] || Object.keys(assessment[s.key]).length === 0);

  // Sep 2026 — Opportunity Assessment + Overall Recommendation
  const [oa, setOa] = React.useState(null);
  const [oaLoading, setOaLoading] = React.useState(false);
  const [oaSaving, setOaSaving] = React.useState(false);
  const [overall, setOverall] = React.useState(null);
  const [genLoading, setGenLoading] = React.useState(false);

  const loadOa = React.useCallback(async () => {
    if (!eaId) return;
    setOaLoading(true);
    try {
      const r = await axios.get(`${API}/viatris/assets/${eaId}/opportunity-assessment`, { withCredentials: true });
      setOa(r.data);
      // Also fetch the engagement asset to read overall_recommendation if present.
      const ra = await axios.get(`${API}/viatris/assets/${eaId}/inventory-row`, { withCredentials: true }).catch(() => null);
      if (ra?.data?.overall_recommendation) setOverall(ra.data.overall_recommendation);
    } catch (e) {
      // 404/401 fine — just show empty editor
    } finally {
      setOaLoading(false);
    }
  }, [eaId]);
  React.useEffect(() => { loadOa(); }, [loadOa]);

  const saveOa = async () => {
    if (!eaId || !oa) return;
    setOaSaving(true);
    try {
      const payload = {
        score: oa.opportunity_assessment?.score || 3,
        rationale: oa.opportunity_assessment?.rationale || '',
        criteria_notes: oa.opportunity_assessment?.criteria_notes || {},
      };
      const r = await axios.post(
        `${API}/viatris/assets/${eaId}/opportunity-assessment`,
        payload, { withCredentials: true },
      );
      setOa((o) => ({ ...o, opportunity_assessment: r.data.opportunity_assessment }));
      toast.success('Opportunity Assessment saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setOaSaving(false);
    }
  };

  const generateOverall = async () => {
    if (!eaId) return;
    setGenLoading(true);
    try {
      const r = await axios.post(
        `${API}/viatris/assets/${eaId}/generate-overall-recommendation`, {},
        { withCredentials: true },
      );
      setOverall(r.data);
      toast.success('Overall Recommendation generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally {
      setGenLoading(false);
    }
  };

  const oppScore = oa?.opportunity_assessment?.score;
  const sciScore = assessment.composite_score;
  const overallScore = (sciScore || 0) + (oppScore || 0);

  // Weighted mean score (1-5) — computed from the 9-domain composite
  // divided by 9. This is now the SINGLE headline number on the AI
  // Assessment modal (Feb 2026 · user request — FRx Opportunity /5
  // and Overall /50 were retired earlier; the old Scientific /45
  // composite is derived data so we surface the weighted mean here
  // for consistency with the Recommendation column tooltip).
  const weightedScore = sciScore != null ? sciScore / 9 : null;

  return (
    <div className="space-y-3">
      {/* Header — Weighted mean score + AI Tier only (Feb 2026 · removed
          Scientific /45 raw composite, FRx Opportunity /5, and Overall /50
          per user request; the weighted mean is the single source of truth
          matching the Recommendation column tooltip). */}
      <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 rounded-md" data-testid="tri-score-header">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 inline-flex items-center gap-1">
            Weighted Score
            <InfoTip wide text={COMPOSITE_SCORE_TOOLTIP} />
          </div>
          <div className="text-2xl font-bold text-slate-800" data-testid="weighted-score-value">
            {weightedScore != null ? weightedScore.toFixed(2) : '—'}
            <span className="text-xs font-normal text-slate-500"> / 5</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">AI Tier</div>
          <Badge className={`${RECOMMENDATION_COLORS[assessment.recommendation] || 'bg-slate-100 text-slate-800'} text-sm mt-1`}>
            {assessment.recommendation || '—'}
          </Badge>
        </div>
      </div>

      {/* Overall Recommendation panel — Claude-generated 6-state verdict */}
      <div className="p-3 border border-emerald-200 bg-emerald-50/40 rounded-md" data-testid="overall-recommendation-panel">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs font-semibold text-emerald-900 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Overall Recommendation
          </div>
          <Button size="sm" onClick={generateOverall} disabled={genLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]" data-testid="generate-overall-btn">
            {genLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
            {overall ? 'Regenerate' : 'Generate with Claude'}
          </Button>
        </div>
        {overall ? (
          <div className="space-y-1.5 text-xs">
            <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 text-sm">{overall.state}</Badge>
            <div className="text-slate-700 leading-relaxed">{overall.rationale}</div>
            {(overall.key_drivers || []).length > 0 && (
              <div><strong className="text-[10px] uppercase tracking-wide text-slate-500">Key drivers:</strong> {overall.key_drivers.join(' · ')}</div>
            )}
            {(overall.key_risks || []).length > 0 && (
              <div><strong className="text-[10px] uppercase tracking-wide text-rose-700">Key risks:</strong> {overall.key_risks.join(' · ')}</div>
            )}
            {(overall.suggested_next_steps || []).length > 0 && (
              <div><strong className="text-[10px] uppercase tracking-wide text-indigo-700">Next steps:</strong> {overall.suggested_next_steps.join(' · ')}</div>
            )}
          </div>
        ) : (
          <div className="text-[11px] text-slate-500 italic">
            No overall recommendation yet. Generate one once the Opportunity Assessment is filled in.
          </div>
        )}
      </div>

      {/* Opportunity Assessment editor — internal-only */}
      <details className="border border-violet-200 rounded-md overflow-hidden" data-testid="opportunity-editor">
        <summary className="px-3 py-2 bg-violet-50 cursor-pointer text-xs font-semibold text-violet-900 hover:bg-violet-100 flex items-center gap-2">
          <Beaker className="w-4 h-4 text-violet-600" /> ForesightRx Opportunity Assessment (1–5)
          <span className="ml-auto text-[10px] text-violet-700 font-normal">
            {oa?.opportunity_assessment?.updated_at
              ? `Last updated ${(oa.opportunity_assessment.updated_at || '').slice(0, 10)}`
              : 'Never edited'}
          </span>
        </summary>
        <div className="p-3 space-y-2.5 text-xs">
          {oaLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          ) : oa ? (
            <>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wide text-slate-500 w-32">Score (1–5)</span>
                <select
                  value={oa.opportunity_assessment?.score || 3}
                  onChange={(e) => setOa((o) => ({ ...o, opportunity_assessment: { ...o.opportunity_assessment, score: Number(e.target.value) } }))}
                  className="text-xs border border-violet-300 rounded px-2 py-1 bg-white"
                  data-testid="oa-score-input"
                >
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} / 5</option>)}
                </select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Rationale</div>
                <textarea
                  value={oa.opportunity_assessment?.rationale || ''}
                  onChange={(e) => setOa((o) => ({ ...o, opportunity_assessment: { ...o.opportunity_assessment, rationale: e.target.value } }))}
                  rows={2}
                  className="w-full border border-slate-200 rounded p-2 text-xs"
                  placeholder="Why this Opportunity score — rights, partners, BD signals…"
                  data-testid="oa-rationale-input"
                />
              </div>
              <details>
                <summary className="text-[10px] uppercase tracking-wide text-slate-500 cursor-pointer">
                  Criterion-level notes (optional)
                </summary>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {(oa.criteria_keys || []).map((k) => (
                    <div key={k}>
                      <div className="text-[10px] text-slate-600 mb-0.5">{(oa.criteria_labels || {})[k] || k}</div>
                      <input
                        type="text"
                        value={(oa.opportunity_assessment?.criteria_notes || {})[k] || ''}
                        onChange={(e) => setOa((o) => ({
                          ...o,
                          opportunity_assessment: {
                            ...o.opportunity_assessment,
                            criteria_notes: { ...(o.opportunity_assessment?.criteria_notes || {}), [k]: e.target.value },
                          },
                        }))}
                        className="w-full border border-slate-200 rounded px-2 py-1 text-xs"
                        data-testid={`oa-criterion-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </details>
              <Button size="sm" onClick={saveOa} disabled={oaSaving} className="bg-violet-600 hover:bg-violet-700 text-white h-7 text-[11px]" data-testid="oa-save-btn">
                {oaSaving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                Save Opportunity Assessment
              </Button>
            </>
          ) : null}
        </div>
      </details>

      {empty && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          Research sections are empty — the web-research API may be unavailable. Claude still produced a baseline scoring/exec summary from CRM data alone. Re-run after restoring API quota for full enrichment.
        </div>
      )}

      {sections.map(({ key, title, icon: Icon }) => {
        const data = assessment[key];
        if (!data || Object.keys(data).length === 0) return null;
        return (
          <details key={key} className="border border-slate-200 rounded-md overflow-hidden" open={key === 'executive_summary' || key === 'scoring'}>
            <summary className="px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-800 hover:bg-slate-100 flex items-center gap-2">
              <Icon className="w-4 h-4 text-violet-600" /> {title}
            </summary>
            <div className="p-3 text-xs space-y-1.5">
              {Object.entries(data).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{k.replace(/_/g, ' ')}</div>
                  <div className="text-slate-700">
                    {typeof v === 'object' && v !== null
                      ? (
                        <span>
                          {v.score != null && <Badge className="bg-violet-100 text-violet-800 text-[10px] mr-1">Score: {v.score}/5</Badge>}
                          {v.confidence && <Badge className="bg-slate-100 text-slate-700 text-[10px] mr-1">{v.confidence}</Badge>}
                          {v.rationale || JSON.stringify(v)}
                        </span>
                      )
                      : Array.isArray(v)
                        ? <ul className="list-disc pl-4 text-slate-700">{v.map((x, i) => <li key={i}>{x}</li>)}</ul>
                        : <span>{String(v || '—')}</span>
                    }
                  </div>
                </div>
              ))}
            </div>
          </details>
        );
      })}

      {assessment.change_log && assessment.change_log.length > 0 && (
        <details className="border border-slate-200 rounded-md overflow-hidden">
          <summary className="px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-800 hover:bg-slate-100 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-violet-600" /> Change Log ({assessment.change_log.length})
          </summary>
          <div className="p-3 space-y-2">
            {assessment.change_log.map((e) => (
              <div key={e.id} className="text-xs border-l-2 border-slate-200 pl-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[9px]">{e.category}</Badge>
                  <span className="text-[10px] text-slate-400">{new Date(e.logged_at).toLocaleDateString()}</span>
                  {e.auto_generated && <Badge className="bg-violet-50 text-violet-700 text-[9px]">AI</Badge>}
                </div>
                <div className="text-slate-700 mt-0.5">{e.summary}</div>
              </div>
            ))}
          </div>
        </details>
      )}

      {assessment.references && assessment.references.length > 0 && (
        <details className="border border-slate-200 rounded-md overflow-hidden">
          <summary className="px-3 py-2 bg-slate-50 cursor-pointer text-xs font-semibold text-slate-800 hover:bg-slate-100 flex items-center gap-2">
            <FileText className="w-4 h-4 text-violet-600" /> References ({assessment.references.length})
          </summary>
          <div className="p-3 space-y-1">
            {assessment.references.map((ref, i) => (
              <a key={i} href={ref} target="_blank" rel="noreferrer"
                 className="block text-[11px] text-blue-700 hover:underline truncate">
                {i + 1}. {ref}
              </a>
            ))}
          </div>
        </details>
      )}

      {assessment.version_history && assessment.version_history.length > 1 && (
        <div className="text-[10px] text-slate-500 pt-2 border-t">
          <strong>Version history:</strong>{' '}
          {assessment.version_history.map((v, i) => (
            <span key={v.version}>
              {i > 0 && ' · '}
              v{v.version} ({v.composite_score?.toFixed(1) ?? '—'}, {v.recommendation || '—'})
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Cohort Reports Panel — appears when a cohort filter is active.
// Surfaces cohort-level counters, exec summary, comparison vs prior cohort,
// and report download buttons.
function CohortReportsPanel({ cohortName, taPriorityOnly, phase3Plus, onChange, activeRecommendation, onTierClick, rdCounts, respondedCount, respondedActive, onRespondedClick }) {
  const [dash, setDash] = useState(null);
  const [rd, setRd] = useState(null);  // universe-wide Recommendation Distribution (source of truth)
  // Defined BEFORE any function that closes over it — a previous
  // placement after the early `if (!dash) return null` left rdData in
  // TDZ for closures created on the first render, which made the
  // event-bus "Run AI Assessment" handler throw ReferenceError and the
  // button silently do nothing (user report: "button not functioning").
  const rdData = rdCounts || rd;
  const [loading, setLoading] = useState(true);
  const [showReports, setShowReports] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [downloading, setDownloading] = useState('');
  const [generatingExec, setGeneratingExec] = useState(false);
  const [locking, setLocking] = useState(false);
  const [lriComputing, setLriComputing] = useState(false);
  const [lriWidgets, setLriWidgets] = useState(null);
  const [batchAssessing, setBatchAssessing] = useState(false);
  const [assessJob, setAssessJob] = useState(null);
  // Feb 2026 · Batch Preview — shows exactly what the next Phase 3+
  // batch would queue and why every other row is skipped. Opened from
  // the pipeline "Preview batch" chip. No side effects — GET only.
  const [batchPreviewOpen, setBatchPreviewOpen] = useState(false);
  const [batchPreviewData, setBatchPreviewData] = useState(null);
  const [batchPreviewLoading, setBatchPreviewLoading] = useState(false);
  const [batchPreviewError, setBatchPreviewError] = useState(null);
  const [batchPreviewFilter, setBatchPreviewFilter] = useState('');
  const [batchPreviewShowOffPhase, setBatchPreviewShowOffPhase] = useState(false);
  // Feb 2026 · Error-samples inspector — click the "N errors" chip
  // during a batch run to see the actual failure messages instead of
  // just a red count. Reads `assessJob.error_samples` populated by the
  // backend (`viatris_assessment_jobs.error_samples` array, last 20).
  const [errorSamplesOpen, setErrorSamplesOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanJob, setScanJob] = useState(null);
  const [alertCounts, setAlertCounts] = useState({ Critical: 0, High: 0, total: 0 });
  const [showAlerts, setShowAlerts] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  // Files published to the Viatris client portal Reports Library.
  const [publishedFiles, setPublishedFiles] = useState([]);
  const [uploadingPub, setUploadingPub] = useState(false);
  const [pubLabel, setPubLabel] = useState('');

  const loadPublishedFiles = useCallback(async () => {
    if (!cohortName) return;
    try {
      const r = await axios.get(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/files`,
        { withCredentials: true },
      );
      setPublishedFiles(r.data?.files || []);
    } catch (e) {
      setPublishedFiles([]);
    }
  }, [cohortName]);

  useEffect(() => { loadPublishedFiles(); }, [loadPublishedFiles]);

  const uploadPublishedFile = async (file) => {
    if (!file || !cohortName) return;
    setUploadingPub(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (pubLabel) fd.append('label', pubLabel);
      await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/files`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setPubLabel('');
      await loadPublishedFiles();
      toast.success('Published to Viatris portal');
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploadingPub(false);
    }
  };

  const deletePublishedFile = async (fileId) => {
    if (!window.confirm('Remove this file from the Viatris portal?')) return;
    try {
      await axios.delete(
        `${API}/viatris/cohort-files/${fileId}`,
        { withCredentials: true },
      );
      await loadPublishedFiles();
      toast.success('Removed from Viatris portal');
    } catch (e) {
      toast.error('Remove failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  // Poll scan status whenever a scan is in flight so the user sees
  // "Scanning N / M · K alerts" tick up live.
  const pollScanStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/viatris/alerts/scan-status/${encodeURIComponent(cohortName)}`,
        { withCredentials: true },
      );
      setScanJob(r.data);
      return r.data?.status;
    } catch {
      return null;
    }
  }, [cohortName]);

  useEffect(() => { pollScanStatus(); }, [pollScanStatus]);

  useEffect(() => {
    if (scanJob?.status !== 'running') return;
    const id = setInterval(async () => {
      const status = await pollScanStatus();
      if (status === 'done' || status === 'idle') {
        clearInterval(id);
        setScanning(false);
        toast.success(`Scan complete · ${scanJob?.new_alerts || 0} new alerts found`);
        await loadAlertCounts();
      }
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanJob?.status, cohortName]);

  const loadAlertCounts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/viatris/alerts`, {
        params: { cohort: cohortName, days: 90, limit: 1 }, withCredentials: true,
      });
      setAlertCounts(r.data?.counts || {});
    } catch { /* silent */ }
  }, [cohortName]);

  useEffect(() => { loadAlertCounts(); }, [loadAlertCounts]);

  const openAlerts = async () => {
    setShowAlerts(true);
    setLoadingAlerts(true);
    try {
      const r = await axios.get(`${API}/viatris/alerts`, {
        params: { cohort: cohortName, days: 90, limit: 100 }, withCredentials: true,
      });
      setAlerts(r.data?.alerts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load alerts');
    } finally {
      setLoadingAlerts(false);
    }
  };

  const scanCohortAlerts = async () => {
    if (!window.confirm(`Scan ${cohortName} for new developments? This uses Perplexity research and may take 5-10 minutes for ~50-250 companies.\n\nNote: scanning creates ALERTS (news, regulatory, funding). It does NOT run AI Assessment on assets — use the "Generate AI Assessment" button or the per-asset row button for that.`)) return;
    setScanning(true);
    try {
      await axios.post(
        `${API}/viatris/alerts/cohorts/${encodeURIComponent(cohortName)}/scan?lookback_days=90&limit=300`,
        {}, { withCredentials: true },
      );
      toast.success('Scan started — live progress will appear in the Scan button');
      await pollScanStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue scan');
      setScanning(false);
    }
  };

  const restoreCommitteeDecisions = async () => {
    if (!window.confirm('Restore lost committee decisions? Scans the audit log for approved/rejected marks whose status was later lost (blank) and restores them. Rows with an explicit current status are never touched.')) return;
    try {
      const r = await axios.post(
        `${API}/viatris/committee/reconcile-decisions?dry_run=false`,
        {}, { withCredentials: true },
      );
      const n = r.data?.restored_count || 0;
      if (n > 0) {
        const names = (r.data?.restored || []).slice(0, 5).map((x) => `${x.company} · ${x.asset_name} → ${x.restored_decision}`).join('; ');
        toast.success(`Restored ${n} committee decision(s): ${names}${n > 5 ? '…' : ''}`, { duration: 12000 });
        window.dispatchEvent(new CustomEvent('viatris:pipeline-action', { detail: { action: 'refresh_data' } }));
      } else {
        toast.info('No lost committee decisions found — everything already consistent.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    }
  };

  const reconcileCohort = async () => {
    if (!window.confirm(`Reconcile cohort membership? This re-tags any assets marked "in active cohort" that are missing a cohort name, so the top "Current Cohort" counter (250) and the cohort dashboard "Total" counter agree.`)) return;
    try {
      const r = await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/reconcile`,
        {}, { withCredentials: true },
      );
      toast.success(`Reconciled ${r.data?.reconciled} asset(s) into ${cohortName}`);
      if (onChange) onChange();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to reconcile');
    }
  };

  const updateAlertStatus = async (alertId, newStatus) => {
    try {
      await axios.patch(`${API}/viatris/alerts/${alertId}`, { status: newStatus }, { withCredentials: true });
      setAlerts((prev) => prev.map((a) => a.id === alertId ? { ...a, status: newStatus } : a));
      await loadAlertCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update alert');
    }
  };


  const syncUniverseEligibleToCohort = async () => {
    if (!cohortName) return;
    if (!window.confirm(
      `Run the deterministic screening engine and sync eligible assets into ${cohortName}?\n\n` +
      `Starting from the refreshed Stage 1 China Universe, this applies the configured Screening Rules in sequence:\n` +
      `  Stage 2 · Phase filter → Stage 3 · TA exclusions → Stage 4 · Modality exclusions → Stage 5 · Asset type / Licensing / Keyword rules.\n\n` +
      `• Adds every eligible asset missing from this cohort.\n` +
      `• Flips rows that no longer qualify to Not Suitable (with the exact rule that fired) and restores rows that qualify again.\n` +
      `• Resets stale "Enriched" flags.\n` +
      `• 100% deterministic — no AI runs. AI Assessment only starts when you click Run AI Assessment.`
    )) return;
    try {
      toast.loading('Running deterministic screening engine…', { id: 'sync-univ' });
      const r = await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/sync-universe-eligible?country=China`,
        {}, { withCredentials: true },
      );
      const d = r.data || {};
      const f = d.funnel || {};
      const funnelLine = [
        `Universe ${f.stage1_universe ?? '—'}`,
        `Phase ${f.stage2_after_phase ?? '—'}`,
        `TA ${f.stage3_after_ta ?? '—'}`,
        `Modality ${f.stage4_after_modality ?? '—'}`,
        `Eligible ${f.stage5_eligible ?? d.universe_eligible_total ?? '—'}`,
      ].join(' → ');
      toast.success(
        `Screening funnel: ${funnelLine}\n` +
        `Added ${d.inserted_into_cohort || 0} to cohort · ${d.excluded_new || 0} newly excluded · ` +
        `${d.restored || 0} restored · ${d.stale_enriched_reset || 0} stale flag(s) reset.\n` +
        `${d.eligible_in_cohort_after || 0} asset(s) ready for AI Assessment.`,
        { id: 'sync-univ', duration: 12000, closeButton: true },
      );
      if (onChange) onChange();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to sync universe-eligible assets', { id: 'sync-univ' });
    }
  };


  const generateBatchAssessment = async (opts = {}) => {
    const phaseFilter = opts.phaseFilter || ''; // 'phase3plus' scopes to late-stage rows only
    // Jul 2026 · Run AI Assessment now runs ENGAGEMENT-WIDE over the
    // full China universe (cohort concept removed). Queues every
    // screening-eligible China asset that lacks a real recommendation.
    const cohortScope = 'engagement';
    const scopeLabel = phaseFilter === 'phase3plus'
      ? 'Phase 3+ / Filed / Approved / Marketed rows'
      : 'screening-eligible';
    // Feb 2026 v18 · use the new `eligible_pending` counter (mirrors
    // the batch's screening_eligible_only guard) so the confirm dialog
    // shows the true asset count that will consume AI credits, not
    // just "pending_assessment" which used to include Not Suitable rows.
    const eligiblePending = dash?.counters?.eligible_pending ?? dash?.eligible_pending ?? 0;
    const pending = dash?.counters?.pending_assessment ?? dash?.pending_assessment ?? 0;
    const total = dash?.counters?.total_assets ?? dash?.total_assets ?? 0;
    // Jul 2026 · prefer the universe-wide "Awaiting AI" count so the
    // confirm dialog reflects the true number of China-universe assets
    // that still need a recommendation (not the cohort subset).
    const n = (phaseFilter === 'phase3plus')
      ? (eligiblePending || pending || total)
      : opts.rerunAll
        ? (rdData?.all ?? eligiblePending ?? total ?? 0)
        : opts.retryCna
          ? (rdData?.could_not_assess ?? 0)
          : (rdData?.awaiting_ai ?? eligiblePending ?? pending ?? total ?? 0);
    // Jul 2026 · short-circuit when nothing is awaiting — clicking "Run AI
    // Assessment" with Awaiting AI = 0 used to spin up a server job that
    // queued nothing, leaving the operator staring at a silent screen
    // (user report: "no action happening"). Tell them immediately and
    // point at the Retry CNA button which is the actual next action.
    if (!opts.force && !opts.retryCna && !opts.rerunAll && phaseFilter === '' && n === 0) {
      const cna = rdData?.could_not_assess ?? 0;
      toast.info(
        `Nothing to assess — 0 assets are Awaiting AI.` +
        (cna > 0
          ? ` The ${cna} "Could Not Assess" asset(s) are only re-run via the "Retry Could Not Assess (${cna})" button.`
          : ` Every eligible asset already has a recommendation.`),
        { duration: 15000, closeButton: true },
      );
      return;
    }
    const confirmHead = opts.rerunAll
      ? `RE-RUN AI on ALL ${n} eligible asset(s)?\n\n` +
        `⚠ This regenerates EVERY assessment with the hardened grounded\n` +
        `pipeline — scores and tiers CAN change. Assets may move between\n` +
        `buckets (High / Medium / Monitor / …) and enter or leave the\n` +
        `Committee Review queue. Reviewer drafts are protected — reviewers\n` +
        `see a "newer AI version" banner and choose when to reset.\n\n` +
        `▶ RESUME-SAFE: assets already re-assessed in the last 24h are\n` +
        `skipped automatically, so if a run was interrupted you can click\n` +
        `this again and it continues with the PENDING assets only.\n\n`
      : opts.retryCna
        ? `Retry AI Assessment on ${n} Could-Not-Assess asset(s)?\n\n` +
          `These assets failed previous runs (see the ⓘ on each row for the\n` +
          `reason). This retries them once more from scratch.\n\n`
        : `Run AI Assessment on ${n} ${scopeLabel} asset(s) across the China universe?\n\n`;
    if (!window.confirm(
      confirmHead +
      `This calls Perplexity + Claude for each asset (8-step pipeline).\n` +
      `Concurrency: 3 in-flight · per-asset hard ceiling: 8 min.\n` +
      `Expected duration: ~${Math.max(5, Math.round(n / 3 * 75 / 60))} min for ${n} assets.\n\n` +
      `Screening-excluded rows (Not Suitable / failed screening) are\n` +
      `automatically skipped — only assets that passed the deterministic\n` +
      `Screening Funnel will consume AI credits. Already-enriched assets\n` +
      `are skipped. Any "stuck Enriching" rows from a previous dead\n` +
      `batch are auto-reverted before requeuing.`
    )) return;
    setBatchAssessing(true);
    // Show the progress bar IMMEDIATELY with optimistic state so the user
    // sees feedback even before the first poll round-trip completes.
    setAssessJob({
      status: 'preparing',
      total: 0,
      completed: 0,
      errors: 0,
      progress_pct: 0,
      current_asset_name: null,
    });
    try {
      const url = `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/generate-batch`
        + `?limit=300&only_unassessed=true`
        + (phaseFilter ? `&phase_filter=${encodeURIComponent(phaseFilter)}` : '')
        + (cohortScope ? `&cohort_scope=${encodeURIComponent(cohortScope)}` : '')
        + (opts.retryCna ? `&retry_cna=1` : '')
        + ((opts.force || opts.rerunAll) ? `&force=true` : '');
      // Jul 2026 · HTTP 520 fix — the POST now returns instantly with
      // status='preparing'; candidate selection + queueing happen inside
      // the background job. Queued count, sync summary and zero-queued
      // diagnostics all arrive via the /assessment-status poller (the
      // job doc carries them), so nothing is read from this response
      // beyond the ack. This keeps production clicks well under the
      // Cloudflare edge timeout that used to surface 520 toasts.
      const r = await axios.post(url, {}, { withCredentials: true, timeout: 45000 });
      if (r.data?.status === 'preparing') {
        toast.info(
          'Batch accepted — the server is selecting eligible assets now. ' +
          'The progress bar below updates automatically.',
          { duration: 8000 },
        );
      }
      // Kick off polling immediately so the progress strip updates.
      await pollAssessStatus();
      if (onChange) onChange();
      await load();
    } catch (e) {
      // Roll back the optimistic state on failure.
      setAssessJob({ status: 'idle', cohort_name: cohortName });
      const detail = e?.response?.data?.detail || e?.message || 'Failed to queue AI Assessment batch';
      const status = e?.response?.status;
      // Persistent error toast — sonner default auto-dismisses too fast
      // for users to read multi-line backend messages. 60s + closeButton
      // gives the operator time to copy/paste it back to the agent.
      const isEdge = [502, 503, 504, 520, 521, 522, 524].includes(status) || /cloudflare/i.test(String(detail));
      toast.error(
        isEdge
          ? `[HTTP ${status}] The production server didn't answer (it may be restarting or busy with a running batch). No batch was started — wait ~30 seconds and click again.`
          : `[HTTP ${status || '?'}] ${detail}`,
        { duration: 60000, closeButton: true },
      );
      // Also log to console for DevTools debugging — keeps a paper trail
      // even after the toast is dismissed.
      // eslint-disable-next-line no-console
      console.error('[viatris] generateBatchAssessment failed', { status, detail, error: e });
    } finally {
      setBatchAssessing(false);
    }
  };

  // Feb 2026 · read-only batch preview. Hits the diagnostic endpoint
  // and opens a modal showing exactly which rows would be queued and
  // WHY every other row is skipped. Zero side effects — no LLM calls,
  // no state mutations on engagement_assets. Wired to the "Preview
  // batch" pipeline chip so the operator never fires a blind batch.
  const openBatchPreview = async ({ phaseFilter = 'phase3plus', cohortScope = 'engagement' } = {}) => {
    if (!cohortName) {
      toast.info('Select a cohort first to preview the batch.');
      return;
    }
    setBatchPreviewOpen(true);
    setBatchPreviewLoading(true);
    setBatchPreviewError(null);
    setBatchPreviewData(null);
    try {
      const url = `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/batch-preview`
        + `?phase_filter=${encodeURIComponent(phaseFilter)}`
        + `&cohort_scope=${encodeURIComponent(cohortScope)}`
        + `&only_unassessed=true&limit=1000`;
      const r = await axios.get(url, { withCredentials: true });
      setBatchPreviewData(r.data);
    } catch (e) {
      setBatchPreviewError(e?.response?.data?.detail || e?.message || 'Failed to load batch preview');
    } finally {
      setBatchPreviewLoading(false);
    }
  };

  // Polling circuit breaker — when the production backend pod is
  // unresponsive (HTTP 520/5xx), back off exponentially so we don't
  // hammer it with status pings every 4s while it's struggling.
  const pollFailRef = React.useRef(0);
  // Track whether we've EVER seen a running job. Suppresses the scary
  // "stuck pod" toast on a fresh page load where polling just happens
  // to fail during cold-start — if no batch is actually expected to
  // be running, repeated poll failures are not the user's problem.
  const sawRunningJobRef = React.useRef(false);
  const pollAssessStatus = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/assessment-status`,
        { withCredentials: true, timeout: 15000 },
      );
      pollFailRef.current = 0;
      if (r.data?.status === 'running') sawRunningJobRef.current = true;
      setAssessJob(r.data);
      return r.data?.status;
    } catch (e) {
      pollFailRef.current += 1;
      // After 3 consecutive failures AND we've actually seen a running
      // job, surface a notice so the operator knows polling has paused.
      // Otherwise these are just cold-start hiccups and not actionable.
      if (pollFailRef.current === 3 && sawRunningJobRef.current) {
        toast.error(
          'Backend not responding (likely a stuck pod). Polling paused. ' +
          'Try the "Force Reset" button below or redeploy production.',
          { duration: 30000, closeButton: true },
        );
      }
      return null;
    }
  }, [cohortName]);

  // Admin force-reset — flips all `running` viatris_assessment_jobs to
  // status='error' AND reverts every `Enriching` engagement_asset to
  // `Not Started` (across all cohorts). Useful when a deploy killed
  // in-flight batch tasks and left zombie state behind.
  const [resetting, setResetting] = useState(false);
  const forceResetStuckJobs = async () => {
    if (!window.confirm(
      'Force-reset ALL stuck AI Assessment jobs?\n\n' +
      'This marks every "running" job in the database as "error" and ' +
      'reverts every "Enriching" row back to "Not Started" across all ' +
      'cohorts. Use this if Stop isn\'t working or the backend is hung. ' +
      'Already-Enriched assets are not affected.'
    )) return;
    setResetting(true);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/reset-stuck-jobs`, {},
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(
        `Reset complete · ${r.data?.jobs_marked_error || 0} job(s) marked error · ` +
        `${r.data?.rows_reverted_to_not_started || 0} row(s) reverted to Not Started`,
        { duration: 15000 },
      );
      // Immediately clear the stale "Stop / 91 min / 0%" strip — don't wait
      // for the poll roundtrip (which may also time out if pod is busy).
      // User just confirmed nothing is running; clearing locally is safe.
      setAssessJob(null);
      pollFailRef.current = 0;
      // Best-effort poll refresh — if it succeeds we get fresh state, if it
      // fails the user already sees a clean UI thanks to the setAssessJob(null) above.
      pollAssessStatus().catch(() => {});
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Reset failed';
      toast.error(`Force reset failed: ${detail}`, { duration: 60000, closeButton: true });
    } finally {
      setResetting(false);
    }
  };

  // Retry every "Could Not Assess" row by resetting them to Not Started
  // so the next "Generate AI Assessment" batch picks them up cleanly.
  // Needed when `Fix null scores` was already run but the rows kept
  // their old `ai_assessment_status=Enriched` flag and are now ignored
  // by the only_unassessed filter.
  const [retryingCNA, setRetryingCNA] = useState(false);
  const retryCouldNotAssess = async () => {
    if (!window.confirm(
      'Retry all "Could Not Assess" rows?\n\n' +
      'Every CNA asset will be reset to "Not Started" so the next ' +
      '"Generate AI Assessment" batch picks them up. Safe — does not ' +
      'touch already-scored rows.'
    )) return;
    setRetryingCNA(true);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/retry-could-not-assess`, {},
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(
        `${r.data?.reset_to_not_started || 0} CNA row(s) reset · click "Generate AI Assessment" to re-run them`,
        { duration: 12000 },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Retry failed';
      const status = e?.response?.status;
      const isEdge = [502, 503, 504, 520, 521, 522, 524].includes(status) || /cloudflare/i.test(String(detail));
      toast.error(
        isEdge
          ? `Retry CNA failed: the production server didn't answer (it may be restarting or busy with a running batch). Nothing was reset — wait ~30 seconds and click again.`
          : `Retry CNA failed: ${detail}`,
        { duration: 30000, closeButton: true },
      );
    } finally {
      setRetryingCNA(false);
    }
  };

  // Jul 2026 · Surgical repair for assets stuck mid-pipeline. Resumes
  // from the first incomplete stage: orphaned rows (complete assessment
  // but blank recommendation → "Awaiting AI") are reconciled with 0 LLM
  // calls; CNA rows with Perplexity research but no Claude output re-run
  // only the 2 Claude steps. Preserves all completed work.
  const [repairing, setRepairing] = useState(false);
  const repairIncomplete = async () => {
    setRepairing(true);
    try {
      // 1) Dry-run first so the operator sees exactly what will happen
      //    (and how many will spend LLM tokens) before confirming.
      const preview = await axios.post(
        `${API}/viatris/admin/repair-incomplete`, { dry_run: true, limit: 500 },
        { withCredentials: true, timeout: 60000 },
      );
      const p = preview.data || {};
      if (!p.candidates) {
        toast.info('Nothing to repair — every assessed asset already has a recommendation.', { duration: 8000 });
        return;
      }
      const ok = window.confirm(
        `Repair ${p.candidates} incomplete asset(s)?\n\n` +
        `• ${p.reconcile_only} will be RECONCILED (assessment already complete, ` +
        `just needs its recommendation written back) — 0 AI cost, instant.\n` +
        `• ${p.resume_claude} will RESUME the Claude scoring/summary step ` +
        `(research already done) — ~2 AI calls each.\n` +
        (p.needs_full_run ? `• ${p.needs_full_run} have no saved work and are skipped (use Run AI Assessment).\n` : '') +
        `\nAll completed work is preserved. Nothing is re-fetched from Perplexity.`
      );
      if (!ok) return;
      // 2) Execute.
      const r = await axios.post(
        `${API}/viatris/admin/repair-incomplete`, { dry_run: false, limit: 500 },
        { withCredentials: true, timeout: 300000 },
      );
      const s = r.data?.summary || {};
      const parts = Object.entries(s).map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`);
      toast.success(
        `Repair complete · ${r.data?.processed || 0} processed — ${parts.join(', ') || 'no changes'}`,
        { duration: 15000, closeButton: true },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Repair failed';
      const status = e?.response?.status;
      const isEdge = [502, 503, 504, 520, 521, 522, 524].includes(status) || /cloudflare/i.test(String(detail));
      toast.error(
        isEdge
          ? `Repair failed: the server didn't answer (busy or restarting). Some rows may still have been repaired — refresh and re-run to finish the rest.`
          : `Repair failed: ${detail}`,
        { duration: 30000, closeButton: true },
      );
    } finally {
      setRepairing(false);
    }
  };

  // Retroactive Fit Screen application (Feb 2026 v2) — walks every
  // engagement_asset and applies the Phase 3+ / TA rules WITHOUT
  // spending any LLM tokens. Used to backfill the Fit Screen on
  // assessments produced before the Feb 2026 v2 pipeline landed.
  const [applyingFitScreen, setApplyingFitScreen] = useState(false);
  const applyFitScreen = async () => {
    const reset = window.confirm(
      'Apply Fit Screen retroactively?\n\n' +
      'Click OK to RESET & RE-APPLY (recommended after regex changes):\n' +
      '  • First un-flips every previously auto-classified row\n' +
      '  • Then re-applies the current Phase 3+ / TA rules cleanly\n\n' +
      'Click CANCEL to skip.\n\n' +
      'Zero LLM cost. ~10-30 seconds for a 250-asset cohort.'
    );
    if (!reset) return;
    setApplyingFitScreen(true);
    try {
      const params = { reset: true };
      if (cohortName) params.cohort = cohortName;
      const r = await axios.post(
        `${API}/viatris/admin/apply-fit-screen`, {},
        { params, withCredentials: true, timeout: 90000 },
      );
      const d = r.data || {};
      toast.success(
        `Fit Screen applied · reset ${d.reset_count || 0} · scanned ${d.scanned || 0} · flipped ${(d.phase_flipped || 0) + (d.ta_flipped || 0)} · unchanged ${d.unchanged || 0}`,
        { duration: 20000, closeButton: true },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Fit Screen apply failed';
      toast.error(`Apply Fit Screen failed: ${detail}`, { duration: 30000, closeButton: true });
    } finally {
      setApplyingFitScreen(false);
    }
  };

  // NUCLEAR — wipes ALL Viatris AI assessments and resets every asset to
  // Not Started. Use only when the previous state is unrecoverable and
  // the user is fine re-spending ~$30 in LLM tokens for a fresh run.
  const [nuking, setNuking] = useState(false);

  // Fix null-score rows — re-labels assets that finished AI assessment
  // but ended up with composite_score=null and a bogus "Monitor"-ish
  // recommendation. They get relabeled to "Could Not Assess" so the
  // operator can re-run them deliberately.
  const [fixingNullScores, setFixingNullScores] = useState(false);
  const fixNullScoreRows = async () => {
    if (!window.confirm(
      'Relabel null-score rows?\n\n' +
      'Finds every asset where AI assessment finished but composite_score ' +
      'is still empty (typically "Monitor" rows with no number). They ' +
      'will be moved to the "Could Not Assess" tier so you can re-run ' +
      'them deliberately. In-flight (Enriching) rows are skipped.'
    )) return;
    setFixingNullScores(true);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/backfill-null-scores`, {},
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(
        `Null-score fix complete · ${r.data?.matched || 0} matched · ${r.data?.updated || 0} relabeled to Could Not Assess`,
        { duration: 12000 },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Fix failed';
      toast.error(`Null-score fix failed: ${detail}`, { duration: 30000, closeButton: true });
    } finally {
      setFixingNullScores(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Monthly ops — Rescore priority tiers (Feb 2026)
  // Re-runs the Claude scoring pass on every asset currently tagged
  // Strategic / High Priority / Medium / Monitor. Skips Low Priority,
  // Not Suitable and Could Not Assess (locked / uninteresting) so a
  // 250-asset cohort only spends Claude tokens on the ~80–120 assets
  // that could still move between tiers. Overrides scores directly.
  // ────────────────────────────────────────────────────────────────
  const PRIORITY_TIERS = ['Strategic Priority', 'High Priority', 'Medium', 'Monitor'];
  const [rescoring, setRescoring] = useState(false);
  const [rescoreSummary, setRescoreSummary] = useState(null); // last run summary
  const rescorePriorityTiers = async () => {
    if (!window.confirm(
      'Re-run AI scoring on priority tiers?\n\n' +
      'Targets every asset currently in Strategic Priority, High Priority, ' +
      'Medium, or Monitor. Skips Low Priority / Not Suitable / Could Not ' +
      'Assess.\n\n' +
      'Uses the latest Viatris rubric and OVERWRITES the existing score, ' +
      'tier and executive summary. History is preserved (new version).\n\n' +
      'Runtime: ~30–45 min for ~120 assets. Cost: ~$8–12 in Claude tokens.'
    )) return;
    setRescoring(true);
    setRescoreSummary(null);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/rescore-all`,
        {},
        {
          withCredentials: true,
          timeout: 3600 * 1000,     // 60 min
          params: {
            cohort: cohortName,
            tiers:  PRIORITY_TIERS.join(','),
          },
        },
      );
      const d = r.data || {};
      setRescoreSummary(d);
      toast.success(
        `Rescore complete · ${d.updated || 0} / ${d.targets || 0} rescored · ` +
        `${d.tier_flips_count || 0} tier flip${d.tier_flips_count === 1 ? '' : 's'}` +
        (d.errors_count ? ` · ${d.errors_count} error(s)` : ''),
        { duration: 20000 },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Rescore failed';
      toast.error(`Rescore failed: ${detail}`, { duration: 30000, closeButton: true });
    } finally {
      setRescoring(false);
    }
  };

  // ────────────────────────────────────────────────────────────────
  // Monthly ops — Fact-Check Audit & Refresh (Feb 2026, upgraded).
  // The button now triggers the full AI Intelligence Refresh Engine
  // (Perplexity company/asset/scientific/competitive intelligence +
  // Claude rescoring + Claude exec summary + reference refresh) for
  // every asset currently in Strategic / High / Medium / Monitor.
  // Manual committee reviewer scores are PRESERVED (they live in a
  // separate collection this pipeline never touches). Progress
  // surfaces through the existing "Assessing X of Y" strip below.
  // ────────────────────────────────────────────────────────────────
  const [factChecking, setFactChecking] = useState(false);
  const runFactCheckAudit = async () => {
    if (!window.confirm(
      'Run Fact-Check Audit — full AI intelligence refresh?\n\n' +
      'Refreshes company, asset, scientific and competitive intelligence ' +
      'from the latest public sources for every asset currently in ' +
      'Strategic Priority, High Priority, Medium and Monitor. Rebuilds the ' +
      'Executive Summary and reapplies the latest Viatris scoring rubric.\n\n' +
      'Manual committee reviewer scores are PRESERVED. Only the AI-authored ' +
      'assessment (score, exec summary, competitive tables, references) is ' +
      'refreshed.\n\n' +
      'Runtime: ~45–75 min for ~120 assets. Cost: ~$25–40 in Claude + ' +
      'Perplexity tokens. Progress shows in the "Assessing X of Y" strip ' +
      'below the toolbar — you can close the tab and check back.'
    )) return;
    setFactChecking(true);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/fact-check-refresh`,
        {},
        {
          withCredentials: true,
          timeout: 120 * 1000,   // 2 min — the POST returns almost instantly
          params: {
            cohort: cohortName,
            tiers:  PRIORITY_TIERS.join(','),
          },
        },
      );
      const d = r.data || {};
      toast.success(
        `Fact-Check Audit queued · ${d.queued || 0} asset${d.queued === 1 ? '' : 's'} refreshing`,
        { duration: 15000 },
      );
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Fact-check refresh failed to queue';
      toast.error(`Fact-Check Audit failed: ${detail}`, { duration: 30000, closeButton: true });
    } finally {
      setFactChecking(false);
    }
  };

  const nuclearResetAllAssessments = async () => {
    // Step 1 — acknowledge what this does.
    if (!window.confirm(
      '⚠️ WIPE ALL AI ASSESSMENTS?\n\n' +
      'This deletes every assessment job, every assessment result, and ' +
      'resets EVERY asset to "Not Started" across the entire Viatris ' +
      'engagement — including the assets that were already enriched.\n\n' +
      'You will need to re-run the AI Assessment from scratch (~$30 in ' +
      'LLM tokens, ~90 min runtime for 250 assets).\n\n' +
      'Use this ONLY when the pipeline is fully wedged and a Force Reset ' +
      'is not enough.\n\nClick OK to proceed to the type-to-confirm step.'
    )) return;

    // Step 2 — strict type-to-confirm. Anything other than the exact
    // string "WIPE ALL" aborts. This is the safety gate that prevents
    // accidental clicks from nuking ~$30 + 90 min of LLM work.
    const REQUIRED = 'WIPE ALL';
    const typed = window.prompt(
      `You are about to PERMANENTLY DELETE every AI assessment, every ` +
      `composite score, every committee-approved decision and every ` +
      `assessment job in the Viatris engagement.\n\n` +
      `This action is IRREVERSIBLE.\n\n` +
      `To proceed, type exactly:\n    ${REQUIRED}\n\n` +
      `(Case-sensitive. Click Cancel to abort.)`,
      '',
    );
    if (typed === null) return; // user clicked Cancel
    if (typed.trim() !== REQUIRED) {
      toast.error(
        `Wipe All aborted — you typed "${typed}" instead of "${REQUIRED}". No data was touched.`,
        { duration: 8000 },
      );
      return;
    }

    setNuking(true);
    try {
      const r = await axios.post(
        `${API}/viatris/admin/nuclear-reset`, {},
        { withCredentials: true, timeout: 60000 },
      );
      toast.success(
        `Wiped clean · ${r.data?.jobs_deleted || 0} job(s), ` +
        `${r.data?.assessments_deleted || 0} assessment(s) deleted · ` +
        `${r.data?.assets_reset || 0} asset(s) reset to Not Started`,
        { duration: 20000 },
      );
      setAssessJob(null);
      pollFailRef.current = 0;
      if (onChange) await onChange();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Nuclear reset failed';
      toast.error(`Wipe All failed: ${detail}`, { duration: 60000, closeButton: true });
    } finally {
      setNuking(false);
    }
  };

  useEffect(() => { pollAssessStatus(); }, [pollAssessStatus]);

  useEffect(() => {
    if (assessJob?.status !== 'running' && assessJob?.status !== 'preparing') return;
    let tick = 0;
    const id = setInterval(async () => {
      // Backoff: skip ticks if backend has been failing — try every
      // 4s normally, every 16s after 3 consecutive failures, every 60s
      // after 6. This prevents the page from DDoSing a stuck pod.
      const fails = pollFailRef.current;
      if (fails >= 6 && tick % 15 !== 0) { tick += 1; return; }
      if (fails >= 3 && tick % 4 !== 0)  { tick += 1; return; }
      const doc = await pollAssessStatus();
      const status = doc?.status;
      tick += 1;
      // Every ~12s (3 ticks), also refresh the cohort dashboard counters
      // so AI DONE / SCORED / etc. stay in sync with the progress bar
      // — not just the headline counter.
      if (tick % 3 === 0 && pollFailRef.current === 0) {
        if (onChange) await onChange();
        await load();
      }
      if (status === 'done' || status === 'idle' || status === 'cancelled' || status === 'error') {
        clearInterval(id);
        if (status === 'cancelled') {
          toast.info(`AI Assessment cancelled · ${doc?.completed || 0} asset(s) completed before stop`);
        } else if (status === 'error') {
          toast.error(
            `AI batch failed on the server: ${doc?.error || 'unknown error'}`,
            { duration: 60000, closeButton: true },
          );
        } else if (status === 'done' && (doc?.total || 0) === 0) {
          // Jul 2026 · zero-queued diagnostics now arrive via the job doc
          // (the POST returns before selection). Same messaging as the old
          // synchronous path, including the Phase 3+ force re-run offer.
          const d = doc?.diagnostics || {};
          const details = Object.keys(d).length
            ? `\n\n· ${d.total_engagement_rows} total engagement rows`
            + `\n· ${d.matched_before_assessed_filter} matched ${d.phase_filter_applied === 'phase3plus' ? 'Phase 3+ / ' + d.query_scope : d.query_scope}`
            + `\n· ${d.excluded_because_already_assessed} already have real assessment docs`
            : '';
          if (doc?.phase_filter === 'phase3plus' && !doc?.force
              && (d.matched_before_assessed_filter || 0) > 0
              && (d.excluded_because_already_assessed || 0) > 0) {
            const forceOk = window.confirm(
              `Nothing queued.${details}\n\n` +
              `Force re-run? This will re-process every Phase 3+ row across the engagement, ignoring any existing assessment docs.\n\n` +
              `Expected duration: ~${Math.max(3, Math.round(d.matched_before_assessed_filter / 3 * 75 / 60))} min for ${d.matched_before_assessed_filter} rows.`
            );
            if (forceOk) {
              generateBatchAssessment({ phaseFilter: 'phase3plus', force: true });
              return;
            }
          }
          toast.warning(
            `Nothing queued for ${cohortName}. ` +
            `Either every asset is already enriched, or none are eligible.${details}`,
            { duration: 30000, closeButton: true },
          );
        } else {
          toast.success(`AI Assessment complete · ${doc?.completed || 0} asset(s) enriched`);
        }
        if (onChange) onChange();
        await load();
      }
    }, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessJob?.status, cohortName]);

  const cancelBatchAssessment = async () => {
    if (!window.confirm(
      `Stop / reset the AI Assessment for ${cohortName}?\n\n` +
      `Any stuck "Enriching" rows will be reverted back to "Not Started" so you can safely re-trigger.\n` +
      `If a batch is actively running, in-flight assets finish but no new ones start.\n\n` +
      `Completed so far: ${assessJob?.completed || 0} of ${assessJob?.total || 0}`
    )) return;
    try {
      const r = await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/cancel-batch`,
        {}, { withCredentials: true },
      );
      toast.success(r.data?.message || 'AI Assessment cancelled');
      // Refresh everything — job state, cohort dashboard counters, AND
      // the inventory table rows (so stuck "Enriching" → "Not Started"
      // is immediately visible without a page refresh).
      await pollAssessStatus();
      if (onChange) await onChange();
      await load();
      // Bonus: force a hard reload of the page after a short delay if
      // the user's browser is showing stale rows. This is a belt-and-
      // braces guarantee they SEE the reset.
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to cancel batch');
    }
  };

  const computeLRIBatch = async () => {
    if (!window.confirm(`Compute LRI for every distinct company in ${cohortName}? This will use Perplexity research and may take ~5-10 minutes for 250 assets.`)) return;
    setLriComputing(true);
    try {
      const r = await axios.post(
        `${API}/viatris/lri/cohorts/${encodeURIComponent(cohortName)}/compute?limit=300&only_missing=true`,
        {}, { withCredentials: true },
      );
      toast.success(`LRI compute queued for ${cohortName} — refresh in a few minutes to see scores`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue LRI compute');
    } finally {
      setLriComputing(false);
    }
  };

  const loadLRIWidgets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/viatris/lri/dashboard/widgets`, {
        params: { cohort: cohortName }, withCredentials: true,
      });
      setLriWidgets(r.data?.widgets);
    } catch { /* silent */ }
  }, [cohortName]);

  useEffect(() => { loadLRIWidgets(); }, [loadLRIWidgets]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Forward the active TA/Phase scope so the panel's
      // Recommendation Distribution + cohort counters stay in sync
      // with the Workbench filter row at the bottom of the page.
      const params = {};
      if (taPriorityOnly) params.ta_priority_only = taPriorityOnly;
      if (phase3Plus) params.phase_3_plus = phase3Plus;
      const r = await axios.get(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/dashboard`,
        { params, withCredentials: true },
      );
      setDash(r.data);
      // Jul 2026 · Recommendation Distribution + AI/committee counters
      // now come from the universe-wide funnel (single source of truth,
      // deduped by asset_id) so tiles + Committee Review always agree
      // with the 135-asset China universe — not the cohort subset.
      try {
        const rp = await axios.get(
          `${API}/viatris/universe-pipeline?country=China`,
          { withCredentials: true },
        );
        setRd(rp.data?.recommendation_distribution || null);
      } catch { /* funnel optional — tiles fall back to cohort counters */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load cohort dashboard');
    } finally {
      setLoading(false);
    }
  }, [cohortName, taPriorityOnly, phase3Plus]);

  useEffect(() => { load(); }, [load]);

  // Jul 2026 · always-fresh action ref — the event-bus handler below
  // only rebinds on [cohortName, locked], so direct closure calls went
  // stale (first-render closures → TDZ ReferenceError → dead buttons).
  // Routing through a ref guarantees the latest render's functions run.
  // NOTE: `.current` is assigned AFTER lockCohortClick is declared
  // further down (const functions are TDZ until their declaration).
  const actionsRef = useRef({});

  // Pipeline action bus (Feb 2026 v2.2) — the Status Pipeline action
  // buttons live in the parent (top-level workbench) but the actual
  // handlers (generateBatchAssessment, setShowReports, lockCohort) sit
  // inside this panel. A lightweight window-scoped CustomEvent bus
  // decouples them without prop-drilling through 20 levels of layout.
  useEffect(() => {
    const handler = (ev) => {
      const action = ev?.detail?.action;
      if (action === 'run_ai_assessment') {
        actionsRef.current.generateBatchAssessment();
      } else if (action === 'retry_could_not_assess') {
        // Jul 2026 · deliberate retry of terminal Could-Not-Assess
        // assets — the ONLY path that re-queues them (normal runs skip
        // them by design so failures don't loop forever).
        actionsRef.current.generateBatchAssessment({ retryCna: true });
      } else if (action === 'rerun_all_ai') {
        // Jun 2026 · explicit "Re-run All AI" — regenerates EVERY eligible
        // asset with the hardened grounded pipeline (force=true bypasses
        // the already-assessed skip). Confirm dialog warns tiers may change.
        actionsRef.current.generateBatchAssessment({ rerunAll: true });
      } else if (action === 'sync_universe_eligible') {
        // Feb 2026 v18c · bulk-inserts universe-eligible assets that
        // are missing from the cohort + resets stale Enriched flags,
        // so Run AI can then queue the full 135.
        actionsRef.current.syncUniverseEligibleToCohort();
      } else if (action === 'run_ai_assessment_phase3plus') {
        // Feb 2026 · user request "at least have deep dives for all Phase 3+ assets".
        actionsRef.current.generateBatchAssessment({ phaseFilter: 'phase3plus' });
      } else if (action === 'preview_batch_phase3plus') {
        // Feb 2026 · read-only diagnostic modal — shows exactly which
        // rows the Phase 3+ batch would queue and WHY every other row
        // is skipped. No side effects, no LLM calls.
        actionsRef.current.openBatchPreview({ phaseFilter: 'phase3plus', cohortScope: 'engagement' });
      } else if (action === 'refresh_pipeline') {
        // No-op here — the top-level ViatrisWorkbenchPage has its own
        // listener for `refresh_pipeline` that invokes the unified
        // orchestrator. Kept as an explicit branch so we don't fall
        // through into the legacy `refresh_universe` handler below.
      } else if (action === 'refresh_universe') {
        // Feb 2026 v3 · "Simplify VIP Screening Pipeline" — Refresh
        // Universe pulls the latest China assets into this cohort.
        // Reuses the existing summary/inventory reload; no LLM call.
        toast.success('Refreshing universe…');
        actionsRef.current.load();
      } else if (action === 'apply_screening_rules') {
        // Feb 2026 v3 · deterministic Phase + TA + Exclusion filter.
        // Fires POST /screening-definition/reapply — no LLM calls, just
        // Mongo writes flipping Not Suitable rows. Polls the progress
        // endpoint for live scan feedback.
        (async () => {
          try {
            toast('Applying screening rules…', { icon: '🔎' });
            const r = await axios.post(
              `${API}/viatris/screening-definition/reapply?cohort=${encodeURIComponent(cohortName || '')}`,
              {},
              { withCredentials: true },
            );
            const taskId = r.data?.task_id;
            if (!taskId) throw new Error('No task_id returned');
            const start = Date.now();
            const poll = setInterval(async () => {
              try {
                const s = await axios.get(`${API}/viatris/screening-refresh/${taskId}`, { withCredentials: true });
                const st = s.data?.status;
                if (st === 'done') {
                  clearInterval(poll);
                  const secs = ((Date.now() - start) / 1000).toFixed(1);
                  toast.success(`Screening applied in ${secs}s — ${s.data.excluded_new || 0} newly excluded, ${s.data.restored || 0} restored.`);
                  actionsRef.current.load();  // refresh counters
                } else if (st === 'error') {
                  clearInterval(poll);
                  toast.error(`Screening refresh failed: ${s.data.error || 'unknown'}`);
                }
              } catch { /* transient — keep polling */ }
            }, 1500);
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Screening reapply failed');
          }
        })();
      } else if (action === 'preview_eligible') {
        // Feb 2026 v3 · read-only preview of the Eligible pool that
        // Run AI Assessment will spend Perplexity/Claude tokens on.
        // Reuses the existing batchPreview modal shell but wires the
        // /pipeline-preview endpoint under the hood (stage=eligible).
        (async () => {
          setBatchPreviewOpen(true);
          setBatchPreviewLoading(true);
          setBatchPreviewError(null);
          setBatchPreviewData(null);
          try {
            const url = `${API}/viatris/pipeline-preview?cohort=${encodeURIComponent(cohortName || '')}&stage=eligible&limit=1000`;
            const r = await axios.get(url, { withCredentials: true });
            const items = r.data?.items || [];
            // Reshape into the {tally, rows} contract that BatchPreviewModal
            // renders. Every row is `would_queue=true` because the
            // /pipeline-preview endpoint only returns eligible rows.
            setBatchPreviewData({
              source: 'pipeline_preview_eligible',
              tally: {
                total_in_scope: r.data?.total || 0,
                would_queue: items.length,
                excluded_off_phase: 0,
                excluded_already_assessed: items.filter(x => (x.assessment_status || '').match(/scored|completed|assessed/i)).length,
                excluded_split_brain_kept: 0,
              },
              rows: items.map((it) => ({
                asset_id: it.id,
                asset_name: it.asset_name,
                company_name: it.biotech_company_name,
                phase: it.phase,
                therapeutic_area: it.therapeutic_area,
                assessment_status: it.assessment_status,
                would_queue: true,
                reason: '',
              })),
            });
          } catch (e) {
            setBatchPreviewError(e?.response?.data?.detail || e?.message || 'Failed to load eligible preview');
          } finally {
            setBatchPreviewLoading(false);
          }
        })();
      } else if (action === 'open_reports' || action === 'generate_reports') {
        setShowReports(true);
      } else if (action === 'deep_dive') {
        // Deep Dive kickoff — the cohort must be locked (client MoM
        // rule). If not yet locked, offer to lock in one click.
        if (!dash?.cohort?.locked) {
          if (window.confirm(
            'Lock this cohort now to unlock Deep Dive?\n\n' +
            'Locking freezes the current committee scores and marks the ' +
            'cohort as "client-finalised". Deep Dive begins on locked ' +
            'assets only.'
          )) {
            actionsRef.current.lockCohortClick();
          }
          return;
        }
        // Cohort locked → open the Reports dialog so the analyst can
        // grab the deep-dive final report (Excel / PowerPoint / PDF).
        // Under the hood these formats reuse the same export routes;
        // once the deep-dive-specific templating lands (P1 backlog),
        // the dialog will surface a distinct "Deep Dive report" pack.
        setShowReports(true);
        toast.success('Deep Dive · locked cohort · pick a download format below');
      }
    };
    window.addEventListener('viatris:pipeline-action', handler);
    return () => window.removeEventListener('viatris:pipeline-action', handler);
    // Handlers close over dash + cohort state — rebind on cohort change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortName, dash?.cohort?.locked]);

  const downloadFile = async (fmt) => {
    setDownloading(fmt);
    try {
      const r = await axios.get(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/export.${fmt}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `viatris_${cohortName.replace(/\s+/g, '_')}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
      await load(); // refresh reports_generated counter
      if (onChange) onChange();
    } catch (e) {
      toast.error(`Failed to download ${fmt}`);
    } finally {
      setDownloading('');
    }
  };

  const regenerateExec = async () => {
    setGeneratingExec(true);
    try {
      await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/exec-summary`,
        {}, { withCredentials: true },
      );
      toast.success('Executive summary regenerated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate');
    } finally {
      setGeneratingExec(false);
    }
  };

  const lockCohortClick = async () => {
    // Bi-directional: locks an unlocked cohort, unlocks a locked one.
    // Both actions audit on the cohort doc server-side.
    const next = !isLocked;
    const promptMsg = next
      ? `Lock ${cohortName}? Client portal scoring becomes read-only and the cohort is snapshotted for historical comparison.`
      : `Unlock ${cohortName}? Client portal scoring will be writable again.`;
    if (!window.confirm(promptMsg)) return;
    setLocking(true);
    try {
      const path = next ? 'lock' : 'unlock';
      const r = await axios.post(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/${path}`,
        {}, { withCredentials: true },
      );
      if (next) {
        if (r.data.locked) {
          toast.success(`Cohort locked · ${r.data.asset_count} assets snapshotted`);
        } else {
          toast.error(r.data.reason || 'Lock failed');
        }
      } else {
        toast.success('Cohort unlocked · client portal writes re-enabled');
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${next ? 'lock' : 'unlock'}`);
    } finally {
      setLocking(false);
    }
  };

  // All event-bus targets are declared by this point — safe to publish
  // the always-fresh action map (re-assigned every render).
  actionsRef.current = {
    generateBatchAssessment,
    syncUniverseEligibleToCohort,
    openBatchPreview,
    lockCohortClick,
    load,
  };

  const openComparison = async () => {
    setShowComparison(true);
    try {
      const r = await axios.get(
        `${API}/viatris/cohorts/${encodeURIComponent(cohortName)}/comparison`,
        { withCredentials: true },
      );
      setComparison(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load comparison');
    }
  };

  if (loading) {
    return (
      <Card className="bg-violet-50/30 border-violet-200">
        <CardContent className="py-3 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading cohort dashboard…
        </CardContent>
      </Card>
    );
  }
  if (!dash) return null;
  const c = dash.counters || {};
  const cohortMeta = dash.cohort || {};
  const isLocked = cohortMeta.locked;
  const execSummary = cohortMeta.exec_summary;

  return (
    <Card className="bg-gradient-to-br from-violet-50/40 to-indigo-50/30 border-violet-200" data-testid="cohort-reports-panel">
      <CardContent className="py-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Flag className="w-4 h-4 text-violet-700" />
            <span className="text-sm font-semibold text-violet-900">
              Current Portfolio
            </span>
            <span className="text-xs text-slate-500">— {rdData?.all ?? c.eligible_for_ai_universe ?? c.total_assets ?? 0} Eligible Assets</span>
            <span className="basis-full text-[11px] text-slate-400">
              Latest AI recommendation for each currently eligible asset, irrespective of the phase in which it was assessed.
            </span>
            {/* Committee status chips MOVED (Feb 2026 v2) to below the
                Status Pipeline. They belonged next to the pipeline stage
                they decompose, not in the Marksman header. */}
            {/* "N reports generated" badge removed (Feb 2026 v2) — the
                cohort-actions row already surfaces the current report
                state and the count wasn't actionable. */}
            <Button size="sm" variant="ghost" onClick={reconcileCohort} className="hidden" title="Legacy — moved to Advanced menu">
              Reconcile
            </Button>
            {/* Fact-Check Audit & Refresh button removed (Feb 2026 v2)
                per client feedback — the bi-monthly committee decision
                arrives well before the intelligence would go stale, so
                a mid-cycle refresh is unnecessary. Fact-checking is
                already built into the initial Generate AI Assessment
                run (Perplexity Steps 1-4 fetch fresh intelligence),
                and ad-hoc per-asset refresh remains available on each
                asset's detail page. The /viatris/admin/fact-check-refresh
                endpoint is kept on the backend for that per-asset flow. */}
            {/* System Utilities (Admin only) — recovery/diagnostic escape
                hatches kept out of the day-to-day screening workflow.
                NOT for normal operation: Reconcile, Fix null scores,
                Retry CNA, Repair incomplete, Force reset. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-[10px] text-slate-500 hover:text-slate-700 gap-1"
                  data-testid="advanced-menu-btn"
                  title="System Utilities (Admin only) — recovery actions needed only when the pipeline shows unexpected state. Not part of normal operation."
                >
                  <Wrench className="w-3 h-3" />
                  System Utilities
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-500">
                  System Utilities · Admin only
                </DropdownMenuLabel>
                <div className="px-2 pb-1 text-[10px] text-slate-400 leading-tight">
                  Recovery tools — not part of the normal screening workflow.
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={reconcileCohort}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  data-testid="advanced-reconcile-item"
                >
                  <span className="font-medium">Reconcile cohort</span>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    Re-tag orphaned assets so cohort counters agree
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={restoreCommitteeDecisions}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  data-testid="advanced-restore-decisions-item"
                >
                  <span className="font-medium">Restore committee decisions</span>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    Recover Approved/Rejected marks lost from assets (audit-log based)
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={fixNullScoreRows}
                  disabled={fixingNullScores}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  data-testid="advanced-fix-null-scores-item"
                >
                  <span className="font-medium inline-flex items-center gap-1">
                    {fixingNullScores && <Loader2 className="w-3 h-3 animate-spin" />}
                    Fix null scores
                  </span>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    Relabel assessed-but-null-score rows to Could Not Assess
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={retryCouldNotAssess}
                  disabled={retryingCNA}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  data-testid="advanced-retry-cna-item"
                >
                  <span className="font-medium inline-flex items-center gap-1">
                    {retryingCNA && <Loader2 className="w-3 h-3 animate-spin" />}
                    Retry Could Not Assess
                  </span>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    Reset CNA rows to Not Started so a new batch picks them up
                  </span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={repairIncomplete}
                  disabled={repairing}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                  data-testid="advanced-repair-incomplete-item"
                >
                  <span className="font-medium inline-flex items-center gap-1">
                    {repairing && <Loader2 className="w-3 h-3 animate-spin" />}
                    Repair incomplete assessments
                  </span>
                  <span className="text-[10px] text-slate-500 leading-tight">
                    Finish assets stuck mid-pipeline — reconcile orphaned rows (0 AI), resume Claude on CNA rows with research
                  </span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={forceResetStuckJobs}
                  disabled={resetting}
                  className="text-[12px] flex flex-col items-start gap-0.5 py-2 cursor-pointer text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                  data-testid="advanced-force-reset-item"
                >
                  <span className="font-medium inline-flex items-center gap-1">
                    {resetting && <Loader2 className="w-3 h-3 animate-spin" />}
                    Force reset stuck jobs
                  </span>
                  <span className="text-[10px] text-rose-500 leading-tight">
                    Mark running jobs as error, revert Enriching rows
                  </span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* Recovery button — visible when no active job is running BUT
                some rows are stuck in Enriching (typically after a redeploy
                killed the background task). One click reverts them and
                lets you retry safely. */}
            {assessJob?.status !== 'running' && (c?.enriching || 0) > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={cancelBatchAssessment}
                className="h-6 text-[10px] border-rose-300 text-rose-700 bg-rose-50 hover:bg-rose-100"
                title="Some rows are stuck in 'Enriching' state but no batch is running. Click to reset them back to 'Not Started' so you can retry."
                data-testid="reset-stuck-enriching-btn"
              >
                <X className="w-3 h-3 mr-1" /> Reset {c.enriching} stuck row{c.enriching > 1 ? 's' : ''}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Toolbar rationalisation (Feb 2026 v2.2) — "Generate AI
                Assessment" and "Generate reports" moved into the Status
                Pipeline action bar above (which surfaces the right CTA
                based on where the cohort is in the workflow). The
                toolbar retains only the terminal action (Lock cohort)
                plus the Advanced diagnostic dropdown; primary CTAs
                live on the pipeline itself so there's a single truth-
                point for "what's the next step?". */}
            {/* Batch progress strip — surfaces when an AI Assessment
                job is actively running so the operator sees live
                completion counter even when the action pill above
                collapses to "Assessing…". */}
            {(batchAssessing || assessJob?.status === 'running' || assessJob?.status === 'preparing') && (
              <span className="inline-flex items-center gap-1 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                {assessJob?.status === 'preparing'
                  ? 'Preparing batch…'
                  : <>Assessing {assessJob?.completed || 0}/{assessJob?.total || '…'}</>}
              </span>
            )}
            <Button size="sm" onClick={() => setShowReports(true)} className="hidden" data-testid="open-reports-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Generate reports
            </Button>
          </div>
        </div>

        {/* Rescore summary strip — surfaces the tier flips from the
            most recent "Rescore priority tiers" run so the analyst can
            see what moved without opening DevTools. Dismissable. */}
        {rescoreSummary && (
          <div
            className="rounded-lg border-2 border-violet-300 bg-gradient-to-r from-violet-50 to-indigo-50 px-4 py-3 shadow-sm"
            data-testid="rescore-summary-strip"
          >
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Sparkles className="w-4 h-4 text-violet-700" />
                <span className="text-sm font-bold text-violet-900">Rescore complete</span>
                <span className="text-xs text-violet-700">
                  {rescoreSummary.updated || 0} / {rescoreSummary.targets || 0} rescored
                </span>
                <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]">
                  {rescoreSummary.tier_flips_count || 0} tier flip{rescoreSummary.tier_flips_count === 1 ? '' : 's'}
                </Badge>
                {(rescoreSummary.errors_count || 0) > 0 && (
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">
                    {rescoreSummary.errors_count} error{rescoreSummary.errors_count === 1 ? '' : 's'}
                  </Badge>
                )}
              </div>
              <button
                type="button"
                onClick={() => setRescoreSummary(null)}
                className="text-violet-500 hover:text-violet-800 p-0.5"
                aria-label="Dismiss"
                data-testid="dismiss-rescore-summary"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {Array.isArray(rescoreSummary.tier_flips) && rescoreSummary.tier_flips.length > 0 && (
              <div className="max-h-40 overflow-y-auto border border-violet-200 rounded bg-white/60">
                <table className="w-full text-[11px]">
                  <thead className="text-violet-700 uppercase tracking-wide text-[9px] bg-violet-50/70">
                    <tr>
                      <th className="px-2 py-1 text-left">Asset</th>
                      <th className="px-2 py-1 text-left">From</th>
                      <th className="px-2 py-1 text-left">To</th>
                      <th className="px-2 py-1 text-right">Weighted /5</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rescoreSummary.tier_flips.map((f, i) => (
                      <tr key={i} className="border-t border-violet-100">
                        <td className="px-2 py-1 font-medium text-slate-800 truncate">{f.asset || '—'}</td>
                        <td className="px-2 py-1 text-slate-600">{f.from || '—'}</td>
                        <td className="px-2 py-1 font-semibold text-violet-800">{f.to || '—'}</td>
                        <td className="px-2 py-1 text-right font-mono text-slate-700">
                          {typeof f.weighted === 'number' ? f.weighted.toFixed(2) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Live AI Assessment progress strip — bold, unmissable, persists
            briefly after completion so you see the final state.
            Hidden when polling has failed 3+ times consecutively so a stale
            "running" state doesn't pin the Stop button visible forever when
            the backend pod is actually unreachable. */}
        {pollFailRef.current < 3 && (assessJob?.status === 'running' || assessJob?.status === 'preparing' || (assessJob?.status === 'done' && (assessJob?.completed || 0) > 0 && assessJob?.finished_at &&
          (Date.now() - new Date(assessJob.finished_at).getTime()) < 30000)) && (
          <div
            className={`rounded-lg border-2 px-4 py-3 shadow-sm ${
              assessJob?.status === 'done'
                ? 'bg-emerald-50 border-emerald-300'
                : 'bg-gradient-to-r from-violet-50 via-violet-100/60 to-violet-50 border-violet-300'
            }`}
            data-testid="assess-progress-strip"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {assessJob?.status === 'done' ? (
                  <>
                    <Check className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm font-bold text-emerald-900">AI Assessment complete</span>
                    <span className="text-xs text-emerald-700">
                      {assessJob?.completed || 0} asset{(assessJob?.completed || 0) > 1 ? 's' : ''} enriched
                    </span>
                    {/* Jul 2026 · errors chip stays clickable AFTER the run
                        completes — operators need the failure list post-run
                        (e.g. "1 enriched · 27 errors" on a CNA retry). */}
                    {assessJob?.errors > 0 && (
                      <button
                        type="button"
                        onClick={() => setErrorSamplesOpen(true)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-800 text-[10px] font-semibold ml-1 hover:bg-rose-200 cursor-pointer"
                        title="Click to see the last 20 error messages · lets you spot systemic failures (missing data link, unlinked biotech, etc.)"
                        data-testid="batch-errors-chip-done"
                      >
                        <AlertCircle className="w-3 h-3" />
                        {assessJob.errors} error{assessJob.errors > 1 ? 's' : ''} — click for details
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                    <span className="text-sm font-bold text-violet-900">
                      {assessJob?.status === 'preparing' ? 'Preparing AI batch…' : 'AI Assessment running…'}
                    </span>
                    <span className="text-xs text-violet-700">
                      {/* Feb 2026 v28 · Clamp so total stays >= completed. If
                          extra rows sneak into the batch mid-run (e.g., a
                          concurrent Retry queued more), the total was stuck
                          at the original size producing "12 of 8 · 150%". */}
                      {assessJob?.status === 'preparing'
                        ? 'selecting eligible assets on the server…'
                        : <>{assessJob?.completed || 0} of {Math.max(assessJob?.total || 0, assessJob?.completed || 0)} done</>}
                    </span>
                    {assessJob?.errors > 0 && (
                      <button
                        type="button"
                        onClick={() => setErrorSamplesOpen(true)}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-rose-100 border border-rose-300 text-rose-800 text-[10px] font-semibold ml-1 hover:bg-rose-200 cursor-pointer"
                        title="Click to see the last 20 error messages · lets you spot systemic failures (missing API key, unlinked biotech, etc.)"
                        data-testid="batch-errors-chip"
                      >
                        <AlertCircle className="w-3 h-3" />
                        {assessJob.errors} error{assessJob.errors > 1 ? 's' : ''}
                      </button>
                    )}
                  </>
                )}
              </div>
              <div className={`text-2xl font-bold font-mono ${assessJob?.status === 'done' ? 'text-emerald-700' : 'text-violet-700'}`}>
                {Math.min(100, Math.max(0, assessJob?.progress_pct ?? 0))}%
              </div>
            </div>
            {/* THE BAR — thick, full-width, animated fill */}
            <div className="relative w-full h-4 bg-white border border-violet-200 rounded-full overflow-hidden shadow-inner">
              <div
                className={`h-full transition-all duration-700 ease-out ${
                  assessJob?.status === 'done'
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600'
                    : 'bg-gradient-to-r from-violet-500 via-violet-600 to-indigo-600'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, assessJob?.progress_pct ?? 0))}%` }}
              >
                {/* Subtle shimmer while running */}
                {assessJob?.status === 'running' && (
                  <div className="h-full w-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse" />
                )}
              </div>
            </div>
            {assessJob?.status === 'running' && (
              <div className="flex items-center justify-between mt-2 text-[11px]">
                <div className="text-violet-700 truncate flex-1">
                  {assessJob?.current_asset_name ? (
                    <><span className="font-semibold">Now assessing:</span> {assessJob.current_asset_name}</>
                  ) : (
                    <span className="italic text-violet-500">Warming up the pipeline…</span>
                  )}
                </div>
                <div className="flex items-center gap-3 ml-3 whitespace-nowrap">
                  <span className="text-violet-600 italic">
                    ~{Math.max(1, Math.round(((assessJob?.total || 0) - (assessJob?.completed || 0)) / 3 * 75 / 60))} min remaining · concurrency 3
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={cancelBatchAssessment}
                    className="h-7 text-[11px] border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                    data-testid="cancel-batch-assessment-btn"
                  >
                    <X className="w-3.5 h-3.5 mr-1" /> Stop
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Recommendation distribution — outcome counts. Each tile is a
            click-to-filter chip (also serves as the LRI band selector,
            since LRI bands === Recommendation values). Pipeline-derived
            stats (Total / AI Done / Pending / Scored / Analyst Reviewed)
            are intentionally NOT here — they're already visualised in the
            Status Pipeline above.

            Feb 2026 v12 · Rendered via React Portal into the funnel's
            `#viatris-rd-anchor` (defined inside ExplainableFunnel just
            below the Eligible for AI boundary card) so the tiles appear
            in the funnel flow. Falls back to in-place render if the
            anchor isn't yet mounted (first paint). */}
        <RdPortal>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 inline-flex items-center gap-1.5">
              Current AI Recommendations
              <InfoTip wide text={COMPOSITE_SCORE_TOOLTIP} />
            </div>
            <div className="text-[10px] text-slate-500 italic">
              Click a tile to filter the table below
            </div>
          </div>
          <div className="grid grid-cols-10 gap-1.5">
          <CohortStat
            label="Strategic Priority"
            value={rdData?.strategic_priority ?? c.strategic_priority ?? 0}
            highlight="violet"
            definition={DEFINITIONS.strategic_priority}
            active={activeRecommendation === 'Strategic Priority'}
            onClick={onTierClick ? () => onTierClick('Strategic Priority') : undefined}
          />
          <CohortStat
            label="High Priority"
            value={rdData?.high_priority ?? c.high_priority ?? 0}
            highlight="emerald"
            definition={DEFINITIONS.high_priority}
            active={activeRecommendation === 'High Priority'}
            onClick={onTierClick ? () => onTierClick('High Priority') : undefined}
          />
          <CohortStat
            label="Medium Priority"
            value={rdData?.medium ?? c.medium_priority ?? 0}
            highlight="sky"
            definition={DEFINITIONS.medium}
            active={activeRecommendation === 'Medium'}
            onClick={onTierClick ? () => onTierClick('Medium') : undefined}
          />
          <CohortStat
            label="Monitor"
            value={rdData?.monitor ?? c.monitor ?? 0}
            highlight="amber"
            definition={DEFINITIONS.monitor}
            active={activeRecommendation === 'Monitor'}
            onClick={onTierClick ? () => onTierClick('Monitor') : undefined}
          />
          <CohortStat
            label="Low Priority"
            value={rdData?.low_priority ?? c.low_priority ?? 0}
            highlight="rose"
            definition={DEFINITIONS.low_priority}
            active={activeRecommendation === 'Low Priority'}
            onClick={onTierClick ? () => onTierClick('Low Priority') : undefined}
          />
          <CohortStat
            label="Not Suitable"
            value={rdData?.not_suitable ?? c.not_suitable ?? 0}
            highlight="rose"
            definition={DEFINITIONS.not_suitable}
            active={activeRecommendation === 'Not Suitable'}
            onClick={onTierClick ? () => onTierClick('Not Suitable') : undefined}
          />
          <CohortStat
            label="Could Not Assess"
            value={rdData?.could_not_assess ?? c.could_not_assess ?? 0}
            highlight="slate"
            definition={DEFINITIONS.could_not_assess}
            active={activeRecommendation === 'Could Not Assess'}
            onClick={onTierClick ? () => onTierClick('Could Not Assess') : undefined}
          />
          {/* Awaiting AI (current) — live-universe view: eligible assets with
              no recommendation TODAY, regardless of phase. Phase-specific
              carried-forward counts live on the phase cards. */}
          <CohortStat
            label="Awaiting AI (current)"
            value={rdData?.awaiting_ai ?? (Math.max(0, (c.eligible_for_ai_universe || 0) - (c.total_assets || 0)) + (c.unassessed_in_cohort || 0))}
            highlight="indigo"
            definition="Currently eligible assets with no AI recommendation today (live universe view, spans all phases). Click to see the exact assets."
            active={activeRecommendation === '__awaiting_ai__'}
            onClick={onTierClick ? () => onTierClick('__awaiting_ai__') : undefined}
          />
          <CohortStat
            label="All"
            value={rdData?.all ?? c.eligible_for_ai_universe ?? c.total_assets ?? 0}
            highlight="slate"
            definition="Total assets eligible for AI evaluation in the China universe (post-screening). Equals the 'Final Eligible Cohort' number above and the sum of the tier tiles + Awaiting AI. Click here to clear any active tier filter."
            active={!activeRecommendation}
            onClick={onTierClick ? () => onTierClick(null) : undefined}
          />
          {/* Aug 2026 · Responded — companies that replied to outreach.
              Manual Yes/No tracking, independent of every other status. */}
          <CohortStat
            label="Responded"
            value={respondedCount ?? 0}
            highlight="emerald"
            definition="Asset owners who have replied to our outreach (manually marked Responded: Yes). Click to filter the table to responded companies only — independent of Shortlisted / Team Approved / availability statuses."
            active={!!respondedActive}
            onClick={onRespondedClick}
          />
          </div>
        </div>
        </RdPortal>

        {/* LRI distribution widgets — only when LRI scores have been computed */}
        {lriWidgets && (lriWidgets.strategic_priority + lriWidgets.high_readiness + lriWidgets.medium_readiness + lriWidgets.monitor + lriWidgets.low_readiness > 0) && (
          <div className="bg-white border border-emerald-200 rounded p-2.5" data-testid="lri-widgets">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-2 flex items-center gap-1">
              <Flag className="w-3 h-3" /> Licensing Readiness (companies, not assets)
            </div>
            <div className="grid grid-cols-5 gap-1.5">
              <CohortStat label="Strategic Priority" value={lriWidgets.strategic_priority} highlight="violet" definition={DEFINITIONS.strategic_priority} />
              <CohortStat label="High Priority" value={lriWidgets.high_readiness} highlight="emerald" definition={DEFINITIONS.high_priority} />
              <CohortStat label="Medium" value={lriWidgets.medium_readiness} highlight="sky" definition={DEFINITIONS.medium} />
              <CohortStat label="Monitor" value={lriWidgets.monitor} highlight="amber" definition={DEFINITIONS.monitor} />
              <CohortStat label="Low Priority" value={lriWidgets.low_readiness} highlight="rose" definition={DEFINITIONS.low_priority} />
            </div>
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              <CohortStat label="Verified BD" value={lriWidgets.verified_bd_contact} highlight="sky" />
              <CohortStat label="Recently Funded" value={lriWidgets.recently_funded} highlight="violet" />
              <CohortStat label="Met In-Person" value={lriWidgets.met_in_person} highlight="indigo" />
            </div>
          </div>
        )}

        {/* Executive Headline removed (Feb 2026 v2) — the cohort text was
            AI-generated boilerplate rather than actionable data. The
            Status Pipeline + Recommendation Distribution tiles above now
            carry the same information without the marketing prose. */}
      </CardContent>

      {/* Reports Dialog */}
      <Dialog open={showReports} onOpenChange={setShowReports}>
        <DialogContent data-testid="reports-dialog">
          <DialogHeader>
            <DialogTitle>Generate Reports — {cohortName}</DialogTitle>
            <DialogDescription>
              All formats are generated from the same structured data — they always stay in sync.
              Excel and PowerPoint take ~2-3 seconds; PDF takes ~5s.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => downloadFile('xlsx')} disabled={!!downloading} variant="outline" className="h-auto py-3 flex-col items-start" data-testid="dl-xlsx">
              {downloading === 'xlsx' ? <Loader2 className="w-5 h-5 animate-spin mb-1" /> : <FileSpreadsheet className="w-5 h-5 text-emerald-600 mb-1" />}
              <div className="font-semibold text-sm">Excel Workbook</div>
              <div className="text-[10px] text-slate-500 text-left">7 sheets: Exec / Ranking / Companies / Full / Scoring / Change Log / Refs</div>
            </Button>
            <Button onClick={() => downloadFile('pptx')} disabled={!!downloading} variant="outline" className="h-auto py-3 flex-col items-start" data-testid="dl-pptx">
              {downloading === 'pptx' ? <Loader2 className="w-5 h-5 animate-spin mb-1" /> : <Presentation className="w-5 h-5 text-orange-600 mb-1" />}
              <div className="font-semibold text-sm">PowerPoint Deck</div>
              <div className="text-[10px] text-slate-500 text-left">Cover + 12 themed slides + top-25 individual asset slides</div>
            </Button>
            <Button onClick={() => downloadFile('pdf')} disabled={!!downloading} variant="outline" className="h-auto py-3 flex-col items-start" data-testid="dl-pdf">
              {downloading === 'pdf' ? <Loader2 className="w-5 h-5 animate-spin mb-1" /> : <FileText className="w-5 h-5 text-rose-600 mb-1" />}
              <div className="font-semibold text-sm">PDF Report</div>
              <div className="text-[10px] text-slate-500 text-left">Executive + portfolio overview + top 20 individual asset pages</div>
            </Button>
            <Button onClick={() => downloadFile('email-summary').catch(()=>{})}
              disabled
              variant="outline" className="h-auto py-3 flex-col items-start opacity-50" data-testid="dl-email">
              <Mail className="w-5 h-5 text-sky-600 mb-1" />
              <div className="font-semibold text-sm">Email Summary</div>
              <div className="text-[10px] text-slate-500 text-left">HTML preview available via API (UI viewer next phase)</div>
            </Button>
          </div>

          {/* Publish to Viatris Portal — files uploaded here appear in the
              client portal's Reports Library. Whatever lives in this list
              is exactly what Viatris sees. */}
          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-semibold text-sm text-slate-800">Published to Viatris Portal</div>
                <div className="text-[10px] text-slate-500">
                  {publishedFiles.length === 0
                    ? 'Nothing published yet — upload any report or file below to make it visible on the client portal.'
                    : `${publishedFiles.length} file${publishedFiles.length === 1 ? '' : 's'} currently visible to Viatris.`}
                </div>
              </div>
            </div>

            <div className="space-y-1.5 mb-3 max-h-48 overflow-y-auto">
              {publishedFiles.map((f) => (
                <div key={f.id} className="flex items-center gap-2 text-xs border border-slate-200 rounded px-2 py-1.5 bg-white" data-testid={`published-file-${f.id}`}>
                  <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-800 truncate" title={f.label}>{f.label}</div>
                    <div className="text-[10px] text-slate-500">
                      {(f.format || '').toUpperCase()} · {(f.uploaded_at || '').slice(0, 10)}
                    </div>
                  </div>
                  <button
                    onClick={() => deletePublishedFile(f.id)}
                    className="text-[10px] text-rose-600 hover:underline shrink-0"
                    data-testid={`remove-published-${f.id}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <input
                type="text"
                value={pubLabel}
                onChange={(e) => setPubLabel(e.target.value)}
                placeholder="Optional label (e.g. Feb 2026 Committee Brief)"
                className="flex-1 text-xs border border-slate-200 rounded px-2 py-1.5 focus:border-indigo-400 focus:outline-none"
                data-testid="publish-label-input"
              />
              <label className="cursor-pointer">
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadPublishedFile(f);
                    e.target.value = '';
                  }}
                  disabled={uploadingPub}
                  data-testid="publish-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={uploadingPub}
                  className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700 text-white pointer-events-none"
                >
                  {uploadingPub ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Publish file
                </Button>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReports(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comparison Dialog */}
      <Dialog open={showComparison} onOpenChange={setShowComparison}>
        <DialogContent className="max-w-3xl" data-testid="comparison-dialog">
          <DialogHeader>
            <DialogTitle>Cohort Comparison</DialogTitle>
            <DialogDescription>
              Diff between {cohortName} and the previously-locked cohort.
            </DialogDescription>
          </DialogHeader>
          {!comparison ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : !comparison.has_prior ? (
            <div className="text-sm text-slate-600 py-4">
              <AlertCircle className="w-5 h-5 inline mr-1 text-amber-500" />
              {comparison.message || 'No prior cohort available for comparison.'}
            </div>
          ) : (
            <div className="space-y-3 text-xs">
              <div className="text-slate-700">
                Compared with: <strong>{comparison.prior_cohort}</strong>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(comparison.counts || {}).map(([k, v]) => (
                  <div key={k} className="border rounded p-2 bg-slate-50">
                    <div className="text-[10px] uppercase text-slate-500">{k.replace(/_/g, ' ')}</div>
                    <div className="text-lg font-bold text-slate-800">{v}</div>
                  </div>
                ))}
              </div>
              {comparison.score_improved?.length > 0 && (
                <details className="border rounded p-2" open>
                  <summary className="font-semibold text-emerald-700 cursor-pointer">Score improved ({comparison.score_improved.length})</summary>
                  <ul className="mt-2 space-y-1 pl-3">
                    {comparison.score_improved.map((x, i) => (
                      <li key={i}>{x.asset_name}: <strong>{x.from} → {x.to}</strong></li>
                    ))}
                  </ul>
                </details>
              )}
              {comparison.phase_progressed?.length > 0 && (
                <details className="border rounded p-2">
                  <summary className="font-semibold text-blue-700 cursor-pointer">Phase progressed ({comparison.phase_progressed.length})</summary>
                  <ul className="mt-2 space-y-1 pl-3">
                    {comparison.phase_progressed.map((x, i) => (
                      <li key={i}>{x.asset_name}: <strong>{x.from} → {x.to}</strong></li>
                    ))}
                  </ul>
                </details>
              )}
              {comparison.new_assets?.length > 0 && (
                <details className="border rounded p-2">
                  <summary className="font-semibold text-violet-700 cursor-pointer">New assets ({comparison.new_assets.length})</summary>
                  <ul className="mt-2 space-y-0.5 pl-3 text-slate-600">{comparison.new_assets.map((n, i) => <li key={i}>• {n}</li>)}</ul>
                </details>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowComparison(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Strategic Alerts Dialog */}
      <Dialog open={showAlerts} onOpenChange={setShowAlerts}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-600" />
              Strategic Alerts — {cohortName}
              <div className="ml-2 flex gap-1">
                {alertCounts.Critical > 0 && <Badge className="bg-rose-100 text-rose-800 border-rose-200 text-[10px]">{alertCounts.Critical} Critical</Badge>}
                {alertCounts.High > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">{alertCounts.High} High</Badge>}
              </div>
            </DialogTitle>
            <DialogDescription>
              AI-detected developments from the last 90 days. Critical + High events flag affected assets for analyst re-review.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-2 pr-2">
            {loadingAlerts ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : alerts.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">
                <AlertCircle className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                No alerts yet. Click &quot;Scan for updates&quot; to detect recent developments.
              </div>
            ) : (
              alerts.map((a) => (
                <AlertCard key={a.id} alert={a} onUpdate={updateAlertStatus} />
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlerts(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Error-samples modal — click the "N errors" chip to
          see the last 20 failure messages. Reads assessJob.error_samples
          populated by the backend for each Exception caught in
          `_bounded_run`. Systemic-fail diagnostic — a single glance tells
          you if it's a missing API key, unlinked biotech, or timeouts. */}
      {errorSamplesOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setErrorSamplesOpen(false)}
          data-testid="error-samples-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold">Batch Errors · Last 20 Samples</div>
                  <div className="text-xs text-slate-600 truncate">
                    {assessJob?.errors || 0} error{(assessJob?.errors || 0) === 1 ? '' : 's'} of {assessJob?.total || 0} · {assessJob?.completed || 0} completed
                  </div>
                </div>
              </div>
              <button
                onClick={() => setErrorSamplesOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
                aria-label="Close"
                data-testid="error-samples-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 text-xs">
              {!assessJob?.error_samples || assessJob.error_samples.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-slate-500 mb-2">No error samples available yet.</div>
                  <div className="text-slate-400 text-[11px]">
                    Either this is an older batch that ran before error-sample tracking was added,
                    <br />or the errors are being counted before the sample gets pushed.
                    <br /><br />
                    Try firing a fresh batch — the next one will capture samples.
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    const groups = {};
                    for (const s of assessJob.error_samples) {
                      const key = (s.error || 'Unknown').trim();
                      if (!groups[key]) groups[key] = { error: key, count: 0, samples: [] };
                      groups[key].count += 1;
                      if (groups[key].samples.length < 3) groups[key].samples.push(s);
                    }
                    const list = Object.values(groups).sort((a, b) => b.count - a.count);
                    return list.map((g, i) => (
                      <div key={i} className="border border-rose-200 rounded p-2 bg-rose-50/50" data-testid={`error-group-${i}`}>
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="font-mono text-[11px] text-rose-900 break-words flex-1">{g.error}</div>
                          <Badge className="bg-rose-200 text-rose-900 text-[10px] shrink-0">x{g.count}</Badge>
                        </div>
                        <div className="text-[10px] text-slate-500 pl-1">
                          e.g., {g.samples.map(s => s.asset_name || s.ea_id).slice(0, 3).join(' - ')}
                          {g.samples.length < g.count ? ` ... (${g.count - g.samples.length} more)` : ''}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
              <div>
                <strong>Common root causes:</strong> unlinked biotech - missing PPLX API key - timeout on hung Perplexity call
              </div>
              <Button size="sm" onClick={() => setErrorSamplesOpen(false)} data-testid="error-samples-dismiss">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Feb 2026 · Batch Preview modal — read-only diagnostic pane
          showing exactly what the next Phase 3+ batch would queue
          and why every other row is skipped. Never fires an LLM call. */}
      {batchPreviewOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setBatchPreviewOpen(false)}
          data-testid="batch-preview-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Search className="w-4 h-4 text-indigo-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-indigo-700 font-semibold">
                    {batchPreviewData?.source === 'pipeline_preview_eligible'
                      ? 'Preview · Eligible Assets'
                      : 'Batch Preview · Phase 3+'}
                  </div>
                  <div className="text-xs text-slate-600 truncate">Cohort: <span className="font-mono">{cohortName}</span> · scope: {batchPreviewData?.source === 'pipeline_preview_eligible' ? 'phase + TA + exclusion rules' : 'engagement-wide'} · read-only</div>
                </div>
              </div>
              <button
                onClick={() => setBatchPreviewOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1"
                aria-label="Close"
                data-testid="batch-preview-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {batchPreviewLoading ? (
                <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading preview…
                </div>
              ) : batchPreviewError ? (
                <div className="text-rose-700 text-sm border border-rose-200 bg-rose-50 rounded p-3" data-testid="batch-preview-error">
                  {batchPreviewError}
                </div>
              ) : batchPreviewData ? (
                <>
                  {/* Tally strip */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
                    {[
                      ['Total in scope',           batchPreviewData.tally?.total_in_scope,           'bg-slate-100 text-slate-700'],
                      ['Would queue',              batchPreviewData.tally?.would_queue,              'bg-emerald-100 text-emerald-800'],
                      ['Excluded — off-phase',     batchPreviewData.tally?.excluded_off_phase,       'bg-amber-100 text-amber-800'],
                      ['Excluded — already done',  batchPreviewData.tally?.excluded_already_assessed, 'bg-slate-100 text-slate-700'],
                      ['Split-brain re-queue',     batchPreviewData.tally?.excluded_split_brain_kept, 'bg-indigo-100 text-indigo-800'],
                    ].map(([label, value, cls]) => (
                      <div key={label} className={`rounded px-2 py-1.5 border border-slate-200 ${cls}`} data-testid={`batch-preview-tally-${label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                        <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
                        <div className="text-lg font-bold">{value ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  {/* Filter bar (Feb 2026 · added after prod payload had
                      926 rows and finding a specific asset was painful) */}
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <div className="relative flex-1 min-w-[240px]">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        value={batchPreviewFilter}
                        onChange={(e) => setBatchPreviewFilter(e.target.value)}
                        placeholder="Filter by asset name, phase, status, or reason…"
                        className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded bg-white focus:outline-none focus:border-indigo-400"
                        data-testid="batch-preview-filter-input"
                        autoFocus
                      />
                    </div>
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={batchPreviewShowOffPhase}
                        onChange={(e) => setBatchPreviewShowOffPhase(e.target.checked)}
                        className="w-3.5 h-3.5"
                        data-testid="batch-preview-show-off-phase"
                      />
                      Show off-phase rows ({batchPreviewData.tally?.excluded_off_phase ?? 0})
                    </label>
                  </div>
                  {/* Per-row table — columns adapt when opened via Preview Eligible
                      (source='pipeline_preview_eligible') so we show the Client
                      Portal-friendly Asset / Company / Phase / TA / AI Status
                      instead of the legacy Phase 3+ Batch columns. */}
                  <div className="overflow-x-auto border border-slate-200 rounded">
                    {batchPreviewData?.source === 'pipeline_preview_eligible' ? (
                      <table className="w-full text-xs" data-testid="batch-preview-table">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Asset</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Company</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Phase</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Therapeutic Area</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-600">AI Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const q = batchPreviewFilter.trim().toLowerCase();
                            const rows = (batchPreviewData.rows || []).filter((r) => {
                              if (!q) return true;
                              const hay = [
                                r.asset_name, r.company_name, r.phase,
                                r.therapeutic_area, r.assessment_status,
                              ].map(s => (s || '').toString().toLowerCase()).join(' | ');
                              return hay.includes(q);
                            });
                            if (rows.length === 0) {
                              return (
                                <tr>
                                  <td colSpan={5} className="px-3 py-6 text-center text-slate-400 text-[11px]">
                                    {q ? `No rows match "${batchPreviewFilter}"` : 'No eligible assets — all rows failed the Phase / TA / Exclusion gate.'}
                                  </td>
                                </tr>
                              );
                            }
                            return rows.map((r, i) => (
                              <tr key={r.asset_id || i} className="border-t border-slate-100 bg-emerald-50/30" data-testid={`eligible-preview-row-${r.asset_id || i}`}>
                                <td className="px-2 py-1.5 font-medium text-slate-800">{r.asset_name || <span className="text-slate-400">—</span>}</td>
                                <td className="px-2 py-1.5 text-slate-600">{r.company_name || <span className="text-slate-400">—</span>}</td>
                                <td className="px-2 py-1.5">{r.phase || <span className="text-slate-400">—</span>}</td>
                                <td className="px-2 py-1.5">{r.therapeutic_area || <span className="text-slate-400">—</span>}</td>
                                <td className="px-2 py-1.5">
                                  {r.assessment_status
                                    ? <Badge className="bg-slate-100 text-slate-700 text-[10px]">{r.assessment_status}</Badge>
                                    : <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">Pending AI</Badge>}
                                </td>
                              </tr>
                            ));
                          })()}
                        </tbody>
                      </table>
                    ) : (
                    <table className="w-full text-xs" data-testid="batch-preview-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Asset</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Phase (ea)</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Status</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Score</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Recommendation</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Would queue?</th>
                          <th className="text-left px-2 py-1.5 font-semibold text-slate-600">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const q = batchPreviewFilter.trim().toLowerCase();
                          const rows = (batchPreviewData.rows || []).filter((r) => {
                            // Hide off-phase by default unless the operator
                            // asks for them or the search matches. Reduces
                            // noise on the common case of a 900-row engagement.
                            const isOffPhase = (r.exclusion_reason || '').startsWith('off-phase');
                            if (isOffPhase && !batchPreviewShowOffPhase && !q) return false;
                            if (!q) return true;
                            const hay = [
                              r.asset_name, r.phase_on_ea, r.engagement_status,
                              r.ea_recommendation, r.exclusion_reason,
                            ].map(s => (s || '').toString().toLowerCase()).join(' | ');
                            return hay.includes(q);
                          });
                          if (rows.length === 0) {
                            return (
                              <tr>
                                <td colSpan={7} className="px-3 py-6 text-center text-slate-400 text-[11px]">
                                  {q ? `No rows match "${batchPreviewFilter}"` : 'No rows to show. Toggle "Show off-phase rows" or clear the filter.'}
                                </td>
                              </tr>
                            );
                          }
                          return rows.map((r) => (
                            <tr
                              key={r.ea_id}
                              className={`border-t border-slate-100 ${r.would_queue ? 'bg-emerald-50/40' : ''}`}
                              data-testid={`batch-preview-row-${r.ea_id}`}
                            >
                              <td className="px-2 py-1.5 font-medium text-slate-800">{r.asset_name || <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1.5">{r.phase_on_ea || <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1.5">{r.engagement_status || <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1.5">{r.ea_composite_score ?? <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1.5">{r.ea_recommendation || <span className="text-slate-400">—</span>}</td>
                              <td className="px-2 py-1.5">
                                {r.would_queue ? (
                                  <Badge className="bg-emerald-100 text-emerald-800 text-[10px]">Yes</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-500 text-[10px]">No</Badge>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-slate-500">{r.exclusion_reason || <span className="text-slate-300">—</span>}</td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                    )}
                  </div>
                </>
              ) : null}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(JSON.stringify(batchPreviewData, null, 2));
                    toast.success('Preview JSON copied to clipboard');
                  } catch { toast.error('Clipboard blocked — copy manually from DevTools'); }
                }}
                disabled={!batchPreviewData}
                data-testid="batch-preview-copy"
              >
                Copy JSON
              </Button>
              <Button
                size="sm"
                onClick={() => setBatchPreviewOpen(false)}
                data-testid="batch-preview-dismiss"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function AlertCard({ alert, onUpdate }) {
  const sevColors = {
    Critical: 'border-rose-300 bg-rose-50',
    High:     'border-amber-300 bg-amber-50',
    Medium:   'border-slate-200 bg-white',
    Low:      'border-slate-200 bg-slate-50',
  };
  const sevBadge = {
    Critical: 'bg-rose-100 text-rose-800',
    High:     'bg-amber-100 text-amber-800',
    Medium:   'bg-slate-100 text-slate-700',
    Low:      'bg-slate-100 text-slate-500',
  };
  return (
    <div className={`border rounded-md p-3 ${sevColors[alert.severity] || 'bg-white'} ${alert.status === 'dismissed' ? 'opacity-50' : ''}`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <Badge className={`${sevBadge[alert.severity]} text-[10px]`}>{alert.severity}</Badge>
            <Badge variant="outline" className="text-[10px]">{alert.category}</Badge>
            {alert.subtype && <Badge variant="outline" className="text-[10px]">{alert.subtype}</Badge>}
            <span className="text-[10px] text-slate-500">· {alert.biotech_company_name}</span>
            {alert.date_observed && <span className="text-[10px] text-slate-400">· {alert.date_observed}</span>}
          </div>
          <div className="font-semibold text-sm text-slate-800 mb-1">{alert.headline}</div>
          <div className="text-xs text-slate-700">{alert.summary}</div>
          <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
            {alert.what_changed && (
              <div>
                <div className="text-[9px] uppercase font-semibold text-slate-500">What changed</div>
                <div className="text-slate-700">{alert.what_changed}</div>
              </div>
            )}
            {alert.why_it_matters && (
              <div>
                <div className="text-[9px] uppercase font-semibold text-slate-500">Why it matters</div>
                <div className="text-slate-700">{alert.why_it_matters}</div>
              </div>
            )}
            {alert.recommended_action && (
              <div>
                <div className="text-[9px] uppercase font-semibold text-slate-500">Next action</div>
                <div className="text-slate-700">{alert.recommended_action}</div>
              </div>
            )}
          </div>
          {alert.source_url && (
            <a href={alert.source_url} target="_blank" rel="noreferrer" className="text-[10px] text-blue-700 hover:underline mt-1 block truncate">
              Source: {alert.source_url}
            </a>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {alert.status !== 'read' && (
            <Button size="sm" variant="ghost" onClick={() => onUpdate(alert.id, 'read')} className="h-6 text-[10px] px-2">
              <Check className="w-3 h-3 mr-1" /> Read
            </Button>
          )}
          {alert.status !== 'dismissed' && (
            <Button size="sm" variant="ghost" onClick={() => onUpdate(alert.id, 'dismissed')} className="h-6 text-[10px] px-2">
              <X className="w-3 h-3 mr-1" /> Dismiss
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


function CohortStat({ label, value, highlight, definition, active, onClick }) {
  const colorMap = {
    violet:  'text-violet-700 bg-violet-100 border-violet-200',
    indigo:  'text-indigo-700 bg-indigo-100 border-indigo-200',
    emerald: 'text-emerald-700 bg-emerald-100 border-emerald-200',
    sky:     'text-sky-700 bg-sky-100 border-sky-200',
    amber:   'text-amber-700 bg-amber-100 border-amber-200',
    rose:    'text-rose-700 bg-rose-100 border-rose-200',
    slate:   'text-slate-700 bg-slate-100 border-slate-200',
  };
  const cls = colorMap[highlight] || 'text-slate-700 bg-white border-slate-200';
  const activeCls = active ? 'ring-2 ring-indigo-400 ring-offset-1' : '';
  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap
      onClick={onClick}
      type={onClick ? 'button' : undefined}
      className={`text-center rounded border p-1.5 transition-all ${cls} ${activeCls} ${onClick ? 'hover:shadow-sm hover:opacity-95 cursor-pointer' : ''}`}
      data-testid={onClick ? `tier-tile-${(label || '').toLowerCase().replace(/\s+/g, '-')}` : undefined}
    >
      <div className="text-lg font-bold">{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-wide inline-flex items-center justify-center gap-1">
        {label}
        {definition && <InfoTip text={definition} />}
      </div>
    </Wrap>
  );
}


// ─── Status Pipeline (Feb 2026 v2.2 · 5-stage top row + 3-action bottom) ─
// Top row: Cohort → Phase 3+ → TA of Interest → Committee Review → Finalised Assets
// Bottom row: Generate AI Assessment · Create Reports · Start Deep Dive
// Deep Dive stays disabled until the cohort is locked (client MoM rule).

// Feb 2026 · Universe strip — top-of-workbench asset accounting.
// Universe = every engagement_asset. Assigned = has cohort name.
// Unallocated = universe − assigned. Per-cohort chips with counts.
// Highlights the active cohort in indigo. Pure counts, no filters.
function UniverseStrip({ activeCohort }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/viatris/universe-stats`, { withCredentials: true });
      setData(r.data);
    } catch {
      // Silent — strip is diagnostic, not blocking.
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/viatris/universe-stats`, { withCredentials: true });
        if (alive) setData(r.data);
      } catch {
        // Silent — strip is diagnostic, not blocking.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Feb 2026 · one-shot "pull every China biotech asset into the Viatris
  // engagement" action. Delegates to POST /viatris/sync-china-universe
  // which wraps _run_auto_shortlist with cohort_size=0 so additions
  // land as Unallocated (never auto-cohorted). Idempotent — safe to
  // click multiple times.
  const handleSync = async () => {
    if (!window.confirm(
      'Pull every China biotech asset from the CRM into the Viatris engagement?\n\n' +
      '• Assets already in the engagement are skipped (idempotent).\n' +
      '• New rows land as Unallocated — cohort assignment is untouched.\n' +
      '• Universe + Unallocated counters will reflect the change immediately.'
    )) return;
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/viatris/sync-china-universe`, null, { withCredentials: true });
      const added = r.data?.added ?? 0;
      toast.success(
        added === 0
          ? 'Universe already in sync — no new China assets to pull in.'
          : `Synced ${added} new China asset${added === 1 ? '' : 's'} into the Viatris engagement (Unallocated pool).`
      );
      await fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  return (
    <div
      className="flex items-center gap-2 flex-wrap p-3 rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white"
      data-testid="universe-strip"
    >
      <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-900 text-white">
        <div className="text-[9px] uppercase tracking-wide opacity-70 font-semibold">Universe</div>
        <div className="text-sm font-bold tabular-nums" data-testid="universe-strip-total">{data.universe_total}</div>
      </div>
      <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200">
        <div className="text-[9px] uppercase tracking-wide opacity-70 font-semibold">Unallocated</div>
        <div className="text-sm font-bold tabular-nums" data-testid="universe-strip-unallocated">{data.unallocated}</div>
      </div>
      <div className="w-px h-6 bg-slate-200" />
      <div className="flex-1" />
      <button
        type="button"
        onClick={handleSync}
        disabled={syncing}
        className={
          'text-[10px] font-semibold px-2.5 py-1 rounded border transition-colors ' +
          (syncing
            ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-wait'
            : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400')
        }
        data-testid="universe-sync-china-btn"
        title="Pull every China biotech asset from the CRM into the Viatris engagement's Unallocated pool. Idempotent — skips already-added rows. Closes the CRM-total ↔ Universe-total gap in one click."
      >
        {syncing ? '⏳ Syncing…' : '🔄 Sync China biotechs → Universe'}
      </button>
    </div>
  );
}


function StatusPipeline({
  summary, activeCohort, activeFilters, onStageClick,
  cohortLocked, batchAssessing, assessJobStatus,
  onRunAssessment, onOpenReports, onSharePortal, onDeepDive,
  pipelineWorkflowRunning = false,
}) {
  const counters = summary?.counters || {};
  const totalAssets = activeCohort
    ? counters.current_cohort ?? 0
    : counters.inventory ?? 0;
  const poolLabel = activeCohort
    ? `${(activeCohort || '').replace(/^China\s+/i, '')} · ${totalAssets} assets`
    : `Inventory · ${totalAssets.toLocaleString()} assets`;

  // Per-stage count computation.
  const stageStates = STATUS_PIPELINE_STAGES.map((s) => {
    let count = 0;
    switch (s.key) {
      case 'cohort':
        count = activeCohort ? totalAssets : (counters.inventory ?? 0); break;
      case 'phase_3_plus':
        count = counters.phase_3_plus_count ?? 0; break;
      case 'ta_of_interest':
        count = counters.ta_priority_count ?? 0; break;
      case 'eligible_assets':
        // Assets that survive the full Phase + TA + Exclusion gate — the
        // ONLY set that gets Perplexity/Claude spend (Feb 2026 v3
        // "Simplify VIP Screening Pipeline" refactor). Falls back to
        // ta_priority_count for backward compat when the /pipeline-stats
        // counter hasn't been re-fetched yet.
        count = counters.eligible_count ?? counters.ta_priority_count ?? 0; break;
      case 'committee_review':
        // Strategic Priority + High Priority + Medium (active-review tiers).
        count = counters.committee_review_active ?? 0; break;
      case 'finalised_assets':
        // All 6 committee members submitted → client has finalised.
        count = counters.committee_scored ?? 0; break;
      default: break;
    }
    return { ...s, count, completed: count > 0 };
  });

  // Next action = the first "not yet done" step across the manual flow.
  //   scored=0 → next is Assessment
  //   scored>0 & reports=0 → next is Reports
  //   finalised>0 & not locked → next is Deep Dive (via Lock)
  const scoredCount = counters.scored ?? 0;
  const reportsCount = counters.reports_generated ?? 0;
  const finalisedCount = counters.committee_scored ?? 0;
  let nextAction = null;
  if (scoredCount === 0)                                            nextAction = 'run_ai_assessment';
  else if (reportsCount === 0)                                      nextAction = 'open_reports';
  else if (finalisedCount > 0 && !cohortLocked)                     nextAction = 'deep_dive';
  else if (cohortLocked)                                            nextAction = 'deep_dive';

  const actionButtons = [
    {
      // Feb 2026 v3 UX consolidation — ONE canonical action that chains
      // Refresh Universe → Apply Screening Rules → Recompute counters.
      // Replaces the two standalone buttons that used to competed for
      // the same intent + the duplicate "Refresh Screening Pipeline"
      // button in the amber stale banner.
      key: 'refresh_pipeline',
      label: pipelineWorkflowRunning
        ? 'Refreshing pipeline…'
        : 'Refresh Screening Pipeline',
      icon: RefreshCw,
      onClick: () => window.dispatchEvent(new CustomEvent(
        'viatris:pipeline-action',
        { detail: { action: 'refresh_pipeline' } },
      )),
      disabled: pipelineWorkflowRunning || !activeCohort,
      loading: pipelineWorkflowRunning,
      done: false,
      badge: 'DB',
      description: 'Refreshes universe + applies Phase / TA / Exclusion rules + rebuilds Committee Queue. Deterministic — no AI runs here.',
    },
    {
      // 3. Preview Eligible Assets — read-only list of what AI will
      //    receive. Zero cost. Fires the /pipeline-preview endpoint.
      key: 'preview_eligible',
      label: 'Preview Eligible',
      icon: Search,
      onClick: () => window.dispatchEvent(new CustomEvent(
        'viatris:pipeline-action',
        { detail: { action: 'preview_eligible' } },
      )),
      disabled: pipelineWorkflowRunning || !activeCohort,
      done: false,
      variant: 'ghost',
      badge: 'DB',
      description: 'Read-only preview of the assets that will get AI Assessment. Never fires an LLM call.',
    },
    {
      // 4. Run AI Assessment — the ONLY LLM step. Runs Perplexity +
      //    Claude on eligible-only rows (delta mode by default).
      key: 'run_ai_assessment',
      label: batchAssessing || assessJobStatus === 'running'
        ? 'Assessing…'
        : 'Run AI Assessment',
      icon: Sparkles,
      onClick: onRunAssessment,
      disabled: pipelineWorkflowRunning || batchAssessing || assessJobStatus === 'running' || !activeCohort,
      loading:  batchAssessing || assessJobStatus === 'running',
      done: scoredCount > 0,
      badge: 'AI',
      description: activeCohort
        ? 'Runs Perplexity + Claude on Eligible Assets only. Uses AI credits.'
        : 'Select a cohort first',
    },
    {
      // 5. Generate Reports — unchanged from the previous panel.
      key: 'generate_reports',
      label: 'Generate Reports',
      icon: FileText,
      onClick: onOpenReports,
      disabled: pipelineWorkflowRunning || !activeCohort || scoredCount === 0,
      done: reportsCount > 0,
      badge: 'DB',
      description: scoredCount === 0
        ? 'Available after AI Assessment'
        : 'Excel + PowerPoint + PDF · shared with client for internal scoring.',
    },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-lg px-6 py-4" data-testid="status-pipeline">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 inline-flex items-baseline gap-1.5">
          Status Pipeline
          <span className="text-[10px] normal-case tracking-normal text-slate-500 font-normal">
            ({poolLabel})
          </span>
        </div>
      </div>

      {/* ── Top row · 5-stage pipeline ────────────────────────────── */}
      <div className="flex items-start gap-0 w-full">
        {stageStates.map((s, idx) => {
          const Icon = s.icon;
          const isActiveFilter = s.filterField != null
            && s.filterField !== '__all__'
            && activeFilters?.[s.filterField] === s.filterValue;
          const clickable = s.filterField != null;

          const ringCls = s.completed
            ? 'bg-emerald-100 ring-2 ring-emerald-400'
            : 'bg-slate-100 ring-2 ring-slate-200';
          const iconColorCls = s.completed ? 'text-emerald-700' : 'text-slate-400';
          const labelCls = (s.completed
              ? 'text-emerald-800 font-semibold'
              : 'text-slate-500')
            + (isActiveFilter ? ' underline' : '');
          const countBadgeCls = s.completed
            ? 'bg-emerald-500 text-white'
            : 'bg-slate-200 text-slate-500';

          return (
            <React.Fragment key={s.key}>
              <button
                onClick={() => clickable && onStageClick(s)}
                disabled={!clickable}
                className={`flex flex-col items-center gap-1 px-1 py-1 flex-1 min-w-0 ${
                  clickable ? 'hover:opacity-80 cursor-pointer' : 'cursor-default'
                }`}
                data-testid={`pipeline-stage-${s.key}`}
              >
                <div className="relative">
                  <div className={`w-11 h-11 rounded-full flex items-center justify-center transition-all ${ringCls}`}>
                    <Icon className={`w-5 h-5 ${iconColorCls}`} />
                  </div>
                  <span
                    className={`absolute -top-1.5 -right-2 min-w-[24px] h-[19px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${countBadgeCls}`}
                    data-testid={`pipeline-stage-${s.key}-count`}
                  >
                    {(s.count ?? 0).toLocaleString()}
                  </span>
                </div>
                <div className={`text-[11px] leading-tight text-center inline-flex items-center justify-center gap-1 ${labelCls}`}>
                  {s.label}
                  {DEFINITIONS[s.key] && <InfoTip text={DEFINITIONS[s.key]} />}
                </div>
              </button>
              {idx < stageStates.length - 1 && (
                <PipelineArrow
                  active={s.completed}
                  pulsing={s.completed && !stageStates[idx + 1].completed}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Bottom row · 3 action buttons ────────────────────────── */}
      <div className="mt-4 pt-3 border-t border-slate-100" data-testid="cohort-actions-strip" id="cohort-actions">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
            Actions
          </div>
          {cohortLocked && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] py-0 h-4">
              <Lock className="w-2.5 h-2.5 mr-0.5" /> Locked
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {actionButtons.map((btn) => {
            const isNext = btn.key === nextAction && !btn.disabled;
            const Icon = btn.icon;
            const cls = btn.disabled
              ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed'
              : isNext
                ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700 shadow-md ring-2 ring-offset-2 ring-indigo-300'
                : btn.done
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50';
            return (
              <button
                key={btn.key}
                type="button"
                onClick={btn.onClick}
                disabled={btn.disabled}
                className={`flex flex-col items-start gap-1 px-3 py-2.5 rounded-md border transition-all text-left ${cls}`}
                data-testid={`pipeline-action-${btn.key}`}
                title={btn.description}
              >
                <div className="inline-flex items-center gap-1.5 font-semibold text-[12px]">
                  {btn.loading
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Icon className="w-3.5 h-3.5" />}
                  {btn.label}
                  {btn.badge && (
                    <span
                      className={`inline-flex items-center rounded px-1 py-0 text-[9px] font-bold tracking-wide ${
                        btn.badge === 'AI'
                          ? (isNext ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-800 border border-emerald-200')
                          : (isNext ? 'bg-white/25 text-white' : 'bg-blue-100 text-blue-800 border border-blue-200')
                      }`}
                      data-testid={`action-badge-${btn.key}`}
                    >
                      {btn.badge}
                    </span>
                  )}
                  {btn.lockHint && <Lock className="w-3 h-3 opacity-80" />}
                  {btn.done && !isNext && <Check className="w-3 h-3 opacity-80" />}
                </div>
                <div className={`text-[10px] leading-snug ${btn.disabled ? 'text-slate-400' : (isNext ? 'text-indigo-100' : 'text-slate-500')}`}>
                  {btn.description}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Connector arrow between two stages. Green & solid when the upstream
// stage is complete; the arrow leading INTO the active stage pulses to
// hint that work is currently flowing into it.

// ============================================================
// LRIExplainer — inline collapsible info card explaining the
// Licensing Readiness Intelligence concept, its 8 components +
// weights, and the 5 tier bands. Persists open/closed state to
// localStorage so the operator only sees the explainer until
// they're familiar, then collapses it for good.
// ============================================================
const LRI_COMPONENTS = [
  ['1', 'Company Accessibility',     15, 'CRM (BD contact, email, website on file)'],
  ['2', 'Partnering Activity',       15, 'Perplexity research — prior licensing deals, named partners'],
  ['3', 'Conference Participation',  10, 'CRM cohorts (BIO, JPM, ChinaBio…) + Perplexity'],
  ['4', 'ForesightRx Relationship',  15, 'CRM outreach + meetings (volume, recency, in-person)'],
  ['5', 'Licensing Availability',    15, 'Perplexity — geographic rights still unencumbered'],
  ['6', 'Corporate Momentum',        10, 'Perplexity — recent funding, IPO, M&A, exec changes'],
  ['7', 'Scientific Maturity',       10, 'CRM (Approved=10 → Preclinical=2)'],
  ['8', 'BD Readiness',              10, 'Perplexity — BD section on website, partnering language'],
];

const LRI_BANDS = [
  ['90 — 100', 'Strategic Priority', 'bg-violet-100 text-violet-800 border-violet-200'],
  ['75 — 89',  'High Priority',      'bg-emerald-100 text-emerald-800 border-emerald-200'],
  ['60 — 74',  'Medium',             'bg-sky-100 text-sky-800 border-sky-200'],
  ['40 — 59',  'Monitor',            'bg-amber-100 text-amber-800 border-amber-200'],
  ['<40',      'Low Priority',       'bg-rose-100 text-rose-800 border-rose-200'],
];

function LRIExplainer() {
  // Default open the first time the operator visits, then remember
  // their collapse state. localStorage key is namespaced so we don't
  // collide with other explainers on the platform.
  const [open, setOpen] = React.useState(() => {
    try {
      const saved = window.localStorage.getItem('viatris.lri_explainer_open');
      return saved === null ? true : saved === '1';
    } catch {
      return true;
    }
  });
  const toggle = () => {
    setOpen((v) => {
      try { window.localStorage.setItem('viatris.lri_explainer_open', v ? '0' : '1'); } catch { /* noop */ }
      return !v;
    });
  };

  return (
    <div className="bg-gradient-to-r from-violet-50 via-white to-emerald-50 border border-violet-200 rounded-lg" data-testid="lri-explainer">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
        data-testid="lri-explainer-toggle"
      >
        <div className="inline-flex items-center gap-2">
          <Flag className="w-4 h-4 text-violet-600" />
          <div>
            <div className="text-[13px] font-bold text-slate-900 leading-tight">
              LRI · Licensing Readiness Intelligence
            </div>
            <div className="text-[11px] text-slate-600 leading-tight mt-0.5">
              0-100 BD-readiness score combining 8 signals · drives the tier badges below
            </div>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 grid grid-cols-1 lg:grid-cols-2 gap-4 text-[11px]">
          {/* 8 components table */}
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">
              8 weighted components (total = 100)
            </div>
            <div className="bg-white rounded border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase tracking-wide text-slate-500">
                    <th className="py-1.5 px-2 text-left font-semibold">#</th>
                    <th className="py-1.5 px-2 text-left font-semibold">Component</th>
                    <th className="py-1.5 px-2 text-right font-semibold w-12">Pts</th>
                    <th className="py-1.5 px-2 text-left font-semibold">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {LRI_COMPONENTS.map(([num, name, pts, src]) => (
                    <tr key={num} className="border-t border-slate-100">
                      <td className="py-1 px-2 text-slate-500">{num}</td>
                      <td className="py-1 px-2 font-medium text-slate-800">{name}</td>
                      <td className="py-1 px-2 text-right font-semibold text-violet-700">{pts}</td>
                      <td className="py-1 px-2 text-slate-600">{src}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 5 tier bands + 1-line definition + how it differs from the
              AI Recommendation */}
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1.5">
                Tier bands (score → label)
              </div>
              <div className="space-y-1">
                {LRI_BANDS.map(([range, label, cls]) => (
                  <div key={label} className="flex items-center gap-2">
                    <span className={`inline-block min-w-[88px] text-center px-2 py-0.5 rounded border text-[11px] font-medium ${cls}`}>
                      {label}
                    </span>
                    <span className="text-slate-600 font-mono">{range}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/60 rounded border border-slate-200 p-2.5">
              <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                LRI vs AI Recommendation
              </div>
              <p className="text-slate-700 leading-snug">
                <strong>LRI</strong> is the underlying 0–100 score (quantitative).
                The <strong>AI Recommendation</strong> is the published label
                derived from it, plus overrides like &ldquo;Not Suitable&rdquo;
                for deal-killers (IP, safety, mis-fit). One ladder, one mental
                model — the tier tiles below filter both.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function PipelineArrow({ active, pulsing }) {
  const baseCls = active ? 'bg-emerald-400' : 'bg-slate-200';
  const arrowCls = active ? 'text-emerald-500' : 'text-slate-300';
  // Feb 2026 v2.2 — arrows sit at the vertical CENTER of the circles
  // (w-11 h-11 = 44px tall). Circle center from the tile top:
  // py-1 (4px pad) + 22px = 26px. `mt-[22px]` lifts the chevron so its
  // 16px icon lands squarely between circle centres.
  return (
    <div className="flex items-center shrink-0 mt-[22px]" aria-hidden="true">
      <div className={`h-0.5 w-10 ${baseCls} ${pulsing ? 'animate-pulse' : ''}`} />
      <ChevronRight className={`w-4 h-4 -ml-1 ${arrowCls} ${pulsing ? 'animate-pulse' : ''}`} />
    </div>
  );
}


// ConversionFunnel component removed (Feb 2026 v2). See STATUS_PIPELINE_STAGES
// above — the 5-stage clickable pipeline supersedes it.


// ─── CommitteeStatusChips (Feb 2026 v2) ─────────────────────────────
// Compact 3-chip row that decomposes the Committee Review pipeline
// stage into Pending / Under Review / Scored. Kept out of the pipeline
// itself so the pipeline stays a clean linear narrative; surfaces the
// same one-click filter behaviour the old ConversionFunnel offered.
function CommitteeStatusChips({ counters, activeFilter, onFilter }) {
  const CHIPS = [
    {
      key: 'Pending',
      label: 'Pending',
      count: counters.committee_pending ?? 0,
      dot: 'bg-rose-500',
      soft: 'bg-rose-50 text-rose-800 border-rose-200',
      tip: 'No committee member has scored yet · click to filter table',
    },
    {
      key: 'Under Review',
      label: 'Under Review',
      count: counters.committee_under_review ?? 0,
      dot: 'bg-amber-500',
      soft: 'bg-amber-50 text-amber-800 border-amber-200',
      tip: '1–5 of 6 reviewers have scored · click to filter table',
    },
    {
      key: 'Scored',
      label: 'Scored',
      count: counters.committee_scored ?? 0,
      dot: 'bg-emerald-500',
      soft: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      tip: 'All 6 reviewers in — ready for consolidation · click to filter table',
    },
  ];
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg px-4 py-2.5 flex items-center gap-3 flex-wrap"
      data-testid="committee-status-chips"
    >
      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 shrink-0">
        Committee status
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {CHIPS.map((chip) => {
          const isActive = activeFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => onFilter && onFilter(chip.key)}
              title={chip.tip}
              className={`inline-flex items-center gap-1.5 border rounded px-2 py-1 text-[11px] transition-all ${chip.soft} ${isActive ? 'ring-2 ring-offset-1 ring-violet-500 shadow-sm' : 'hover:shadow-sm'}`}
              data-testid={`committee-chip-${chip.key.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${chip.dot}`} />
              <span className="font-medium">{chip.label}</span>
              <span className="font-mono font-semibold">{chip.count}</span>
            </button>
          );
        })}
        {activeFilter && (
          <span className="ml-2 text-[10px] text-violet-700 inline-flex items-center gap-1">
            Table filtered: <strong>{activeFilter}</strong>
            <button
              type="button"
              onClick={() => onFilter && onFilter(activeFilter)}
              className="underline ml-1 text-violet-600"
              data-testid="clear-committee-filter"
            >
              clear
            </button>
          </span>
        )}
      </div>
    </div>
  );
}



// ─── CommitteeProgressMaster (Feb 2026) ──────────────────────────────
// Admin bird's-eye view of committee scoring progress for a cohort.
// Renders a collapsible section with two levels:
//   1. Master table — one row per asset, showing reviewers started /
//      fully scored / skips / committee avg / status. Sorted least-
//      progress-first so stragglers surface on top.
//   2. Per-asset drill-down — inline expand to a 9-dim × 6-reviewer
//      matrix showing exact scores, SKIPs, and empty cells.
// Backed by /api/viatris/committee/master and /viatris/committee/{ea_id}/matrix.
function CommitteeProgressMaster({ cohort }) {
  const [open, setOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedEa, setExpandedEa] = useState(null);
  const [matrixByEa, setMatrixByEa] = useState({}); // { ea_id: matrixData }
  const [matrixLoading, setMatrixLoading] = useState(false);

  const load = useCallback(async () => {
    if (!cohort) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/viatris/committee/master?cohort=${encodeURIComponent(cohort)}`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load committee progress');
    } finally {
      setLoading(false);
    }
  }, [cohort]);

  useEffect(() => { load(); }, [load]);

  const loadMatrix = async (eaId) => {
    if (matrixByEa[eaId]) return; // cached
    setMatrixLoading(true);
    try {
      const r = await axios.get(
        `${API}/viatris/committee/${eaId}/matrix`,
        { withCredentials: true },
      );
      setMatrixByEa((prev) => ({ ...prev, [eaId]: r.data }));
    } catch { toast.error('Failed to load matrix'); }
    finally { setMatrixLoading(false); }
  };

  const toggleExpand = async (eaId) => {
    if (expandedEa === eaId) { setExpandedEa(null); return; }
    setExpandedEa(eaId);
    await loadMatrix(eaId);
  };

  const summary = data?.summary || {};
  const assets = data?.assets || [];

  return (
    <div className="my-4 border border-slate-200 rounded-lg bg-white shadow-sm" data-testid="committee-progress-master">
      <div
        onClick={() => setOpen(!open)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors cursor-pointer"
        data-testid="committee-progress-toggle"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-600" />
          <span className="font-semibold text-sm text-slate-800">Committee Progress</span>
          <Badge className="bg-indigo-100 text-indigo-800 text-[10px] border-indigo-200">
            {summary.total || 0} in review
          </Badge>
          {summary.complete > 0 && (
            <Badge className="bg-emerald-100 text-emerald-800 text-[10px] border-emerald-200">
              {summary.complete} fully in
            </Badge>
          )}
          {summary.in_progress > 0 && (
            <Badge className="bg-amber-100 text-amber-800 text-[10px] border-amber-200">
              {summary.in_progress} in progress
            </Badge>
          )}
          {summary.pending > 0 && (
            <Badge className="bg-slate-100 text-slate-700 text-[10px] border-slate-200">
              {summary.pending} not started
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={(e) => { e.stopPropagation(); load(); }}
            title="Refresh"
            data-testid="committee-progress-refresh"
          >
            <Loader2 className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? '' : '-rotate-90'}`} />
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200">
          {assets.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="committee-progress-empty">
              {loading ? 'Loading…' : `No committee-review assets in ${cohort}.`}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="committee-progress-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-8"></th>
                    <th className="text-left px-3 py-2 font-medium">Asset</th>
                    <th className="text-left px-3 py-2 font-medium">Biotech</th>
                    <th className="text-left px-3 py-2 font-medium">Phase</th>
                    {/* Per-reviewer status columns — populated from the
                        first row's `per_reviewer` array (ordering is
                        deterministic — server returns REVIEWER_ROLES in
                        canonical order). */}
                    {(assets[0]?.per_reviewer || []).map((pr) => (
                      <th key={pr.member_id} className="text-center px-2 py-2 font-medium" title={pr.member_label}>
                        {pr.member_short}
                      </th>
                    ))}
                    <th className="text-center px-3 py-2 font-medium">Started</th>
                    <th className="text-center px-3 py-2 font-medium">Committee Avg</th>
                    <th className="text-center px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((a) => (
                    <React.Fragment key={a.ea_id}>
                      <tr
                        className={`border-b border-slate-100 cursor-pointer hover:bg-indigo-50/40 ${expandedEa === a.ea_id ? 'bg-indigo-50/60' : ''}`}
                        onClick={() => toggleExpand(a.ea_id)}
                        data-testid={`committee-progress-row-${a.ea_id}`}
                      >
                        <td className="px-3 py-2">
                          <ChevronRight
                            className={`w-3 h-3 text-slate-400 transition-transform ${expandedEa === a.ea_id ? 'rotate-90' : ''}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{a.asset_name}</td>
                        <td className="px-3 py-2 text-slate-600">{a.biotech_name}</td>
                        <td className="px-3 py-2 text-slate-600">{a.phase || '—'}</td>
                        {a.per_reviewer.map((pr) => (
                          <td key={pr.member_id} className="px-2 py-2 text-center">
                            <ReviewerHeadlineCell pr={pr} />
                          </td>
                        ))}
                        <td className="px-3 py-2 text-center">
                          <span className={`font-mono ${a.reviewers_started === a.total_members ? 'text-emerald-700 font-semibold' : a.reviewers_started > 0 ? 'text-amber-700' : 'text-slate-400'}`}>
                            {a.reviewers_started}/{a.total_members}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center font-mono font-semibold">
                          {a.committee_overall_avg != null
                            ? <span className={a.committee_overall_avg >= 3 ? 'text-emerald-700' : 'text-rose-700'}>{a.committee_overall_avg.toFixed(2)}/5</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <CommitteeStatusBadge status={a.committee_status} />
                        </td>
                      </tr>
                      {expandedEa === a.ea_id && (
                        <tr>
                          <td colSpan={a.per_reviewer.length + 7} className="p-0 bg-slate-50 border-b border-slate-200">
                            <CommitteeMatrixDrilldown
                              eaId={a.ea_id}
                              matrix={matrixByEa[a.ea_id]}
                              loading={matrixLoading && !matrixByEa[a.ea_id]}
                              perReviewer={a.per_reviewer}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Per-reviewer headline cell shown in each row of the master table.
// Compact, single-token summary optimised for scannability:
//   • 4.20    → weighted avg (real scores only). Colour-coded.
//   • SKIP    → the reviewer explicitly skipped every dimension.
//   • 2/9     → partial submission (real + skips < 9)
//   • —       → not started
// Skips are always excluded from the reviewer's own weighted avg,
// mirroring the committee-average rule.
function ReviewerHeadlineCell({ pr }) {
  if (!pr) return <span className="text-slate-300">—</span>;
  if (pr.state === 'not_started') {
    return <span className="text-slate-300">—</span>;
  }
  if (pr.state === 'all_skipped') {
    return <span className="inline-block text-[10px] font-semibold text-slate-600 bg-slate-200 rounded px-1.5 py-0.5" title="All 9 dimensions skipped">SKIP</span>;
  }
  if (pr.state === 'fully_scored' || pr.state === 'all_answered_with_skips') {
    // Reviewer has decided on every dim (may include skips). Their
    // weighted avg is over real scores only.
    const v = pr.weighted_avg;
    if (v == null) return <span className="text-slate-300">—</span>;
    const cls = v >= 4 ? 'text-emerald-700 bg-emerald-50'
      : v >= 3 ? 'text-amber-700 bg-amber-50'
      : 'text-rose-700 bg-rose-50';
    const suffix = pr.skips > 0
      ? <span className="ml-1 text-[8px] font-semibold text-slate-500">·{pr.skips}s</span>
      : null;
    return (
      <span className={`inline-block text-[11px] font-mono font-semibold rounded px-1.5 py-0.5 ${cls}`} title={`Weighted avg (real scores only) · ${pr.skips} skip${pr.skips === 1 ? '' : 's'}`}>
        {v.toFixed(2)}{suffix}
      </span>
    );
  }
  // Partial — X/9 answered so far. Show fraction; if any skips, show as ·Ns.
  const answered = (pr.real_scores || 0) + (pr.skips || 0);
  const skipSuffix = pr.skips > 0
    ? <span className="ml-1 text-[8px] font-semibold text-slate-500">·{pr.skips}s</span>
    : null;
  return (
    <span className="inline-block text-[10px] font-mono text-amber-700 bg-amber-50 rounded px-1.5 py-0.5" title={`${pr.real_scores} scored + ${pr.skips} skipped, ${pr.not_yet} pending`}>
      {answered}/9{skipSuffix}
    </span>
  );
}

function CommitteeStatusBadge({ status }) {
  const s = status || 'Pending';
  const cls = s === 'Approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : s === 'Rejected' ? 'bg-rose-100 text-rose-800 border-rose-200'
    : s === 'Shortlisted' ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
    : s === 'Under Review' ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`text-[10px] ${cls}`}>{s}</Badge>;
}

function CommitteeMatrixDrilldown({ eaId, matrix, loading, perReviewer }) {
  if (loading) {
    return (
      <div className="p-6 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading reviewer matrix…
      </div>
    );
  }
  if (!matrix) return null;
  const { dimensions, reviewers, cells, per_dim, comments, committee_overall_avg } = matrix;
  const renderCell = (dim, mid) => {
    const c = cells?.[dim]?.[mid];
    if (!c) return <span className="text-slate-300">—</span>;
    if (c.is_skip) return <span className="inline-block text-[9px] font-semibold text-slate-500 bg-slate-200 rounded px-1 py-0.5">SKIP</span>;
    if (c.value == null) return <span className="text-slate-300">—</span>;
    const color = c.value >= 4 ? 'text-emerald-700 bg-emerald-50'
      : c.value >= 3 ? 'text-amber-700 bg-amber-50'
      : 'text-rose-700 bg-rose-50';
    return <span className={`inline-block text-[11px] font-mono font-semibold rounded px-1.5 py-0.5 ${color}`}>{c.value}</span>;
  };
  return (
    <div className="p-4" data-testid={`committee-matrix-${eaId}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Reviewer × Dimension</div>
        <div className="text-[11px] text-slate-600">
          Committee weighted avg: <strong className="text-emerald-700">{committee_overall_avg != null ? `${committee_overall_avg.toFixed(2)}/5` : '—'}</strong>
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded border border-slate-200">
        <table className="w-full text-xs" data-testid={`committee-matrix-table-${eaId}`}>
          <thead className="bg-slate-100 border-b border-slate-200 text-slate-700">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Dimension</th>
              {reviewers.map((r) => (
                <th key={r.id} className="text-center px-2 py-1.5 font-medium" title={r.label}>
                  <div className="text-[10px]">{r.short}</div>
                </th>
              ))}
              <th className="text-center px-2 py-1.5 font-medium bg-emerald-50 text-emerald-800">Avg</th>
              <th className="text-center px-2 py-1.5 font-medium">Skips</th>
            </tr>
          </thead>
          <tbody>
            {dimensions.map((d) => (
              <tr key={d.key} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-2 py-1.5 text-slate-700">{d.label}</td>
                {reviewers.map((r) => (
                  <td key={r.id} className="px-2 py-1.5 text-center">{renderCell(d.key, r.id)}</td>
                ))}
                <td className="px-2 py-1.5 text-center font-mono font-semibold text-emerald-700 bg-emerald-50/40">
                  {per_dim?.[d.key]?.avg != null ? per_dim[d.key].avg.toFixed(2) : '—'}
                </td>
                <td className="px-2 py-1.5 text-center text-slate-500">
                  {(per_dim?.[d.key]?.skips || 0) || '—'}
                </td>
              </tr>
            ))}
            {/* Per-reviewer progress summary */}
            <tr className="bg-slate-100 font-semibold border-t-2 border-slate-300">
              <td className="px-2 py-1.5 text-slate-700">State</td>
              {reviewers.map((r) => {
                const pr = (perReviewer || []).find((p) => p.member_id === r.id);
                const stateLabel = pr?.state === 'fully_scored' ? 'Full' :
                  pr?.state === 'all_answered_with_skips' ? 'All (with skips)' :
                  pr?.state === 'partial' ? `${pr.real_scores + pr.skips}/9` :
                  'Not started';
                const cls = pr?.state === 'fully_scored' ? 'text-emerald-700'
                  : pr?.state === 'partial' || pr?.state === 'all_answered_with_skips' ? 'text-amber-700'
                  : 'text-slate-400';
                return (
                  <td key={r.id} className={`px-2 py-1.5 text-center text-[10px] ${cls}`}>
                    {stateLabel}
                  </td>
                );
              })}
              <td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Reviewer comments (only shown where non-empty) */}
      {reviewers.some((r) => (comments?.[r.id] || '').trim().length > 0) && (
        <div className="mt-3 space-y-1.5">
          <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">Reviewer Comments</div>
          {reviewers.map((r) => {
            const c = (comments?.[r.id] || '').trim();
            if (!c) return null;
            return (
              <div key={r.id} className="text-[11px] bg-white rounded border border-slate-200 p-2">
                <span className="font-semibold text-slate-700 mr-2">{r.short}</span>
                <span className="text-slate-600">{c}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
